import { createProgram, uniformLoc } from "./gl";
import {
  mat4GhostCharacterBillboardZUp,
  mat4Identity,
  mat4LookAt,
  mat4Multiply,
  mat4Perspective,
  vec3,
  type Mat4,
  type Vec3,
} from "./math";
import type { GridMap } from "./world";
import { EYE_HEIGHT, WORLD_HEIGHT } from "./world";
import {
  BLOCK_SIZE, DEBRIS_LIFETIME, GRAVITY,
  defaultColor, type DebrisBlock,
} from "./voxel";
import type { VoxelChunkClient } from "./chunks";
import { quatIntegrate, quatToAxisAngle, isAliveBit } from "./chunks";
import { rotateAxisAngle, type FallingGroupClient } from "./falling";
import { getTexture, uploadGLTexture, type Texture } from "./textures";
import {
  BODY_VOX_CUBE,
  BODY_VOX_GRID_BYTES,
  BODY_VOX_OX,
  BODY_VOX_OY,
  BODY_VOX_OZ,
  BODY_VOX_SX,
  BODY_VOX_SY,
  BODY_VOX_SZ,
  bodyVoxTooSparseToRenderInner,
} from "./bodyVox";
import { bakeBodyVoxProxyData } from "./bodyVoxProxyBake";
import {
  BODY_DEAD_VOL_SHADER_MAX,
  fillDeadBodyVoxWorldSpheres,
} from "./bodyVoxDeadVolume";

/** Имя glTF-ноды для сабмеша innerMesh (индекс в innerMesh.nodes). */
function innerNodeNameLower(inner: LoadedMesh, sm: SubMesh): string {
  const n = inner.nodes[sm.nodeIdx];
  return (n?.name ?? "").toLowerCase();
}

/** Гладкая оболочка в hybrid GLB (не путать с воксельным телом). */
function innerSubMeshIsOuterShell(inner: LoadedMesh, sm: SubMesh): boolean {
  if (sm.bodyLayer === "outer") return true;
  const low = innerNodeNameLower(inner, sm);
  if (low === "output") return true;
  if (low.includes("smooth") || low.includes("shell")) return true;
  if (low.includes("outer") && !low.includes("voxel")) return true;
  return false;
}

/** Дубликат оружия из того же GLB — всегда рисуем из основного soldier.glb. */
function innerSubMeshIsDuplicateWeapon(inner: LoadedMesh, sm: SubMesh): boolean {
  const low = innerNodeNameLower(inner, sm);
  return low === "weapon" || low.includes("weapon");
}

/** Воксельное «мясо» / каркас для bodyVox-шейдера. */
function innerSubMeshIsVoxelCarcass(inner: LoadedMesh, sm: SubMesh): boolean {
  if (sm.bodyLayer === "inner") return true;
  const low = innerNodeNameLower(inner, sm);
  return low.includes("voxel") || low.includes("carcass") || low.includes("blocks");
}

/** Есть ли в файле явный воксельный кусок — тогда не рисуем прочие null-слои (гладкий дубль). */
function innerMeshHasVoxelLike(inner: LoadedMesh): boolean {
  for (const sm of inner.subMeshes) {
    if (innerSubMeshIsVoxelCarcass(inner, sm)) return true;
  }
  return false;
}

// ── Material palette texMode scheme ────────────────────────────────────
// Шейдер voxel.frag выбирает ветку рендеринга по `vTexMode` float:
//   matKind * 0.1           → tile-material (atlas lookup по `matKind`)
//   OBJECT_TRIPLANAR (0.95)  → legacy object triplanar (chair.json)
//   UNIFORM (1.0)            → uniform color (debris, decorации без текстуры,
//                              трейсеры, лампы)
// Порядок слотов в palette-атласе обязан совпадать с matKind в .frag.
const MATERIAL_KIND: Record<string, number> = {
  brick:    0,
  concrete: 1,
  plaster:  2,
  wood:     3,
  grass:    4,
  dirt:     5,
  stone:    6,
  metal:    7,
  glass:    8,
};
/** Порядок для `buildMaterialAtlas` — индекс соответствует `matKind`. */
export const PALETTE_MATERIAL_ORDER = [
  "brick", "concrete", "plaster", "wood",
  "grass", "dirt", "stone", "metal", "glass",
];
export const OBJECT_TRIPLANAR_TEX_MODE = 0.95;
export const UNIFORM_TEX_MODE = 1.0;
/** Одна диффузная текстура из GLB (levelpacks/...) — триплантар в voxel.frag. */
export const LEVELPACK_SINGLE_TRIPLANAR_TEX_MODE = 0.91;

function isLevelpackDiffuseTex(name: string): boolean {
  return name.startsWith("levelpacks/");
}

function materialTexMode(name: string): number {
  const k = MATERIAL_KIND[name];
  return k === undefined ? UNIFORM_TEX_MODE : k * 0.1;
}

import raycastVert from "../shaders/raycast.vert?raw";
import raycastFrag from "../shaders/raycast.frag?raw";
import voxelVert from "../shaders/voxel.vert?raw";
import voxelFrag from "../shaders/voxel.frag?raw";
import voxObjVert from "../shaders/voxObj.vert?raw";
import voxObjFaceVert from "../shaders/voxObjFace.vert?raw";
import voxObjFrag from "../shaders/voxObj.frag?raw";
import meshVert from "../shaders/mesh.vert?raw";
import meshFrag from "../shaders/mesh.frag?raw";
import postVert from "../shaders/post.vert?raw";
import postFrag from "../shaders/post.frag?raw";
import bloomExtractFrag from "../shaders/bloom_extract.frag?raw";
import bloomBlurFrag from "../shaders/bloom_blur.frag?raw";
import dustSurfaceVert from "../shaders/dustSurface.vert?raw";
import dustSurfaceFrag from "../shaders/dustSurface.frag?raw";
import dustBurstVert from "../shaders/dustBurst.vert?raw";
import dustBurstFrag from "../shaders/dustBurst.frag?raw";

import { loadGlb, canSkinInnerToOuterPose, MESH_COLOR_SRGB, VIDEO_PACKED_ALPHA, type LoadedMesh, type SubMesh } from "./meshLoader";
import {
  buildInnerPoseFromOuter,
  computeBoneMatrices, computeBoneMatricesInnerFromOuterPose, computeNodeWorldMatrices,
  sampleClipInto, blendPosesInto, overlayPose,
  applyAimToPose, newAnimState, tickAnimState,
  type AnimState, type PoseSample,
} from "./animation";
import { updateRealVoxelFlyPhysics, type RealVoxelFlyPiece } from "./realVoxFly";
import { ParticleRenderer } from "./particleRenderer";
import type { ParticleSimulation } from "./particles/simulate";

export type { RealVoxelFlyPiece };

/** Спавненная инстанция mesh-объекта в мире (player/NPC/item).
 *  Mesh (LoadedMesh) шарится между всеми инстансами одного персонажа —
 *  уникально на инстанс только pose state и матрицы. */
/** Оверлей-анимация двери на static-меше уровня: клипы (общие DoorOpen/…)
 *  играются с фильтром по нодам конкретной двери, поверх base-позы.
 *  `holdLast` — после конца держать последний кадр (открытая дверь);
 *  `autoRemove` — по концу клипа снять слой (закрытие вернулось в base-позу). */
export interface DoorAnimLayer {
  clips: string[];
  time: number;
  speed: number;
  holdLast: boolean;
  autoRemove: boolean;
  nodeFilter: Set<number>;
}

export interface MeshInstance {
  mesh: LoadedMesh;
  /** мировые координаты в клетках карты (как у raycaster: x,y — горизонталь, z — вверх) */
  x: number;
  y: number;
  z: number;
  /** поворот вокруг вертикальной оси (yaw в радианах) */
  yaw: number;
  /** Дополнительный yaw-offset (в радианах), накладывается поверх `yaw`.
   *  Нужен когда mesh в .glb не ориентирован по конвенции "+X = forward"
   *  (например новый soldier_animated.glb: forward = -Z, нужен offset -π/2). */
  yawOffset: number;
  /** Aim-pitch (рад) — наклон верхнего корпуса вверх/вниз. Применяется к
   *  кости mesh.aimPitchBoneIdx (Spine) до вычисления FK, поэтому
   *  поднимаются плечи + голова + руки + оружие. Используется когда нужно
   *  чтобы оружие смотрело туда же, куда смотрит игрок (look up/down). */
  aimPitch: number;
  /** Aim-yaw (рад) — горизонтальный твист корпуса. Используется чтобы
   *  компенсировать запечённый поворот ствола в анимации стрельбы
   *  (например firingRifle держит ствол ~40° левее body-forward — здесь
   *  ставим -40°, корпус доворачивается, оружие смотрит на крест). */
  aimYaw: number;
  /** общий scale (модель умножается на это) */
  scale: number;
  /** State машины анимаций (baseAnim + crossfade + upperLayer). */
  state: AnimState;
  /** Дверные оверлеи static-меша уровня по базе имени двери (см. DoorAnimLayer). */
  doorLayers: Map<string, DoorAnimLayer>;
  /** Кэш мировых AABB сабмешей (6 float на сабмеш) для frustum-куллинга
   *  не-skinned геометрии (302 сабмеша особняка рисовались без отсева).
   *  Пересчитывается лениво при worldAabbDirty (поза изменилась). */
  subMeshWorldAABB: Float32Array | null;
  worldAabbDirty: boolean;
  /** Поза уже посчитана и анимация заморожена — updateMeshInstancePose может скипать
   *  сэмплинг клипов и FK по всем нодам (см. meshPoseIsStatic). */
  poseComputed: boolean;
  lastPoseBaseAnim: string;
  lastPoseBaseTime: number;
  lastPoseDoorCount: number;
  /** Если true — не рендерить (например локальный игрок в FP). */
  hidden: boolean;
  /** Skin-joint индексы (в skin.joints), которые надо схлопнуть в 0
   *  (вершины с этими joint weights исчезают). Используется для FP-вида,
   *  чтобы спрятать голову/шею своего же mesh'а — иначе они закрывают камеру.
   *  null = ничего не скрываем. */
  hiddenJoints: number[] | null;
  /** Если true — рендерить ТОЛЬКО не-skinned sub-mesh'ы этого инстанса
   *  (для soldier'а это именно оружие, прикреплённое через `weapone_bone`).
   *  Используется в FP-виде своего игрока: тело целиком прячем, в кадре
   *  остаётся только пулемёт перед камерой (стиль Doom). Skin-анимации
   *  и aim IK всё равно считаются, потому что позиция/поворот оружия
   *  зависят от weapone_bone → RightHand → Spine. */
  fpViewOnly: boolean;
  /** Computed each frame. Длина = mesh.nodes.length * 16. */
  nodeWorldMatrices: Float32Array;
  /** Computed each frame. Длина = (mesh.skin?.joints.length ?? 0) * 16. */
  boneMatrices: Float32Array;
  /** Второй меш (воксельное тело); кости маппятся по имени на позу основного mesh. */
  innerMesh: LoadedMesh | null;
  /** IBM для innerMesh.skin; длина = inner joints × 16. */
  innerBoneMatrices: Float32Array;
  /** Сферы «дыр» (локаль): packed vec4×24, count ≤24. */
  bodyHitCount: number;
  bodyHitsPacked: Float32Array;
  /** v12: Uint8Array длиной BODY_VOX_GRID_BYTES или null — только шейдерное тело (каркас/мясо). */
  bodyVoxAlive: Uint8Array | null;
  /** v13: гладкая оболочка (outer GLB); null = до первого снапшота / legacy. */
  bodyVoxShell: Uint8Array | null;
  /** FNV-хеш последнего bodyVoxAlive — чтобы ловить in-place обновления буфера. */
  bodyVoxContentHash: number;
  bodyVoxHashInit: boolean;
  /** Секунды (timeAcc): момент последнего изменения битмаски; для анимации обломков. */
  bodyVoxLastMutSec: number;
  /** Копия прошлого bodyVox для диффа и спавна per-cell debris (см. bodyVoxDebris.ts). */
  bodyVoxPrevForDebris: Uint8Array | null;
  /** Предыдущий кадр shell-битмапы — дербис сколов оболочки (без дыр во «мясе»). */
  bodyVoxShellPrevForDebris: Uint8Array | null;
  /** false — красный inner без discard по битмаске (только дербис); для NPC с оболочкой сверху. */
  innerBodyVoxShaderCarve: boolean;
  /** Предыдущий hp для детекта респавна (0 → >0): полная замена bodyVox с сервера. */
  prevHpForBodyVoxMerge?: number;
  /** Joint-индексы innerMesh: занулить кости отрубленных конечностей (см. bodyVoxLimbs.ts). */
  innerMeshHiddenJoints: number[] | null;
  /** Копия индексов костей головы/шеи (Mixamo) — outer skin при headshot NPC. */
  headNeckBoneIndices: number[] | null;
  /** Серверный headshot: скрыть голову на outer mesh (dying без «маски»). */
  npcHeadshotDetach: boolean;
  /** FK inner-нод по именам из outer-позы (длина = inner.nodes.length × 16). */
  innerNodeWorldMatrices: Float32Array;
  /** Пустые Map'ы для buildInnerPoseFromOuter → computeNodeWorldMatrices(inner). */
  innerPoseScratch: PoseSample;
  /** Прототип `?realBody=1`: отдельный примитив на куб; иначе псевдо-воксель = шейдер + кости. */
  realVoxelBody: boolean;
  /** Длина = inner.subMeshes.length; 1 = пропуск draw для этого сабмеша. */
  realVoxelHiddenSub: Uint8Array | null;
  /** Предыдущий кадр: жива ли ячейка сервера для этого сабмеша (0/1). */
  realVoxelCellAlivePrev: Uint8Array | null;
  /** Отлетевшие кубы (геометрия того же VAO). */
  realVoxelFlying: RealVoxelFlyPiece[];
  /** Три буфера позы на инстанс — sampleClip/blend без `new Map` каждый кадр (GC). */
  poseScratch: [PoseSample, PoseSample, PoseSample];
  /** weapons: готовая мировая матрица модели (column-major, Z-up мира) вместо
   *  x/y/z/yaw/scale — оружие в кисти, вид от первого лица, снаряды. */
  worldMatrix?: Float32Array | null;
  /** weapons: свечение (линейный RGB, добавляется к цвету) — подсветка подборов, дуло. */
  emissive?: [number, number, number] | null;
  /** weapons: ноды, сабмеши которых не рисовать (вшитый автомат солдата). */
  hiddenNodes?: Set<number> | null;
  /** weapons: то же для innerMesh (свои индексы нод). */
  innerHiddenNodes?: Set<number> | null;
}

const FOV_Y = (75 * Math.PI) / 180;
/** Горизонтальный угол обзора не шире, чем у экрана 16:9 при 75° по вертикали
 *  (~107°). На вытянутых экранах телефонов (2,16:1 → 117°) кадр становился
 *  широкоугольным: края, руки и оружие «растягивались» (жалоба Олега 21.10). */
const MAX_FOV_X = 2 * Math.atan(Math.tan(FOV_Y / 2) * (16 / 9));
/** Экспозиция по умолчанию (см. Renderer.setBrightness). */
const EXPOSURE_BASE = 1.5;

/** story: всё, что нужно проходу билбордов сюжетного режима (src/story). */
export interface StoryPassContext {
  gl: WebGL2RenderingContext;
  viewProj: Float32Array;
  eye: ArrayLike<number>;
  fogColor: ArrayLike<number>;
  fogDensity: number;
  fogHeight: number;
  fogHeightFalloff: number;
  exposure: number;
  /** До 8 ближайших ламп кадра (как у мешей): xyz+range и rgb. */
  numLights: number;
  lightPos: Float32Array;
  lightCol: Float32Array;
  /** Время эффектов рендера, с. */
  time: number;
}

export interface CameraState {
  x: number;
  y: number;
  yaw: number;
  pitch: number;
  /** небольшое смещение камеры по Z (view bobbing); 0 если не используется */
  bobZ?: number;
  /** наклон обзора вокруг forward-оси (head sway, как в Doom 2); радианы */
  bobRoll?: number;
  /** опциональная Z-координата глаза в мире. Если undefined — берётся EYE_HEIGHT.
   *  Используется в TP, чтобы поднять камеру над плечом игрока. */
  eyeZ?: number;
}

export interface PointLight {
  x: number; y: number; z: number;
  /** дистанция в мире (м), на которой свет полностью гаснет */
  range: number;
  /** RGB интенсивность (можно > 1 для ярких ламп) */
  color: [number, number, number];
  /** weapons: только свет, без кубика-«плафона» (вспышки, ореолы подборов). */
  noBulb?: boolean;
}

// 800K инстансов: floor+ceiling+walls дают ~400K видимых кубов в плотной арене,
// плюс debris/decorations/players/tracers — нужен запас.
const MAX_INSTANCES = 800000;

/** Какие анимации зацикленные.
 *  - idle / walk / run / runLeft / runRight / runForwardLeft /
 *    runForwardRight / crouchIdle / crouchWalk — передвижение/стойка, лупим.
 *  - shoot — fire-loop (каждый выстрел держит overlay живым).
 *  - jump / jumpDown / die / runToStop — one-shot, на последнем кадре фриз. */
function isLoopingAnim(name: string): boolean {
  // Дверной клип уровня — не лупить (иначе визуально «крутится» при sampleClip).
  if (name === "door_manison_open") return false;
  return (
    name === "idle" || name === "walk" || name === "run" ||
    name === "runLeft" || name === "runRight" ||
    name === "runForwardLeft" || name === "runForwardRight" ||
    name === "crouchIdle" || name === "crouchWalk"
  );
}
// Ширина 2D-лукап-текстуры для corner UV (степень двойки для дешёвого %/ в шейдере
// через bitwise). 1024 текселей × достаточно высоты — для объекта в 10K alive cubes
// высота = ceil(10K*24 / 1024) = 235 строк, легко влезает в WebGL2 max=8192+.
const FACE_UV_LOOKUP_W = 1024;

// ── «Точечный стиль» ──────────────────────────────────────────────────
// Точек на видимый куб в dust-пассе (drawArraysInstanced(POINTS, 0, K, n)).
// «Мир из точек»: 6 точек ПОКРУПНЕЕ (перф: вершинная цена пасса — главный
// расход, 12→6 вдвое дешевле; покрытие компенсировано размером точки).
const DUST_POINTS_PER_CUBE = 6;
// Ёмкость ring-buffer'а песка (зёрен) и лимит одного спавна. Референс —
// Houdini sand sim: плотные ПОТОКИ зёрен, поэтому ёмкость большая:
// 128K × 48 байт = 6 MiB VBO; баллистика в шейдере, CPU-цены на кадр нет.
const DUST_BURST_CAP = 131072;
const DUST_BURST_MAX_PER_CALL = 96;
// Точка dust-burst: pos0(3) + vel0(3) + rgb+floorZ(4) + born+life(2).
const DUST_FLOATS = 12;
// Кап заливки зёрен в VBO за кадр (точек): гигантский обвал размазывается
// на несколько кадров вместо одного 6-мегабайтного bufferSubData-фриза.
const DUST_FLUSH_PER_FRAME = 24576;

// Per-instance: 4 vec4 = 16 floats
//   iPosSize:   xyz = world pos (после pose), w = cube size
//   iColor:     rgb = fallback color ИЛИ bbox size (для object triplanar), a = texMode
//   iObjectUV:  xyz = bbox min (для object triplanar), unused для других
//   iLocalPos:  xyz = cube center в local frame (для object triplanar), w spare
const INSTANCE_FLOATS = 16;
/** Сторона пространственного тайла статического levelpack-чанка (м).
 *  Компромисс: мельче — точнее frustum-отсев, но больше draw call'ов
 *  (в Safari они дороже). 8 м на alone_in_the_dark даёт ~180 тайлов. */
const LP_TILE_METERS = 8;

/** Статический тайл levelpack-чанка: собственный GPU-буфер инстансов +
 *  VAO + мировой AABB для frustum-отсева. Собирается один раз (и заново
 *  только при разрушении — смене visibleEpoch), за кадр — лишь bind+draw. */
interface LpTile {
  buf: WebGLBuffer;
  vao: WebGLVertexArrayObject;
  /** VAO пыли поверх ТОГО ЖЕ буфера (раскладка совпадает: loc2 pos+size,
   *  loc3 aliveIdx). Создаётся лениво — только если включён dust-стиль. */
  dustVao?: WebGLVertexArrayObject;
  cubes: number;
  /** Ёмкость буфера в кубах — растим только вверх (bufferData), иначе
   *  обновляем на месте (bufferSubData). */
  cap?: number;
  /** Последние залитые данные — чтобы при разрушении не трогать GPU у тайлов,
   *  которых выстрел не коснулся. */
  data?: Float32Array;
  minX: number; minY: number; minZ: number;
  maxX: number; maxY: number; maxZ: number;
}

/** Тайл СТАТИЧЕСКОГО object-чанка. Держит два независимых инстанс-буфера:
 *  - faceBuf: по одному инстансу на КАЖДУЮ ОТКРЫТУЮ ГРАНЬ (6 вершин) — им
 *    рисуется мир (voxObjFace.vert). Скрытые грани на GPU не попадают.
 *  - dustBuf: по инстансу на КУБ — пыль (dustSurface.vert) осталась
 *    покубовой, чтобы её вид не изменился. Создаётся только если пыль
 *    включена (tier 0), иначе VRAM не тратится. */
interface ObjTile {
  faceBuf: WebGLBuffer;
  faceVao: WebGLVertexArrayObject;
  faces: number;
  faceCap: number;
  /** Индексы кубов чанка, попавших в этот тайл. Позволяет пересобрать тайл
   *  за O(размер тайла), а не за O(всего чанка) — критично при разрушении. */
  cubeIdx: number[];
  dustBuf: WebGLBuffer | null;
  dustVao: WebGLVertexArrayObject | null;
  dustCubes: number;
  dustCap: number;
  minX: number; minY: number; minZ: number;
  maxX: number; maxY: number; maxZ: number;
}

// Far plane согласован с туманом: при density 0.14 туман достигает ~99.98%
// на 60 м, поэтому геометрия растворяется в тумане ДО среза far plane.
// Раньше 30 м: дальние объекты срезались проекцией и «появлялись
// фрагментарно» при приближении (туман не успевал их спрятать).
const FAR_PLANE = 60.0;
const TRACER_LIFETIME = 0.12;

export type PomLayerOption = 0 | 4 | 8;

const RENDER_SCALE_MIN = 0.5;
const RENDER_SCALE_MAX = 1.0;

interface Tracer {
  ox: number; oy: number; oz: number;
  hx: number; hy: number; hz: number;
  born: number;
  /** weapons: вид трассера (луч лазера и т.п.); undefined — пуля как раньше. */
  opts?: TracerOpts;
}

/** weapons: параметры необычного трассера. */
export interface TracerOpts {
  color: [number, number, number];
  /** размер кубика, м */
  size: number;
  /** жизнь, с */
  life: number;
  /** кубиков на метр */
  density: number;
  /** пропуск у начала, м */
  skip: number;
}

export class Renderer {
  private gl: WebGL2RenderingContext;
  private canvas: HTMLCanvasElement;

  // Raycaster
  private rcProg: WebGLProgram;
  private quadVAO: WebGLVertexArrayObject;
  private mapTex: WebGLTexture;
  private mapSize: [number, number] = [0, 0];
  private uRC: {
    map: WebGLUniformLocation | null;
    mapSize: WebGLUniformLocation | null;
    playerPos: WebGLUniformLocation | null;
    yaw: WebGLUniformLocation | null;
    pitch: WebGLUniformLocation | null;
    fovY: WebGLUniformLocation | null;
    aspect: WebGLUniformLocation | null;
    time: WebGLUniformLocation | null;
    eyeHeight: WebGLUniformLocation | null;
    ceilHeight: WebGLUniformLocation | null;
    near: WebGLUniformLocation | null;
    far: WebGLUniformLocation | null;
    cameraPos: WebGLUniformLocation | null;
    fogColor: WebGLUniformLocation | null;
    fogDensity: WebGLUniformLocation | null;
    fogHeight: WebGLUniformLocation | null;
    fogHeightFalloff: WebGLUniformLocation | null;
    exposure?: WebGLUniformLocation | null;
    sky: WebGLUniformLocation | null;
    skyEnabled: WebGLUniformLocation | null;
    skyEquirect: WebGLUniformLocation | null;
  };

  // Voxels (instanced)
  private voxProg: WebGLProgram;
  private voxVAO: WebGLVertexArrayObject;
  /** Общая геометрия юнит-куба — переиспользуется VAO статических тайлов. */
  private voxGeomVBO!: WebGLBuffer;
  /** Статические object-чанки (faceUVs), нарезанные на тайлы с постоянными
   *  GPU-буферами. Ключ — chunk.id, инвалидация — по visibleEpoch. */
  private _objTileCache: Map<number, { epoch: number; tiles: ObjTile[] }> = new Map();
  /** Переиспользуемый буфер для сортировки тайлов front-to-back. */
  private _objTileOrder: Array<{ t: ObjTile; d: number }> = [];
  private voxInstanceBuf: WebGLBuffer;
  private voxInstanceData: Float32Array;

  // Voxel objects (interior items с corner UV grid и diffuse текстурой)
  private voxObjProg: WebGLProgram;
  /** Пофасадная программа для статических object-чанков (см. voxObjFace.vert). */
  private voxObjFaceProg: WebGLProgram;
  private uVoxObjFace: Omit<Renderer["uVoxObj"], "chunkQuat">;
  private voxObjVAO: WebGLVertexArrayObject;
  private voxObjInstanceBuf: WebGLBuffer;
  private voxObjInstanceData: Float32Array;
  private uVoxObj: {
    viewProj: WebGLUniformLocation | null;
    uvLookup: WebGLUniformLocation | null;
    diffuse:  WebGLUniformLocation | null;
    lookupW:  WebGLUniformLocation | null;
    cameraPos: WebGLUniformLocation | null;
    lightLodDist: WebGLUniformLocation | null;
    numLights: WebGLUniformLocation | null;
    lights: WebGLUniformLocation | null;
    lightColors: WebGLUniformLocation | null;
    chunkQuat: WebGLUniformLocation | null;
    fogColor: WebGLUniformLocation | null;
    fogDensity: WebGLUniformLocation | null;
    fogHeight: WebGLUniformLocation | null;
    fogHeightFalloff: WebGLUniformLocation | null;
    exposure?: WebGLUniformLocation | null;
    objectAlphaMode: WebGLUniformLocation | null;
    objectAlphaCutoff: WebGLUniformLocation | null;
    baseDim: WebGLUniformLocation | null;
  };
  /** Per-chunk RG16UI lookup-текстура с corner UV для всех граней.
   *  Layout: 1D массив (alive_count × 24 texels) уложен в 2D с шириной FACE_UV_LOOKUP_W;
   *  каждый texel = (u, v) корнера как два uint16 (нормализованных 0..65535).
   *  Маппинг: texelIdx = aliveIdx*24 + faceIdx*4 + cornerIdx; row = texelIdx/W, col = texelIdx%W. */
  private faceUVTextures = new Map<number, WebGLTexture>();

  // ── «Точечный стиль»: пыль на стенах + пыль от разрушения ────────────
  private dustSurfProg: WebGLProgram;
  private dustBurstProg: WebGLProgram;
  private uDustSurf: {
    viewProj: WebGLUniformLocation | null;
    uvLookup: WebGLUniformLocation | null;
    lookupW: WebGLUniformLocation | null;
    cameraPos: WebGLUniformLocation | null;
    time: WebGLUniformLocation | null;
    pointScale: WebGLUniformLocation | null;
    diffuse: WebGLUniformLocation | null;
    fogColor: WebGLUniformLocation | null;
    fogDensity: WebGLUniformLocation | null;
    fogHeight: WebGLUniformLocation | null;
    fogHeightFalloff: WebGLUniformLocation | null;
    exposure?: WebGLUniformLocation | null;
  };
  private uDustBurst: {
    viewProj: WebGLUniformLocation | null;
    cameraPos: WebGLUniformLocation | null;
    time: WebGLUniformLocation | null;
    pointScale: WebGLUniformLocation | null;
  };
  /** Батч спавна песчинок: за кадр может прийти сотня бёрстов (обвал) —
   *  копим и заливаем в ring-buffer ОДНИМ bufferSubData в renderDustBursts. */
  private dustPending: number[] = [];
  private dustBurstVAO: WebGLVertexArrayObject;
  private dustBurstBuf: WebGLBuffer;
  /** Ring-buffer голова (в точках). Точка = 12 floats, см. makeDustBurstVAO. */
  private dustBurstHead = 0;
  private _dustEnabled = true;
  private uVox: {
    viewProj: WebGLUniformLocation | null;
    palette: WebGLUniformLocation | null;
    paletteDepth: WebGLUniformLocation | null;
    objectTex: WebGLUniformLocation | null;
    tileSize: WebGLUniformLocation | null;
    vTileScale: WebGLUniformLocation | null;
    cameraPos: WebGLUniformLocation | null;
    pomScale: WebGLUniformLocation | null;
    pomLayerCount: WebGLUniformLocation | null;
    pomMaxDist: WebGLUniformLocation | null;
    lightLodDist: WebGLUniformLocation | null;
    numLights: WebGLUniformLocation | null;
    lights: WebGLUniformLocation | null;
    lightColors: WebGLUniformLocation | null;
    chunkQuat: WebGLUniformLocation | null;
    fogColor: WebGLUniformLocation | null;
    fogDensity: WebGLUniformLocation | null;
    fogHeight: WebGLUniformLocation | null;
    fogHeightFalloff: WebGLUniformLocation | null;
    exposure?: WebGLUniformLocation | null;
    objectNormalTex: WebGLUniformLocation | null;
    objectHasNormal: WebGLUniformLocation | null;
    objectNormalScale: WebGLUniformLocation | null;
    objectAlphaMode: WebGLUniformLocation | null;
    objectAlphaCutoff: WebGLUniformLocation | null;
    voxelBevel: WebGLUniformLocation | null;
    voxelBevelRadius: WebGLUniformLocation | null;
    sceneTime: WebGLUniformLocation | null;
  };
  // Material palette atlas: 9 слотов × 512×512 = 4608×512 RGBA.
  // Слоты horizontally по matKind: 0=brick, 1=concrete, 2=plaster, 3=wood,
  // 4=grass, 5=dirt, 6=stone, 7=metal, 8=glass. Атлас + depth-атлас
  // заменяют 8 отдельных sampler2D: сохраняем бюджет текстурных units
  // (mobile GPU часто имеют MAX_FRAGMENT_TEXTURE_IMAGE_UNITS=16) и даём
  // scalability — для нового материала достаточно расширить атлас.
  private paletteTex: WebGLTexture | null = null;
  private paletteDepthTex: WebGLTexture | null = null;
  /** Map<textureName, GPU texture> для object chunks (chair_diffuse, soldier_diffuse и т.д.) */
  private objectTextures = new Map<string, WebGLTexture>();
  /** Map<normalTextureName, GPU texture> для levelpack normal-карт. Используется
   *  при triplanar-рендере vox-чанков с PBR material'ом из GLB (плющ, кирпич).
   *  Ключ — `levelpacks/<scene>/mat_<N>_n.png` (как в chunk.normalTexture). */
  private objectNormalTextures = new Map<string, WebGLTexture>();
  // Fallback 1×1 RGBA8 для voxel pass — слот 0 ВСЕГДА должен иметь
  // float-sampler-совместимую текстуру, иначе GL_INVALID_OPERATION
  // (после raycast pass там сидит mapTex, который R8UI integer).
  private fallbackTex: WebGLTexture;
  /** 1×1 «плоская» normal map (linear RGB), если у primitive нет normalTexture. */
  private meshFlatNormalTex!: WebGLTexture;
  /** 1×1 ORM по умолчанию: roughness=1, metallic=0. */
  private meshDefaultMRTex!: WebGLTexture;
  // Pre-allocated buffers для передачи ламп в uniform массивы.
  // Размер — MAX_LIGHTS (8) × 4 (vec4/vec3 padded). Шейдеры теперь
  // используют ровно 8 ламп (см. voxel.frag), клиент сортирует их по
  // distance к камере и берёт ближайшие.
  private static readonly MAX_LIGHTS = 8;
  private _lpos = new Float32Array(Renderer.MAX_LIGHTS * 4);
  private _lcol = new Float32Array(Renderer.MAX_LIGHTS * 3);
  /** Сколько ламп подготовлено в _lpos/_lcol в этом кадре. -1 = ещё не
   *  готовили, нужно пересчитать. Сбрасывается в render() в начале кадра. */
  private _preparedNumLights: number = -1;

  // Mesh-renderer (player, NPC, items)
  private meshProg!: WebGLProgram;
  private uMesh!: {
    viewProj: WebGLUniformLocation | null;
    model: WebGLUniformLocation | null;
    diffuse: WebGLUniformLocation | null;
    hasDiffuse: WebGLUniformLocation | null;
    baseColorFactor: WebGLUniformLocation | null;
    bones: WebGLUniformLocation | null;
    useSkin: WebGLUniformLocation | null;
    numLights: WebGLUniformLocation | null;
    lights: WebGLUniformLocation | null;
    lightColors: WebGLUniformLocation | null;
    lightLodDist: WebGLUniformLocation | null;
    bodyDamageMode: WebGLUniformLocation | null;
    bodyGridPack: WebGLUniformLocation | null;
    bodyVoxSize: WebGLUniformLocation | null;
    bodyVoxBits: WebGLUniformLocation | null;
    bodyVoxDebris: WebGLUniformLocation | null;
    bodyDebrisAge: WebGLUniformLocation | null;
    hitCount: WebGLUniformLocation | null;
    hits: WebGLUniformLocation | null;
    actorXY: WebGLUniformLocation | null;
    actorYaw: WebGLUniformLocation | null;
    actorFootZ: WebGLUniformLocation | null;
    cameraPos: WebGLUniformLocation | null;
    fogColor: WebGLUniformLocation | null;
    fogDensity: WebGLUniformLocation | null;
    fogHeight: WebGLUniformLocation | null;
    fogHeightFalloff: WebGLUniformLocation | null;
    exposure?: WebGLUniformLocation | null;
    meatTint: WebGLUniformLocation | null;
    useFlyDetach: WebGLUniformLocation | null;
    flyWorldDelta: WebGLUniformLocation | null;
    bodyVoxBorderDent: WebGLUniformLocation | null;
    bodyDeadVolCount: WebGLUniformLocation | null;
    bodyDeadVol: WebGLUniformLocation | null;
    bodyOuterShell: WebGLUniformLocation | null;
    normalMap: WebGLUniformLocation | null;
    hasNormalMap: WebGLUniformLocation | null;
    metallicRoughness: WebGLUniformLocation | null;
    hasMetallicRoughness: WebGLUniformLocation | null;
    normalScale: WebGLUniformLocation | null;
    roughnessFactor: WebGLUniformLocation | null;
    metallicFactor: WebGLUniformLocation | null;
    alphaMode: WebGLUniformLocation | null;
    alphaCutoff: WebGLUniformLocation | null;
    diffuseVideoBillboard: WebGLUniformLocation | null;
    videoPacked: WebGLUniformLocation | null;
    emissive: WebGLUniformLocation | null; // weapons
  };
  private meshInstances: MeshInstance[] = [];
  /** Пред-выделенные scratch-буферы для матриц (заполняются раз за инстанс).
   *  Размер MAX_BONES_GPU = 64, должен совпадать с MAX_BONES в mesh.vert. */
  private _identityBones = new Float32Array(64 * 16);
  /** Scratch для умножения instModel · nodeWorld для weapon. */
  private _scratchModelMat = new Float32Array(16);
  /** Результат mat4Multiply(im, nodeWorld) без аллокаций в горячем цикле. */
  private _scratchModelMatNode = new Float32Array(16);
  private _flyTScratch = new Float32Array(16);
  private _flyModelOut = new Float32Array(16);
  /** Scratch для bones с зануленными hiddenJoints (FP head-cull). */
  private _hiddenJointsScratch = new Float32Array(64 * 16);
  private _proxySkinM = new Float32Array(16);
  private _proxyWp = new Float32Array(3);
  /** uBodyDeadVol: до BODY_DEAD_VOL_SHADER_MAX сфер (mesh.frag nearDeadVolumeWorld). */
  private _bodyDeadVolBuf = new Float32Array(BODY_DEAD_VOL_SHADER_MAX * 4);
  /** R8 16×19: байты битмаски тела (v12), см. mesh.frag uBodyVoxBits. */
  private _bodyVoxBitsTex: WebGLTexture | null = null;
  private readonly _bodyVoxUploadPad = new Uint8Array(304);
  /** Кэш Float32 участка буфера статических чанков (мировые инстансы не меняются). */
  private _staticChunkCacheBytes = new Float32Array(65536);
  private _staticChunkCacheFloatCount = 0;
  private _staticChunkCacheValid = false;
  private _cacheChunkEpoch = -1;
  private _cachePrefixInstanceCount = -1;
  private _cacheCamX = 0;
  private _cacheCamY = 0;
  private _cacheCamZ = 0;
  private _cacheYaw = 0;
  private _cachePitch = 0;
  private _objectChunksCache: VoxelChunkClient[] = [];
  private _objectChunksEpoch = Number.NaN;
  /** Мировые метры: дальше POM отключён в voxel.frag (0 = без отсечения). */
  private _pomMaxDist = 22;
  /** Дальше — только 4 лампы в шейдере (0 = всегда 8). */
  private _lightLodDist = 26;
  /** Смягчение рёбер/углов инстанс-вокселей (voxel.frag radial normal blend). */
  private _voxelBevel = 0.78;
  private _voxelBevelRadius = 0.2;
  /** UI «Bloom» (состояние чекбокса; пост-пайплайн может не использовать). */
  private _bloomUiEnabled = true;
  /** Bloom/heat haze off when false (mobile lite). */
  private _luxEffectsEnabled = true;
  /** Fewer dust puffs / particle FX on mobile lite. */
  private _mobileLiteParticles = false;
  private _debugPerf = false;
  private readonly _perfLastFrame = {
    raycastMs: 0,
    fillPrefixMs: 0,
    staticChunksMs: 0,
    bufferUploadMs: 0,
    voxelDrawMs: 0,
    dynamicChunksMs: 0,
    objectChunksMs: 0,
    meshesMs: 0,
    postMs: 0,
  };

  // Debris pool
  private debris: DebrisBlock[] = [];
  private tracers: Tracer[] = [];
  private timeAcc = 0;

  /** Параметры height fog (Hello / setSceneFog). density=0 — выкл. */
  private _fogColor: [number, number, number] = [0.5, 0.55, 0.6];
  private _fogDensity = 0;
  private _fogHeight = 0;
  private _fogHeightFalloff = 0;
  /** Множитель освещённого цвета (воксели, меши, объекты) до тумана. База
   *  1.5 — сцена была темновата; ползунок «Яркость» умножает её. */
  private _exposure = EXPOSURE_BASE;

  /** Ползунок «Яркость»: 1 = по умолчанию. */
  setBrightness(k: number): void {
    this._exposure = EXPOSURE_BASE * Math.max(0.5, Math.min(2, k));
  }

  /** Equirect sky (Hello / setSkyboxUrl); null = выкл. */
  private _skyTex: WebGLTexture | null = null;
  private _skyVideo: HTMLVideoElement | null = null;
  private _skyGpuReady = false;
  private _skyLoadGen = 0;
  /** true — текстура неба реально equirect 2:1 (панорама 360°); false — обычное
   *  фото, которое нельзя разворачивать как панораму (см. photoDomeUv в шейдере). */
  private _skyEquirect = false;

  // Сохраняем матрицы между кадрами
  private proj: Mat4 = mat4Identity();
  private view: Mat4 = mat4Identity();
  private viewProj: Mat4 = mat4Identity();
  private eye: Vec3 = vec3();
  private target: Vec3 = vec3();
  private up: Vec3 = vec3(0, 0, 1);
  // Frustum-плоскости мира, пересчитываемые в начале render(). Каждая
  // плоскость — [a,b,c,d] где ax+by+cz+d≥0 означает "внутри". AABB test'ы
  // (см. `chunkInFrustum`) отсекают чанки, полностью находящиеся за любой
  // плоскостью — это отрезает всё что за спиной/сбоку/ниже/выше экрана.
  private frustumPlanes: Float32Array = new Float32Array(24); // 6 planes × 4 floats
  /** Счётчик `culled` чанков за последний кадр — показывается в HUD. */
  public lastCulledCount: number = 0;
  public lastDrawnChunks: number = 0;
  /** Последний кадр: сколько draw вызовов ушло на real-voxel кубы (прототип `?realBody=1`). */
  public lastRealVoxelBodyDrawCalls: number = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const gl = canvas.getContext("webgl2", {
      antialias: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
    });
    if (!gl) throw new Error("WebGL2 unsupported");
    this.gl = gl;

    gl.getExtension("EXT_color_buffer_float");

    this.rcProg = createProgram(gl, raycastVert, raycastFrag);
    this.voxProg = createProgram(gl, voxelVert, voxelFrag);
    this.voxObjProg = createProgram(gl, voxObjVert, voxObjFrag);
    // Пофасадная программа для статики: тот же fragment-шейдер, вершинный
    // генерирует одну грань из gl_VertexID. Динамические чанки (мебель)
    // остаются на старой кубовой программе — им нужен uChunkQuat.
    this.voxObjFaceProg = createProgram(gl, voxObjFaceVert, voxObjFrag);
    this.meshProg = createProgram(gl, meshVert, meshFrag);
    this.dustSurfProg = createProgram(gl, dustSurfaceVert, dustSurfaceFrag);
    this.dustBurstProg = createProgram(gl, dustBurstVert, dustBurstFrag);
    this.uDustSurf = {
      viewProj:  uniformLoc(gl, this.dustSurfProg, "uViewProj"),
      uvLookup:  uniformLoc(gl, this.dustSurfProg, "uUVLookup"),
      lookupW:   uniformLoc(gl, this.dustSurfProg, "uLookupW"),
      cameraPos: uniformLoc(gl, this.dustSurfProg, "uCameraPos"),
      time:      uniformLoc(gl, this.dustSurfProg, "uTime"),
      pointScale: uniformLoc(gl, this.dustSurfProg, "uPointScale"),
      diffuse:   uniformLoc(gl, this.dustSurfProg, "uDiffuse"),
      fogColor:  uniformLoc(gl, this.dustSurfProg, "uFogColor"),
      fogDensity: uniformLoc(gl, this.dustSurfProg, "uFogDensity"),
      fogHeight: uniformLoc(gl, this.dustSurfProg, "uFogHeight"),
      fogHeightFalloff: uniformLoc(gl, this.dustSurfProg, "uFogHeightFalloff"),
    };
    this.uDustBurst = {
      viewProj:  uniformLoc(gl, this.dustBurstProg, "uViewProj"),
      cameraPos: uniformLoc(gl, this.dustBurstProg, "uCameraPos"),
      time:      uniformLoc(gl, this.dustBurstProg, "uTime"),
      pointScale: uniformLoc(gl, this.dustBurstProg, "uPointScale"),
    };

    this.quadVAO = this.makeQuadVAO();
    [this.voxVAO, this.voxInstanceBuf] = this.makeVoxelVAO();
    this.voxInstanceData = new Float32Array(MAX_INSTANCES * INSTANCE_FLOATS);
    [this.voxObjVAO, this.voxObjInstanceBuf] = this.makeVoxObjVAO();
    this.voxObjInstanceData = new Float32Array(MAX_INSTANCES * 8); // 8 floats per cube
    [this.dustBurstVAO, this.dustBurstBuf] = this.makeDustBurstVAO();

    const tex = gl.createTexture();
    if (!tex) throw new Error("createTexture failed");
    this.mapTex = tex;

    // 1×1 серая RGBA8 placeholder
    const fb = gl.createTexture();
    if (!fb) throw new Error("createTexture (fallback) failed");
    gl.bindTexture(gl.TEXTURE_2D, fb);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([128, 128, 128, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    this.fallbackTex = fb;

    const meshN = gl.createTexture();
    if (!meshN) throw new Error("createTexture (mesh flat normal) failed");
    gl.bindTexture(gl.TEXTURE_2D, meshN);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([128, 128, 255, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    this.meshFlatNormalTex = meshN;

    const meshMr = gl.createTexture();
    if (!meshMr) throw new Error("createTexture (mesh default MR) failed");
    gl.bindTexture(gl.TEXTURE_2D, meshMr);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([255, 255, 0, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    this.meshDefaultMRTex = meshMr;
    gl.bindTexture(gl.TEXTURE_2D, null);

    this.uRC = {
      map: uniformLoc(gl, this.rcProg, "uMap"),
      mapSize: uniformLoc(gl, this.rcProg, "uMapSize"),
      playerPos: uniformLoc(gl, this.rcProg, "uPlayerPos"),
      yaw: uniformLoc(gl, this.rcProg, "uPlayerYaw"),
      pitch: uniformLoc(gl, this.rcProg, "uPlayerPitch"),
      fovY: uniformLoc(gl, this.rcProg, "uFovY"),
      aspect: uniformLoc(gl, this.rcProg, "uAspect"),
      time: uniformLoc(gl, this.rcProg, "uTime"),
      eyeHeight: uniformLoc(gl, this.rcProg, "uEyeHeight"),
      ceilHeight: uniformLoc(gl, this.rcProg, "uCeilHeight"),
      near: uniformLoc(gl, this.rcProg, "uNear"),
      far: uniformLoc(gl, this.rcProg, "uFar"),
      cameraPos: uniformLoc(gl, this.rcProg, "uCameraPos"),
      fogColor: uniformLoc(gl, this.rcProg, "uFogColor"),
      fogDensity: uniformLoc(gl, this.rcProg, "uFogDensity"),
      fogHeight: uniformLoc(gl, this.rcProg, "uFogHeight"),
      fogHeightFalloff: uniformLoc(gl, this.rcProg, "uFogHeightFalloff"),
      sky: uniformLoc(gl, this.rcProg, "uSky"),
      skyEnabled: uniformLoc(gl, this.rcProg, "uSkyEnabled"),
      skyEquirect: uniformLoc(gl, this.rcProg, "uSkyEquirect"),
    };
    this.uVoxObj = {
      viewProj:    uniformLoc(gl, this.voxObjProg, "uViewProj"),
      uvLookup:    uniformLoc(gl, this.voxObjProg, "uUVLookup"),
      diffuse:     uniformLoc(gl, this.voxObjProg, "uDiffuse"),
      lookupW:     uniformLoc(gl, this.voxObjProg, "uLookupW"),
      numLights:   uniformLoc(gl, this.voxObjProg, "uNumLights"),
      lights:      uniformLoc(gl, this.voxObjProg, "uLights"),
      lightColors: uniformLoc(gl, this.voxObjProg, "uLightColors"),
      chunkQuat:   uniformLoc(gl, this.voxObjProg, "uChunkQuat"),
      cameraPos:   uniformLoc(gl, this.voxObjProg, "uCameraPos"),
      lightLodDist: uniformLoc(gl, this.voxObjProg, "uLightLodDist"),
      fogColor:    uniformLoc(gl, this.voxObjProg, "uFogColor"),
      fogDensity:  uniformLoc(gl, this.voxObjProg, "uFogDensity"),
      fogHeight:   uniformLoc(gl, this.voxObjProg, "uFogHeight"),
      fogHeightFalloff: uniformLoc(gl, this.voxObjProg, "uFogHeightFalloff"),
      exposure: uniformLoc(gl, this.voxObjProg, "uExposure"),
      objectAlphaMode:   uniformLoc(gl, this.voxObjProg, "uObjectAlphaMode"),
      objectAlphaCutoff: uniformLoc(gl, this.voxObjProg, "uObjectAlphaCutoff"),
      baseDim:           uniformLoc(gl, this.voxObjProg, "uBaseDim"),
    };
    this.uVoxObjFace = {
      viewProj:    uniformLoc(gl, this.voxObjFaceProg, "uViewProj"),
      uvLookup:    uniformLoc(gl, this.voxObjFaceProg, "uUVLookup"),
      diffuse:     uniformLoc(gl, this.voxObjFaceProg, "uDiffuse"),
      lookupW:     uniformLoc(gl, this.voxObjFaceProg, "uLookupW"),
      numLights:   uniformLoc(gl, this.voxObjFaceProg, "uNumLights"),
      lights:      uniformLoc(gl, this.voxObjFaceProg, "uLights"),
      lightColors: uniformLoc(gl, this.voxObjFaceProg, "uLightColors"),
      cameraPos:   uniformLoc(gl, this.voxObjFaceProg, "uCameraPos"),
      lightLodDist: uniformLoc(gl, this.voxObjFaceProg, "uLightLodDist"),
      fogColor:    uniformLoc(gl, this.voxObjFaceProg, "uFogColor"),
      fogDensity:  uniformLoc(gl, this.voxObjFaceProg, "uFogDensity"),
      fogHeight:   uniformLoc(gl, this.voxObjFaceProg, "uFogHeight"),
      fogHeightFalloff: uniformLoc(gl, this.voxObjFaceProg, "uFogHeightFalloff"),
      exposure: uniformLoc(gl, this.voxObjFaceProg, "uExposure"),
      objectAlphaMode:   uniformLoc(gl, this.voxObjFaceProg, "uObjectAlphaMode"),
      objectAlphaCutoff: uniformLoc(gl, this.voxObjFaceProg, "uObjectAlphaCutoff"),
      baseDim:           uniformLoc(gl, this.voxObjFaceProg, "uBaseDim"),
    };
    this.uMesh = {
      viewProj:    uniformLoc(gl, this.meshProg, "uViewProj"),
      model:       uniformLoc(gl, this.meshProg, "uModel"),
      diffuse:     uniformLoc(gl, this.meshProg, "uDiffuse"),
      hasDiffuse:  uniformLoc(gl, this.meshProg, "uHasDiffuse"),
      baseColorFactor: uniformLoc(gl, this.meshProg, "uBaseColorFactor"),
      bones:       uniformLoc(gl, this.meshProg, "uBones"),
      useSkin:     uniformLoc(gl, this.meshProg, "uUseSkin"),
      numLights:   uniformLoc(gl, this.meshProg, "uNumLights"),
      lights:      uniformLoc(gl, this.meshProg, "uLights"),
      lightColors: uniformLoc(gl, this.meshProg, "uLightColors"),
      lightLodDist: uniformLoc(gl, this.meshProg, "uLightLodDist"),
      bodyDamageMode: uniformLoc(gl, this.meshProg, "uBodyDamageMode"),
      bodyGridPack: uniformLoc(gl, this.meshProg, "uBodyGridPack"),
      bodyVoxSize: uniformLoc(gl, this.meshProg, "uBodyVoxSize"),
      bodyVoxBits: uniformLoc(gl, this.meshProg, "uBodyVoxBits"),
      bodyVoxDebris: uniformLoc(gl, this.meshProg, "uBodyVoxDebris"),
      bodyDebrisAge: uniformLoc(gl, this.meshProg, "uBodyDebrisAge"),
      hitCount:    uniformLoc(gl, this.meshProg, "uHitCount"),
      hits:        uniformLoc(gl, this.meshProg, "uHits[0]"),
      actorXY:     uniformLoc(gl, this.meshProg, "uActorXY"),
      actorYaw:    uniformLoc(gl, this.meshProg, "uActorYaw"),
      actorFootZ:  uniformLoc(gl, this.meshProg, "uActorFootZ"),
      cameraPos:   uniformLoc(gl, this.meshProg, "uCameraPos"),
      fogColor:    uniformLoc(gl, this.meshProg, "uFogColor"),
      fogDensity:  uniformLoc(gl, this.meshProg, "uFogDensity"),
      fogHeight:   uniformLoc(gl, this.meshProg, "uFogHeight"),
      fogHeightFalloff: uniformLoc(gl, this.meshProg, "uFogHeightFalloff"),
      exposure: uniformLoc(gl, this.meshProg, "uExposure"),
      meatTint:      uniformLoc(gl, this.meshProg, "uMeatTint"),
      useFlyDetach:  uniformLoc(gl, this.meshProg, "uUseFlyDetach"),
      flyWorldDelta: uniformLoc(gl, this.meshProg, "uFlyWorldDelta"),
      bodyVoxBorderDent: uniformLoc(gl, this.meshProg, "uBodyVoxBorderDent"),
    bodyDeadVolCount: uniformLoc(gl, this.meshProg, "uBodyDeadVolCount"),
    bodyDeadVol: uniformLoc(gl, this.meshProg, "uBodyDeadVol[0]"),
    bodyOuterShell: uniformLoc(gl, this.meshProg, "uBodyOuterShell"),
    normalMap: uniformLoc(gl, this.meshProg, "uNormalMap"),
    hasNormalMap: uniformLoc(gl, this.meshProg, "uHasNormalMap"),
    metallicRoughness: uniformLoc(gl, this.meshProg, "uMetallicRoughness"),
    hasMetallicRoughness: uniformLoc(gl, this.meshProg, "uHasMetallicRoughness"),
    normalScale: uniformLoc(gl, this.meshProg, "uNormalScale"),
    roughnessFactor: uniformLoc(gl, this.meshProg, "uRoughnessFactor"),
    metallicFactor: uniformLoc(gl, this.meshProg, "uMetallicFactor"),
    alphaMode: uniformLoc(gl, this.meshProg, "uAlphaMode"),
    alphaCutoff: uniformLoc(gl, this.meshProg, "uAlphaCutoff"),
    diffuseVideoBillboard: uniformLoc(gl, this.meshProg, "uDiffuseVideoBillboard"),
    videoPacked: uniformLoc(gl, this.meshProg, "uVideoPacked"),
    emissive: uniformLoc(gl, this.meshProg, "uEmissive"), // weapons
  };
    // Identity для uBones когда инстанс без skin'а (чтобы шейдер не подсасывал
    // мусор из uniform-памяти). 64 матрицы — лимит mesh.vert MAX_BONES.
    for (let i = 0; i < 64; i++) {
      const o = i * 16;
      this._identityBones[o + 0] = 1;
      this._identityBones[o + 5] = 1;
      this._identityBones[o + 10] = 1;
      this._identityBones[o + 15] = 1;
    }
    this.uVox = {
      viewProj:    uniformLoc(gl, this.voxProg, "uViewProj"),
      palette:      uniformLoc(gl, this.voxProg, "uPalette"),
      paletteDepth: uniformLoc(gl, this.voxProg, "uPaletteDepth"),
      objectTex:   uniformLoc(gl, this.voxProg, "uObjectTex"),
      tileSize:    uniformLoc(gl, this.voxProg, "uTileSize"),
      vTileScale:  uniformLoc(gl, this.voxProg, "uVTileScale"),
      cameraPos:   uniformLoc(gl, this.voxProg, "uCameraPos"),
      pomScale:    uniformLoc(gl, this.voxProg, "uPomScale"),
      pomLayerCount: uniformLoc(gl, this.voxProg, "uPomLayerCount"),
      pomMaxDist:    uniformLoc(gl, this.voxProg, "uPomMaxDist"),
      lightLodDist:  uniformLoc(gl, this.voxProg, "uLightLodDist"),
      numLights:   uniformLoc(gl, this.voxProg, "uNumLights"),
      lights:      uniformLoc(gl, this.voxProg, "uLights"),
    lightColors: uniformLoc(gl, this.voxProg, "uLightColors"),
    chunkQuat:   uniformLoc(gl, this.voxProg, "uChunkQuat"),
    fogColor:    uniformLoc(gl, this.voxProg, "uFogColor"),
    fogDensity:  uniformLoc(gl, this.voxProg, "uFogDensity"),
    fogHeight:   uniformLoc(gl, this.voxProg, "uFogHeight"),
    fogHeightFalloff: uniformLoc(gl, this.voxProg, "uFogHeightFalloff"),
    exposure: uniformLoc(gl, this.voxProg, "uExposure"),
    objectNormalTex:   uniformLoc(gl, this.voxProg, "uObjectNormalTex"),
    objectHasNormal:   uniformLoc(gl, this.voxProg, "uObjectHasNormal"),
    objectNormalScale: uniformLoc(gl, this.voxProg, "uObjectNormalScale"),
    objectAlphaMode:   uniformLoc(gl, this.voxProg, "uObjectAlphaMode"),
    objectAlphaCutoff: uniformLoc(gl, this.voxProg, "uObjectAlphaCutoff"),
      voxelBevel:        uniformLoc(gl, this.voxProg, "uVoxelBevel"),
      voxelBevelRadius:  uniformLoc(gl, this.voxProg, "uVoxelBevelRadius"),
      sceneTime:         uniformLoc(gl, this.voxProg, "uSceneTime"),
    };
    this.postProg = createProgram(gl, postVert, postFrag);
    this.uPost = {
      scene: uniformLoc(gl, this.postProg, "uScene"),
      invRes: uniformLoc(gl, this.postProg, "uInvRes"),
      time: uniformLoc(gl, this.postProg, "uTime"),
      bloom: uniformLoc(gl, this.postProg, "uBloom"),
      bloomMix: uniformLoc(gl, this.postProg, "uBloomMix"),
      heatStrength: uniformLoc(gl, this.postProg, "uHeatStrength"),
      heatUv: uniformLoc(gl, this.postProg, "uHeatUv"),
      posterize: uniformLoc(gl, this.postProg, "uPosterize"),
    };
    this.bloomExtractProg = createProgram(gl, postVert, bloomExtractFrag);
    this.bloomBlurProg = createProgram(gl, postVert, bloomBlurFrag);
    this.uBloomExtract = {
      tex: uniformLoc(gl, this.bloomExtractProg, "uTex"),
      threshold: uniformLoc(gl, this.bloomExtractProg, "uThreshold"),
      knee: uniformLoc(gl, this.bloomExtractProg, "uKnee"),
    };
    this.uBloomBlur = {
      tex: uniformLoc(gl, this.bloomBlurProg, "uTex"),
      texel: uniformLoc(gl, this.bloomBlurProg, "uTexel"),
      dir: uniformLoc(gl, this.bloomBlurProg, "uDir"),
    };
    this.particleRenderer = new ParticleRenderer(gl);
  }

  /** Клиентская симуляция частиц (дым/огонь); null — не рисовать. */
  setParticleSimulation(sim: ParticleSimulation | null): void {
    this.particleSim = sim;
  }

  /** Mobile lite: меньше частиц пыли при ударе дербиса об пол. */
  setMobileLiteParticles(on: boolean): void {
    this._mobileLiteParticles = on;
  }

  /** Mobile lite: bloom + heat haze off. */
  setLuxEffectsEnabled(on: boolean): void {
    this._luxEffectsEnabled = on;
  }

  setBloomUiEnabled(on: boolean): void {
    this._bloomUiEnabled = on;
  }

  getBloomUiEnabled(): boolean {
    return this._bloomUiEnabled;
  }

  /** Процедурный атлас частиц vs тяжёлый FBM в шейдере. */
  setParticleAtlasEnabled(on: boolean): void {
    this.particleRenderer.setUseAtlas(on);
  }

  /** Сцена с сервера (Hello): экспоненциальный height fog. */
  setSceneFog(p: {
    color: [number, number, number];
    density: number;
    height: number;
    heightFalloff: number;
  }): void {
    this._fogColor[0] = p.color[0];
    this._fogColor[1] = p.color[1];
    this._fogColor[2] = p.color[2];
    this._fogDensity = p.density;
    this._fogHeight = p.height;
    this._fogHeightFalloff = p.heightFalloff;
  }

  /** story: WebGL-контекст для модулей со своими шейдерами (src/story). */
  getGL(): WebGL2RenderingContext {
    return this.gl;
  }

  /** story: прозрачный проход сюжетных билбордов (зомби, призраки, картины,
   *  книга) — после опаковой геометрии, до частиц. null — режим не сюжетный. */
  public storyPass: ((ctx: StoryPassContext) => void) | null = null;

  /** Когда true — НЕ рендерим static voxel chunks (визуально); их роль уровня
   *  занимает GLB из level-pack (см. main.ts loadLevelStaticMesh). Воксели всё
   *  равно ходят между сервером и клиентом — нужны для коллизии и debris. */
  public hideStaticVoxels: boolean = false;

  /** Небо: equirect 2:1 (панорама 360°) или обычное фото/видео неба; пустая
   *  строка — процедурное небо. Формат определяем по аспекту кадра: только
   *  ~2:1 сэмплируется как equirect, всё остальное — купол-проекция фото
   *  (photoDomeUv), иначе не-панорама сворачивается у зенита в «шар-планету». */
  setSkyboxUrl(url: string): void {
    const u = (url || "").trim();
    this._skyLoadGen++;
    const gen = this._skyLoadGen;
    this._releaseSkybox();
    if (!u) return;

    const gl = this.gl;
    const isVideo = /\.(webm|mp4|ogg)(\?.*)?$/i.test(u);

    const tex = gl.createTexture();
    if (!tex) return;
    this._skyTex = tex;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([24, 28, 40, 255]));

    if (isVideo) {
      const v = document.createElement("video");
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.preload = "auto";
      v.src = u;
      this._skyVideo = v;
      v.addEventListener("loadeddata", () => {
        if (gen !== this._skyLoadGen) return;
        this._applySkyAspect(v.videoWidth, v.videoHeight);
        this._skyGpuReady = true;
        void v.play().catch(() => { /* автозапуск может быть заблокирован до жеста */ });
      });
      v.addEventListener("error", () => {
        if (gen !== this._skyLoadGen) return;
        console.warn("[skybox] video failed:", u);
        this._releaseSkybox();
      });
      void v.load();
    } else {
      const img = new Image();
      img.onload = () => {
        if (gen !== this._skyLoadGen) return;
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const isPot = (n: number) => n > 0 && (n & (n - 1)) === 0;
        if (isPot(iw) && isPot(ih)) {
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
          gl.generateMipmap(gl.TEXTURE_2D);
        }
        this._applySkyAspect(iw, ih);
        this._skyGpuReady = true;
      };
      img.onerror = () => {
        if (gen !== this._skyLoadGen) return;
        console.warn("[skybox] image failed:", u);
        this._releaseSkybox();
      };
      img.src = u;
    }
  }

  /** По размеру кадра решает, панорама это или фото: equirect всегда ровно 2:1.
   *  Для панорамы включает WRAP_S=REPEAT, чтобы шов по азимуту (u=0/1) был
   *  бесшовным (WebGL2 разрешает REPEAT и для NPOT). Текстура неба должна быть
   *  привязана вызывающим кодом (после texImage2D так и есть). */
  private _applySkyAspect(w: number, h: number): void {
    if (!(w > 0) || !(h > 0)) return;
    this._skyEquirect = Math.abs(w / h - 2.0) < 0.25;
    const gl = this.gl;
    if (this._skyTex) {
      gl.bindTexture(gl.TEXTURE_2D, this._skyTex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S,
        this._skyEquirect ? gl.REPEAT : gl.CLAMP_TO_EDGE);
    }
  }

  private _releaseSkybox(): void {
    const gl = this.gl;
    if (this._skyVideo) {
      try {
        this._skyVideo.pause();
        this._skyVideo.removeAttribute("src");
        this._skyVideo.load();
      } catch { /* ignore */ }
      this._skyVideo = null;
    }
    if (this._skyTex) {
      gl.deleteTexture(this._skyTex);
      this._skyTex = null;
    }
    this._skyGpuReady = false;
    this._skyEquirect = false;
  }

  private bindFogUniforms(gl: WebGL2RenderingContext, u: {
    color: WebGLUniformLocation | null;
    density: WebGLUniformLocation | null;
    height: WebGLUniformLocation | null;
    falloff: WebGLUniformLocation | null;
    exposure?: WebGLUniformLocation | null;
  }): void {
    if (u.exposure) gl.uniform1f(u.exposure, this._exposure);
    if (!u.color || !u.density || !u.height || !u.falloff) return;
    gl.uniform3f(u.color, this._fogColor[0], this._fogColor[1], this._fogColor[2]);
    gl.uniform1f(u.density, this._fogDensity);
    gl.uniform1f(u.height, this._fogHeight);
    gl.uniform1f(u.falloff, this._fogHeightFalloff);
  }

  /** Залить material-palette атласы в GPU. Вызывается ОДИН раз из main.start
   *  после того как атласы собраны через `buildMaterialAtlas`. Mipmap ОТКЛЮЧЁН:
   *  на границах слотов (kind/9) mipmap смешал бы соседние материалы, даже
   *  если шейдер честно fract()'ит tile UV. Потери фильтрации компенсируются
   *  `textureGrad` в шейдере (при желании — позже). */
  uploadMaterialAtlases(palette: Texture | null, paletteDepth: Texture | null): void {
    const gl = this.gl;
    if (palette) {
      this.paletteTex = uploadGLTexture(gl, palette, { wrap: gl.CLAMP_TO_EDGE, mipmap: false, filter: gl.LINEAR });
    }
    if (paletteDepth) {
      this.paletteDepthTex = uploadGLTexture(gl, paletteDepth, { wrap: gl.CLAMP_TO_EDGE, mipmap: false, filter: gl.LINEAR });
    }
  }

  /** Object diffuse текстуры (chair_diffuse, soldier_diffuse) — регистрируются
   *  отдельно по имени; выбираются в render по chunk.textureName. */
  uploadObjectTextures(): void {
    const gl = this.gl;
    for (const name of ["chair_diffuse", "soldier_diffuse"]) {
      const tex = getTexture(name);
      if (tex) {
        this.objectTextures.set(name, uploadGLTexture(gl, tex, { wrap: gl.CLAMP_TO_EDGE, mipmap: true }));
      }
    }
  }

  /** GPU-текстуры для чанков level-pack (`texture_name` = levelpacks/...). */
  uploadLevelpackObjectTextures(names: Iterable<string>): void {
    const gl = this.gl;
    for (const name of names) {
      if (!isLevelpackDiffuseTex(name)) continue;
      const tex = getTexture(name);
      if (tex) {
        this.objectTextures.set(
          name,
          uploadGLTexture(gl, tex, { wrap: gl.REPEAT, mipmap: true }),
        );
      }
    }
  }

  /** Normal-карты для level-pack чанков (`mat_N_n.png`). Каждая нормал-мапа
   *  хранится как линейный (не sRGB) RGB texture; здесь идёт обычный
   *  uploadGLTexture (sRGB-преобразование происходит в шейдере). */
  uploadLevelpackNormalTextures(names: Iterable<string>): void {
    const gl = this.gl;
    for (const name of names) {
      if (!isLevelpackDiffuseTex(name)) continue;
      const tex = getTexture(name);
      if (tex) {
        this.objectNormalTextures.set(
          name,
          uploadGLTexture(gl, tex, { wrap: gl.REPEAT, mipmap: true }),
        );
      }
    }
  }

  uploadMap(map: GridMap): void {
    const gl = this.gl;
    this.mapSize = [map.width, map.height];
    gl.bindTexture(gl.TEXTURE_2D, this.mapTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.R8UI, map.width, map.height, 0,
      gl.RED_INTEGER, gl.UNSIGNED_BYTE, map.cells,
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  // Кэшированные размеры canvas в CSS-пикселях. Обновляются лениво: в
  // первый кадр + при window 'resize'. `canvas.clientWidth` — это layout
  // query, который форсирует synchronous reflow после любого DOM-write
  // в предыдущем кадре. Вызов его 120 раз/сек давал FPS 12-17 даже на
  // мощных машинах — это было главное bottleneck'ом.
  private _sizeDirty = true;
  private _cssW = 0;
  private _cssH = 0;
  /** Верхняя граница для `devicePixelRatio` при расчёте backing-store (1 = экономия на телефонах). */
  private _maxDevicePixelRatio = 2;
  /** Внутреннее разрешение = client×DPR×_renderScale; 1 = как раньше. */
  private _renderScale = 1.0;
  /** Пиксель-арт: backing-store фиксированной высоты + pixelated-апскейл. */
  private _pixelArt = false;
  private _pixelArtTargetH = 360;
  /** POM-слои в voxel.frag: 0 / 4 / 8 (по умолчанию 8 как до настроек). */
  private _pomLayers: PomLayerOption = 8;
  /** Fullscreen-пост (зерно, виньетка, FXAA) — рендер в FBO + один pass на canvas. */
  private _postProcessEnabled = false;
  private _sceneFbo: WebGLFramebuffer | null = null;
  private _sceneColorTex: WebGLTexture | null = null;
  /** Глубина сцены для soft particles; при fallback на renderbuffer — null. */
  private _sceneDepthTex: WebGLTexture | null = null;
  /** Копия depth (не привязана к `_sceneFbo`) — сэмпл для частиц без feedback loop. */
  private _sceneDepthTexRead: WebGLTexture | null = null;
  private _depthBlitFbo: WebGLFramebuffer | null = null;
  private _sceneDepthRb: WebGLRenderbuffer | null = null;
  private _sceneFbW = 0;
  private _sceneFbH = 0;
  private _sceneFboReady = false;
  private _postProcessAppliedLastFrame = false;
  private _postFboLastFailHex: string | null = null;
  private postProg: WebGLProgram;
  private uPost: {
    scene: WebGLUniformLocation | null;
    invRes: WebGLUniformLocation | null;
    time: WebGLUniformLocation | null;
    bloom: WebGLUniformLocation | null;
    bloomMix: WebGLUniformLocation | null;
    heatStrength: WebGLUniformLocation | null;
    heatUv: WebGLUniformLocation | null;
    posterize: WebGLUniformLocation | null;
  };
  private bloomExtractProg: WebGLProgram;
  private bloomBlurProg: WebGLProgram;
  private uBloomExtract: {
    tex: WebGLUniformLocation | null;
    threshold: WebGLUniformLocation | null;
    knee: WebGLUniformLocation | null;
  };
  private uBloomBlur: {
    tex: WebGLUniformLocation | null;
    texel: WebGLUniformLocation | null;
    dir: WebGLUniformLocation | null;
  };
  private _bloomHalfTexA: WebGLTexture | null = null;
  private _bloomHalfTexB: WebGLTexture | null = null;
  private _bloomHalfFboA: WebGLFramebuffer | null = null;
  private _bloomHalfFboB: WebGLFramebuffer | null = null;
  private _bloomHalfW = 0;
  private _bloomHalfH = 0;
  private particleRenderer: ParticleRenderer;
  private particleSim: ParticleSimulation | null = null;
  /** Пометить, что размер canvas может измениться (например window 'resize').
   *  resize() в следующем кадре запросит clientWidth/Height. */
  markResize(): void {
    this._sizeDirty = true;
  }

  getRenderScale(): number {
    return this._renderScale;
  }

  /** Множитель к backing-store (0.5..1); CSS-размер canvas не меняется. */
  setRenderScale(scale: number): void {
    const s = Math.min(RENDER_SCALE_MAX, Math.max(RENDER_SCALE_MIN, scale));
    if (s === this._renderScale) return;
    this._renderScale = s;
    this.markResize();
  }

  getMaxDevicePixelRatio(): number {
    return this._maxDevicePixelRatio;
  }

  /** Ограничить DPR (например 1 на мобильных — меньше пикселей без смены CSS-размера canvas). */
  setMaxDevicePixelRatio(max: number): void {
    const m = Math.max(0.5, Math.min(3, max));
    if (m === this._maxDevicePixelRatio) return;
    this._maxDevicePixelRatio = m;
    this.markResize();
  }

  getPomLayers(): PomLayerOption {
    return this._pomLayers;
  }

  setPomLayers(layers: PomLayerOption): void {
    if (layers !== 0 && layers !== 4 && layers !== 8) return;
    this._pomLayers = layers;
  }

  /** Визуальное «закругление» рёбер инстанс-кубов (только шейдинг; коллизии не меняются). */
  setVoxelEdgeSoftness(strength: number, radius?: number): void {
    this._voxelBevel = Math.max(0, Math.min(1, strength));
    if (radius !== undefined) {
      this._voxelBevelRadius = Math.max(0.04, Math.min(0.48, radius));
    }
  }

  getPostProcessEnabled(): boolean {
    return this._postProcessEnabled;
  }

  /** Диагностика: хотели пост, есть ли FBO, применился ли в последнем render(), код ошибки FBO. */
  getPostProcessDiagnostics(): {
    enabled: boolean;
    fboReady: boolean;
    appliedLastFrame: boolean;
    canvasW: number;
    canvasH: number;
    fboFailHex: string | null;
  } {
    return {
      enabled: this._postProcessEnabled,
      fboReady: this._sceneFboReady,
      appliedLastFrame: this._postProcessAppliedLastFrame,
      canvasW: this.canvas.width,
      canvasH: this.canvas.height,
      fboFailHex: this._postFboLastFailHex,
    };
  }

  /** Вкл: сцена в offscreen RGBA8 + depth, затем один fullscreen-pass (FXAA, зерно, виньетка). Выкл: без FBO. */
  setPostProcessEnabled(on: boolean): void {
    if (on === this._postProcessEnabled) return;
    this._postProcessEnabled = on;
    if (!on) {
      this.disposeSceneFramebuffer();
    }
    this.markResize();
  }

  private disposeSceneFramebuffer(): void {
    this.disposeBloomFramebuffers();
    const gl = this.gl;
    if (this._sceneFbo) {
      gl.deleteFramebuffer(this._sceneFbo);
      this._sceneFbo = null;
    }
    if (this._sceneColorTex) {
      gl.deleteTexture(this._sceneColorTex);
      this._sceneColorTex = null;
    }
    if (this._sceneDepthTex) {
      gl.deleteTexture(this._sceneDepthTex);
      this._sceneDepthTex = null;
    }
    if (this._sceneDepthTexRead) {
      gl.deleteTexture(this._sceneDepthTexRead);
      this._sceneDepthTexRead = null;
    }
    if (this._depthBlitFbo) {
      gl.deleteFramebuffer(this._depthBlitFbo);
      this._depthBlitFbo = null;
    }
    if (this._sceneDepthRb) {
      gl.deleteRenderbuffer(this._sceneDepthRb);
      this._sceneDepthRb = null;
    }
    this._sceneFbW = 0;
    this._sceneFbH = 0;
    this._sceneFboReady = false;
    this._postProcessAppliedLastFrame = false;
    this._postFboLastFailHex = null;
  }

  private disposeBloomFramebuffers(): void {
    const gl = this.gl;
    if (this._bloomHalfFboA) {
      gl.deleteFramebuffer(this._bloomHalfFboA);
      this._bloomHalfFboA = null;
    }
    if (this._bloomHalfFboB) {
      gl.deleteFramebuffer(this._bloomHalfFboB);
      this._bloomHalfFboB = null;
    }
    if (this._bloomHalfTexA) {
      gl.deleteTexture(this._bloomHalfTexA);
      this._bloomHalfTexA = null;
    }
    if (this._bloomHalfTexB) {
      gl.deleteTexture(this._bloomHalfTexB);
      this._bloomHalfTexB = null;
    }
    this._bloomHalfW = 0;
    this._bloomHalfH = 0;
  }

  private ensureBloomHalfRes(hw: number, hh: number): void {
    if (hw <= 0 || hh <= 0) return;
    if (this._bloomHalfW === hw && this._bloomHalfH === hh && this._bloomHalfTexA && this._bloomHalfFboA) {
      return;
    }
    const gl = this.gl;
    this.disposeBloomFramebuffers();
    const ta = gl.createTexture();
    const tb = gl.createTexture();
    const fa = gl.createFramebuffer();
    const fb = gl.createFramebuffer();
    if (!ta || !tb || !fa || !fb) {
      if (ta) gl.deleteTexture(ta);
      if (tb) gl.deleteTexture(tb);
      if (fa) gl.deleteFramebuffer(fa);
      if (fb) gl.deleteFramebuffer(fb);
      return;
    }
    for (const T of [ta, tb]) {
      gl.bindTexture(gl.TEXTURE_2D, T);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, hw, hh, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, fa);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, ta, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tb, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, null);
    this._bloomHalfTexA = ta;
    this._bloomHalfTexB = tb;
    this._bloomHalfFboA = fa;
    this._bloomHalfFboB = fb;
    this._bloomHalfW = hw;
    this._bloomHalfH = hh;
  }

  /** Bright-pass + separable blur → `_bloomHalfTexA`. */
  private runBloomPass(sceneW: number, sceneH: number): void {
    const gl = this.gl;
    if (
      !this._luxEffectsEnabled ||
      !this._bloomUiEnabled ||
      !this._sceneColorTex ||
      sceneW <= 0 ||
      sceneH <= 0
    ) {
      return;
    }
    const hw = Math.max(1, Math.floor(sceneW / 2));
    const hh = Math.max(1, Math.floor(sceneH / 2));
    this.ensureBloomHalfRes(hw, hh);
    if (!this._bloomHalfTexA || !this._bloomHalfTexB || !this._bloomHalfFboA || !this._bloomHalfFboB) {
      return;
    }

    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);
    gl.viewport(0, 0, hw, hh);
    gl.useProgram(this.bloomExtractProg);
    gl.bindVertexArray(this.quadVAO);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this._sceneColorTex);
    if (this.uBloomExtract.tex) gl.uniform1i(this.uBloomExtract.tex, 0);
    if (this.uBloomExtract.threshold) gl.uniform1f(this.uBloomExtract.threshold, 0.72);
    if (this.uBloomExtract.knee) gl.uniform1f(this.uBloomExtract.knee, 0.12);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this._bloomHalfFboA);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.useProgram(this.bloomBlurProg);
    if (this.uBloomBlur.tex) gl.uniform1i(this.uBloomBlur.tex, 0);
    if (this.uBloomBlur.texel) gl.uniform2f(this.uBloomBlur.texel, 1 / hw, 1 / hh);

    gl.bindFramebuffer(gl.FRAMEBUFFER, this._bloomHalfFboB);
    gl.bindTexture(gl.TEXTURE_2D, this._bloomHalfTexA);
    if (this.uBloomBlur.dir) gl.uniform2f(this.uBloomBlur.dir, 1, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.bindFramebuffer(gl.FRAMEBUFFER, this._bloomHalfFboA);
    gl.bindTexture(gl.TEXTURE_2D, this._bloomHalfTexB);
    if (this.uBloomBlur.dir) gl.uniform2f(this.uBloomBlur.dir, 0, 1);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, sceneW, sceneH);
    gl.bindVertexArray(null);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  /** Пересоздать FBO при смене размера backing-store; при ошибке — без FBO (кадр пойдёт в default FB). */
  private ensureSceneFramebuffer(w: number, h: number): void {
    if (!this._postProcessEnabled || w <= 0 || h <= 0) {
      return;
    }
    if (this._sceneFboReady && this._sceneFbW === w && this._sceneFbH === h && this._sceneFbo !== null) {
      return;
    }

    this.disposeSceneFramebuffer();
    const gl = this.gl;
    const tex = gl.createTexture();
    const fbo = gl.createFramebuffer();
    const depthTex = gl.createTexture();
    if (!tex || !fbo || !depthTex) {
      console.warn("[renderer] post-process: failed to create FBO objects");
      if (tex) gl.deleteTexture(tex);
      if (fbo) gl.deleteFramebuffer(fbo);
      if (depthTex) gl.deleteTexture(depthTex);
      return;
    }

    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.bindTexture(gl.TEXTURE_2D, depthTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const halfFloatBuf =
      gl.getExtension("EXT_color_buffer_float") ?? gl.getExtension("EXT_color_buffer_half_float");
    type ColorTry = { intFmt: number; srcFmt: number; srcType: number };
    const colorTries: ColorTry[] = [];
    if (halfFloatBuf) {
      colorTries.push({ intFmt: gl.RGBA16F, srcFmt: gl.RGBA, srcType: gl.HALF_FLOAT });
    }
    colorTries.push({ intFmt: gl.RGBA8, srcFmt: gl.RGBA, srcType: gl.UNSIGNED_BYTE });
    colorTries.push({ intFmt: gl.RGBA, srcFmt: gl.RGBA, srcType: gl.UNSIGNED_BYTE });

    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    let complete = false;
    let lastBadStatus = 0;

    outerTex:
    for (const ct of colorTries) {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, ct.intFmt, w, h, 0, ct.srcFmt, ct.srcType, null);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);

      gl.bindTexture(gl.TEXTURE_2D, depthTex);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.DEPTH_COMPONENT24,
        w,
        h,
        0,
        gl.DEPTH_COMPONENT,
        gl.UNSIGNED_INT,
        null,
      );
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, depthTex, 0);

      const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
      if (status === gl.FRAMEBUFFER_COMPLETE) {
        complete = true;
        this._sceneFbo = fbo;
        this._sceneColorTex = tex;
        this._sceneDepthTex = depthTex;
        this._sceneDepthRb = null;
        break outerTex;
      }
      lastBadStatus = status;
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, null, 0);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, null, 0);
    }

    if (complete && this._sceneDepthTex !== null) {
      const depthRead = gl.createTexture();
      const blitFbo = gl.createFramebuffer();
      if (depthRead && blitFbo) {
        gl.bindTexture(gl.TEXTURE_2D, depthRead);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.DEPTH_COMPONENT24,
          w,
          h,
          0,
          gl.DEPTH_COMPONENT,
          gl.UNSIGNED_INT,
          null,
        );
        gl.bindFramebuffer(gl.FRAMEBUFFER, blitFbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, depthRead, 0);
        const readSt = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.bindTexture(gl.TEXTURE_2D, null);
        if (readSt === gl.FRAMEBUFFER_COMPLETE) {
          this._sceneDepthTexRead = depthRead;
          this._depthBlitFbo = blitFbo;
        } else {
          gl.deleteTexture(depthRead);
          gl.deleteFramebuffer(blitFbo);
        }
      }
    }

    if (!complete) {
      gl.deleteTexture(depthTex);
      const depth = gl.createRenderbuffer();
      if (!depth) {
        console.warn("[renderer] post-process: failed to create depth RB fallback");
        gl.deleteFramebuffer(fbo);
        gl.deleteTexture(tex);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.bindTexture(gl.TEXTURE_2D, null);
        return;
      }
      const depthModes: { fmt: number; attach: number }[] = [
        { fmt: gl.DEPTH24_STENCIL8, attach: gl.DEPTH_STENCIL_ATTACHMENT },
        { fmt: gl.DEPTH_COMPONENT24, attach: gl.DEPTH_ATTACHMENT },
        { fmt: gl.DEPTH_COMPONENT16, attach: gl.DEPTH_ATTACHMENT },
      ];
      const rbColorFormats: number[] = [gl.RGBA8, gl.RGBA];
      outer:
      for (const colorIfmt of rbColorFormats) {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, colorIfmt, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        for (const dm of depthModes) {
          gl.bindRenderbuffer(gl.RENDERBUFFER, depth);
          gl.renderbufferStorage(gl.RENDERBUFFER, dm.fmt, w, h);
          gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
          gl.framebufferRenderbuffer(gl.FRAMEBUFFER, dm.attach, gl.RENDERBUFFER, depth);
          const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
          if (status === gl.FRAMEBUFFER_COMPLETE) {
            complete = true;
            this._sceneFbo = fbo;
            this._sceneColorTex = tex;
            this._sceneDepthRb = depth;
            this._sceneDepthTex = null;
            break outer;
          }
          lastBadStatus = status;
          gl.framebufferRenderbuffer(gl.FRAMEBUFFER, dm.attach, gl.RENDERBUFFER, null);
          gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, null, 0);
        }
      }
      if (!complete) {
        this._postFboLastFailHex = `0x${lastBadStatus.toString(16)}`;
        console.warn("[renderer] post-process: framebuffer incomplete (last status " + this._postFboLastFailHex + ")");
        gl.deleteFramebuffer(fbo);
        gl.deleteTexture(tex);
        gl.deleteRenderbuffer(depth);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.bindTexture(gl.TEXTURE_2D, null);
        gl.bindRenderbuffer(gl.RENDERBUFFER, null);
        return;
      }
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindRenderbuffer(gl.RENDERBUFFER, null);

    this._sceneFbW = w;
    this._sceneFbH = h;
    this._sceneFboReady = true;
    this._postFboLastFailHex = null;
  }

  /** Включить замеры этапов render() → `getPerfBreakdown()` + HUD при `?debugPerf=1`. */
  setDebugPerf(on: boolean): void {
    this._debugPerf = on;
  }

  getPerfBreakdown(): Readonly<typeof this._perfLastFrame> {
    return this._perfLastFrame;
  }

  /** Дальше этой дистанции (м) POM отключён в voxel.frag; 0 = без отсечения. */
  getPomMaxDistance(): number {
    return this._pomMaxDist;
  }

  setPomMaxDistance(meters: number): void {
    this._pomMaxDist = Math.max(0, meters);
  }

  /** Дальше (м) — только 4 лампы в шейдере вокселей/object; 0 = всегда 8. */
  getLightLodDistance(): number {
    return this._lightLodDist;
  }

  setLightLodDistance(meters: number): void {
    this._lightLodDist = Math.max(0, meters);
  }

  resize(): void {
    if (this._sizeDirty) {
      this._cssW = this.canvas.clientWidth;
      this._cssH = this.canvas.clientHeight;
      this._sizeDirty = false;
    }
    let w: number;
    let h: number;
    if (this._pixelArt) {
      // Пиксель-арт: фиксированная высота backing-store (~360 строк, как у
      // классики), ширина по аспекту. Апскейл до CSS-размера делает браузер
      // с image-rendering: pixelated → крупные чёткие пиксели.
      h = this._pixelArtTargetH;
      const aspect = this._cssH > 0 ? this._cssW / this._cssH : 16 / 9;
      w = Math.max(1, Math.round(h * aspect));
    } else {
      const dpr = Math.min(window.devicePixelRatio || 1, this._maxDevicePixelRatio);
      w = Math.max(1, Math.floor(this._cssW * dpr * this._renderScale));
      h = Math.max(1, Math.floor(this._cssH * dpr * this._renderScale));
    }
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  /** Пиксель-арт режим: низкое внутреннее разрешение + жёсткий апскейл +
   *  постеризация с дизерингом в post.frag (uPosterize). */
  setPixelArtMode(on: boolean, targetHeight = 360): void {
    this._pixelArt = on;
    this._pixelArtTargetH = Math.max(120, Math.min(720, Math.round(targetHeight)));
    this.canvas.style.imageRendering = on ? "pixelated" : "";
    this.markResize();
  }

  getPixelArtMode(): boolean {
    return this._pixelArt;
  }

  /** Осколки тела: wx,wy,wz — мировая точка попадания. */
  spawnDebris(
    playerId: number, wx: number, wy: number, wz: number, vx: number, vy: number, vz: number,
    opts?: { size?: number; color?: [number, number, number]; physicsArmAt?: number; floorZ?: number },
  ): void {
    this.pushDebris({
      x: wx, y: wy, z: wz,
      vx, vy, vz,
      born: this.timeAcc,
      color: opts?.color ?? defaultColor(playerId),
      size: opts?.size ?? BLOCK_SIZE,
      physicsArmAt: opts?.physicsArmAt,
      floorZ: opts?.floorZ,
    });
  }

  /** Спавн осколков от воксельного объекта (мелкие). */
  spawnChunkDebris(
    cx: number, cy: number, cz: number,
    vx: number, vy: number, vz: number,
    color: [number, number, number],
    size: number,
    opts?: { physicsArmAt?: number; floorZ?: number; splat?: boolean },
  ): void {
    this.pushDebris({
      x: cx, y: cy, z: cz, vx, vy, vz,
      born: this.timeAcc, color, size,
      physicsArmAt: opts?.physicsArmAt,
      floorZ: opts?.floorZ,
      splat: opts?.splat,
    });
  }

  /** Кап на живые обломки: при крупных обвалах (shatter 1500-кубовой группы)
   *  массив не должен расти бесконечно — вытесняем самые старые. */
  private pushDebris(d: DebrisBlock): void {
    const MAX_DEBRIS = 2600;
    if (this.debris.length >= MAX_DEBRIS) {
      this.debris.splice(0, this.debris.length - MAX_DEBRIS + 1);
    }
    this.debris.push(d);
  }

  spawnTracer(ox: number, oy: number, oz: number, hx: number, hy: number, hz: number, opts?: TracerOpts): void {
    this.tracers.push({ ox, oy, oz, hx, hy, hz, born: this.timeAcc, opts });
  }

  updateDebris(dt: number): void {
    this.timeAcc += dt;
    const now = this.timeAcc;
    let writeIdx = 0;
    for (let i = 0; i < this.debris.length; i++) {
      const d = this.debris[i];
      const arm = d.physicsArmAt ?? d.born;
      if (now > arm + DEBRIS_LIFETIME) {
        // «Точечный стиль»: обломок не исчезает молча, а РАСТВОРЯЕТСЯ —
        // лопается в облачко пылинок своего цвета (баллистика в шейдере).
        // Капля крови просто гаснет (shrink/fade в инстансе).
        if (!d.splat) {
          this.spawnDustBurst(d.x, d.y, d.z, d.vx * 0.3, d.vy * 0.3, 0.4,
            d.color, d.floorZ ?? 0, 10);
        }
        continue;
      }
      if (now < arm) {
        this.debris[writeIdx++] = d;
        continue;
      }
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      d.z += d.vz * dt;
      d.vz -= GRAVITY * dt;
      const floorZ = d.floorZ ?? 0;
      if (d.z < floorZ) {
        d.z = floorZ;
        const speed = Math.abs(d.vz);
        if (d.splat) {
          // Капля прилипает: ни отскока, ни пыли.
          d.vx = 0;
          d.vy = 0;
          d.vz = 0;
        } else if (speed < 0.5) {
          // Малая скорость — оседаем
          d.vz = 0;
          d.vx *= 0.5;
          d.vy *= 0.5;
        } else {
          // SHATTER ON IMPACT: при сильном ударе об пол кусок "лопается" —
          // отскакивает + получает случайную горизонтальную скорость.
          // Так группа кубов, упавшая как единый блок, при ударе разлетается
          // в стороны (как реальный объект, рассыпающийся на куски).
          d.vz = -d.vz * 0.25;
          // Энергия удара 0..1 (нормировано на ~5 м/с)
          const energy = Math.min(1, speed / 5.0);
          d.vx = d.vx * 0.4 + (Math.random() - 0.5) * 4.0 * energy;
          d.vy = d.vy * 0.4 + (Math.random() - 0.5) * 4.0 * energy;
          if (this.particleSim) {
            this.particleSim.spawnDustPuff(d.x, d.y, d.z, now, energy, this._mobileLiteParticles);
          }
          // Пыльная вспышка при ударе — часть точечного стиля.
          this.spawnDustBurst(d.x, d.y, d.z, d.vx, d.vy, speed * 0.3,
            d.color, floorZ, 6);
        }
      }
      this.debris[writeIdx++] = d;
    }
    this.debris.length = writeIdx;

    let trWrite = 0;
    for (let i = 0; i < this.tracers.length; i++) {
      const t = this.tracers[i];
      if (t.opts) {
        if (this.timeAcc - t.born < t.opts.life) this.tracers[trWrite++] = t;
        continue;
      }
      if (now - t.born > TRACER_LIFETIME) continue;
      this.tracers[trWrite++] = t;
    }
    this.tracers.length = trWrite;
  }

  /** Секунды с старта сцены — как у debris (`born`). */
  getTimeAccSec(): number {
    return this.timeAcc;
  }

  /** Физика оторванных real-voxel кубов (до spawn в том же кадре — вызывать до syncRealVoxelHiddenFlags). */
  updateRealVoxelFlyForAll(dt: number): void {
    const t = this.timeAcc;
    for (const inst of this.meshInstances) {
      if (inst.realVoxelFlying.length > 0) {
        updateRealVoxelFlyPhysics(inst, dt, t);
      }
    }
  }

  render(
    camera: CameraState,
    chunks: VoxelChunkClient[],
    fallingGroups: FallingGroupClient[],
    lights: PointLight[],
    time: number,
    chunkContentEpoch: number,
  ): void {
    const gl = this.gl;
    const dbg = this._debugPerf;
    let perfMark = dbg ? performance.now() : 0;
    this.resize();
    // Сбрасываем кэш canvas rect — запрошен будет лениво в projectToScreen
    // (ТОЛЬКО после render'а, на post-pass этапе, все DOM-writes уже сделаны).
    this._cachedRect = null;
    // Инвалидация кэша подготовленных ламп — eye и список ламп могли
    // измениться между кадрами.
    this._preparedNumLights = -1;

    const fbW = this.canvas.width;
    const fbH = this.canvas.height;
    if (this._postProcessEnabled) {
      this.ensureSceneFramebuffer(fbW, fbH);
    }
    const postPass =
      this._postProcessEnabled &&
      this._sceneFboReady &&
      this._sceneFbo !== null &&
      this._sceneColorTex !== null;
    this._postProcessAppliedLastFrame = false;
    if (postPass) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, this._sceneFbo);
      gl.viewport(0, 0, fbW, fbH);
    }

    // Мировая позиция глаза (x,y,eyeZ+bob) — нужна ДО pass 1: raycast-tуман
    // (uCameraPos), затем viewProj для вокселей/меша. Без этого eye остаётся
    // (0,0,0) и весь 3D-pass смотрит «из начала координат» — сцена/персонаж
    // пропадают или съезжают.
    const ez = camera.eyeZ ?? EYE_HEIGHT;
    const bz = camera.bobZ ?? 0;
    this.eye[0] = camera.x;
    this.eye[1] = camera.y;
    this.eye[2] = ez + bz;

    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.disable(gl.BLEND);

    // Pass 1 — raycaster (он же чистит экран и пишет depth = 0/1 в буфер цвета,
    // но реальный depth не используется).
    gl.useProgram(this.rcProg);
    gl.bindVertexArray(this.quadVAO);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.mapTex);
    gl.uniform1i(this.uRC.map, 0);
    gl.uniform2i(this.uRC.mapSize, this.mapSize[0], this.mapSize[1]);
    gl.uniform2f(this.uRC.playerPos, camera.x, camera.y);
    gl.uniform1f(this.uRC.yaw, camera.yaw);
    // Учитываем небольшое смещение по Z (view bobbing) как доп. наклон,
    // чтобы горизонт sky/потолка качался синхронно с камерой.
    gl.uniform1f(this.uRC.pitch, camera.pitch);
    gl.uniform1f(this.uRC.fovY, this.effectiveFovY());
    gl.uniform1f(this.uRC.aspect, this.canvas.width / this.canvas.height);
    gl.uniform1f(this.uRC.time, time);
    gl.uniform1f(this.uRC.eyeHeight, EYE_HEIGHT / WORLD_HEIGHT);
    gl.uniform1f(this.uRC.ceilHeight, WORLD_HEIGHT);
    gl.uniform1f(this.uRC.near, 0.05);
    gl.uniform1f(this.uRC.far, FAR_PLANE);
    if (this.uRC.cameraPos) {
      gl.uniform3f(this.uRC.cameraPos, this.eye[0], this.eye[1], this.eye[2]);
    }
    this.bindFogUniforms(gl, {
      color: this.uRC.fogColor,
      density: this.uRC.fogDensity,
      height: this.uRC.fogHeight,
      falloff: this.uRC.fogHeightFalloff,
    });
    if (this._skyVideo && this._skyTex &&
        this._skyVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this._skyTex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this._skyVideo);
    }
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this._skyTex ?? this.fallbackTex);
    if (this.uRC.sky !== null) {
      gl.uniform1i(this.uRC.sky, 1);
    }
    if (this.uRC.skyEnabled !== null) {
      gl.uniform1f(this.uRC.skyEnabled, this._skyTex !== null && this._skyGpuReady ? 1 : 0);
    }
    if (this.uRC.skyEquirect !== null) {
      gl.uniform1f(this.uRC.skyEquirect, this._skyEquirect ? 1 : 0);
    }
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.mapTex);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    if (dbg) {
      this._perfLastFrame.raycastMs = performance.now() - perfMark;
      perfMark = performance.now();
    }

    // Pass 2 — кубики поверх. Включаем depth, но он работает только между кубиками.
    // Стены raycaster'а не пишут depth, поэтому кубики могут оказаться "внутри" стен —
    // это допустимо для MVP (игроки не могут зайти в стены).
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LESS);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.clear(gl.DEPTH_BUFFER_BIT);

    // view / viewProj: this.eye выставлен в начале render() из camera.
    mat4Perspective(this.proj, this.effectiveFovY(), this.canvas.width / this.canvas.height, 0.05, FAR_PLANE);
    const cy = Math.cos(camera.yaw), sy = Math.sin(camera.yaw);
    const cp = Math.cos(camera.pitch), sp = Math.sin(camera.pitch);
    this.target[0] = camera.x + cy * cp;
    this.target[1] = camera.y + sy * cp;
    this.target[2] = this.eye[2] + sp;
    // Head sway (как в Doom 2): наклоняем up-вектор вокруг forward-оси.
    // up_world = (0, 0, 1) поворачиваем вокруг forward на угол bobRoll.
    // С учётом Rodrigues: new_up = up*cos + (forward × up)*sin.
    // (forward × up_z) = (sy*cp, -cy*cp, 0), нормированный (имеет длину cp ≈ 1).
    const roll = camera.bobRoll ?? 0;
    if (roll !== 0) {
      const cr = Math.cos(roll);
      const sr = Math.sin(roll);
      this.up[0] = sy * cp * sr;
      this.up[1] = -cy * cp * sr;
      this.up[2] = cr;
    } else {
      this.up[0] = 0; this.up[1] = 0; this.up[2] = 1;
    }
    mat4LookAt(this.view, this.eye, this.target, this.up);
    mat4Multiply(this.viewProj, this.proj, this.view);
    // Пересчитать frustum-плоскости для этого кадра.
    this.extractFrustumPlanes();
    this.lastCulledCount = 0;
    this.lastDrawnChunks = 0;
    this.lastLpTilesDrawn = 0;
    this.lastLpTilesCulled = 0;
    this.lastLpCubesDrawn = 0;

    // Заполнение инстансов. Персонажи — skinned mesh (renderMeshes). Разрушение
    // персонажей — только debris (spawnDebris / spawnChunkDebris) по Events.
    let n = 0;
    const data = this.voxInstanceData;

    // ВАЖНО: малочисленные эффекты (debris/tracers/lights/falling groups)
    // рендерим ДО chunks. Если упрёмся в MAX_INSTANCES, дропнутся chunks
    // (их 200K+, потеря пары далеко от камеры — не заметно), а debris
    // и тд останутся видны.

    // Падающие rigid-body группы.
    const nowSec = this.timeAcc;
    for (const g of fallingGroups) {
      const dt = Math.max(0, nowSec - g.lastUpdateTime);
      const px = g.pos[0] + g.vel[0] * dt;
      const py = g.pos[1] + g.vel[1] * dt;
      const pz = g.pos[2] + g.vel[2] * dt;
      const ang = g.angle + g.angVel * dt;
      const cs = g.cubeSize;
      const cubes = g.cubes;
      const ax = g.axis[0], ay = g.axis[1], az = g.axis[2];
      const piv = g.pivot;
      for (let ci = 0; ci < cubes.length; ci++) {
        if (n >= MAX_INSTANCES) break;
        const c = cubes[ci];
        const r = rotateAxisAngle(c.rx, c.ry, c.rz, ax, ay, az, ang);
        const idx = n * INSTANCE_FLOATS;
        data[idx]     = piv[0] + px + r[0];
        data[idx + 1] = piv[1] + py + r[1];
        data[idx + 2] = piv[2] + pz + r[2];
        data[idx + 3] = cs;
        data[idx + 4] = c.color[0];
        data[idx + 5] = c.color[1];
        data[idx + 6] = c.color[2];
        data[idx + 7] = 1.0; data[idx + 8] = data[idx + 9] = data[idx + 10] = data[idx + 11] = data[idx + 12] = data[idx + 13] = data[idx + 14] = data[idx + 15] = 0; // uniform color
        n++;
      }
    }

    // Debris (всегда uniform color → vTexMode=1; "fade out" имитируем
    // затемнением цвета, т.к. alpha-канал теперь несёт texMode).
    for (const d of this.debris) {
      if (n >= MAX_INSTANCES) break;
      const t0 = d.physicsArmAt ?? d.born;
      const age = Math.max(0, (this.timeAcc - t0) / DEBRIS_LIFETIME);
      const fade = Math.max(0, 1 - age);
      // «Крошение»: кубик тает — усыхает к концу жизни (песок уносит масса
      // песчинок из spawnDustBurst, а сам обломок съёживается).
      const shrink = 1 - 0.75 * age * age;
      const idx = n * INSTANCE_FLOATS;
      data[idx]     = d.x;
      data[idx + 1] = d.y;
      data[idx + 2] = d.z;
      data[idx + 3] = d.size * shrink;
      data[idx + 4] = d.color[0] * fade;
      data[idx + 5] = d.color[1] * fade;
      data[idx + 6] = d.color[2] * fade;
      data[idx + 7] = UNIFORM_TEX_MODE;
      data[idx + 8] = d.vx;
      data[idx + 9] = d.vy;
      data[idx + 10] = d.vz;
      data[idx + 11] = 1.0;
      data[idx + 12] = 0;
      data[idx + 13] = 0;
      data[idx + 14] = 0;
      data[idx + 15] = t0;
      n++;
    }

    // Лампочки — маленькие яркие кубики (uniform color = их цвет освещения),
    // visible как "лампа на потолке". Не затрагивают depth-test их свечения,
    // т.к. сам свет от лампы считается per-fragment в шейдере.
    for (const lt of lights) {
      if (n >= MAX_INSTANCES) break;
      if (lt.noBulb) continue; // weapons: вспышка/ореол — только свет
      const idx = n * INSTANCE_FLOATS;
      data[idx]     = lt.x;
      data[idx + 1] = lt.y;
      data[idx + 2] = lt.z;
      data[idx + 3] = 0.18; // ~18 см "плафон"
      // Яркая версия цвета лампы (clamp for sRGB-ish look)
      data[idx + 4] = Math.min(1, lt.color[0]);
      data[idx + 5] = Math.min(1, lt.color[1]);
      data[idx + 6] = Math.min(1, lt.color[2]);
      data[idx + 7] = 1.0; data[idx + 8] = data[idx + 9] = data[idx + 10] = data[idx + 11] = data[idx + 12] = data[idx + 13] = data[idx + 14] = data[idx + 15] = 0; // uniform color, no UV
      n++;
    }

    // Tracers — тонкие 1.5см "искорки" вдоль луча. Не рисуем ближе чем за
    // ~1м от стартовой точки, чтобы пуля не появлялась прямо перед глазами.
    for (const t of this.tracers) {
      if (t.opts) {
        // weapons: луч/дробь — свой цвет, толщина, жизнь и плотность.
        n = this.fillWeaponTracer(t, t.opts, data, n);
        continue;
      }
      const age = (this.timeAcc - t.born) / TRACER_LIFETIME;
      if (age >= 1) continue;
      const fade = (1 - age) * 0.9;
      const dx = t.hx - t.ox, dy = t.hy - t.oy, dz = t.hz - t.oz;
      const len = Math.hypot(dx, dy, dz);
      if (len < 1.2) continue;
      const startF = Math.min(0.5, 1.0 / len);   // пропустить ~1м у дула
      const usableLen = (1 - startF) * len;
      const segs = Math.min(16, Math.max(3, Math.floor(usableLen * 2.5)));
      for (let s = 1; s <= segs; s++) {
        if (n >= MAX_INSTANCES) break;
        const f = startF + ((1 - startF) * s) / (segs + 1);
        const idx = n * INSTANCE_FLOATS;
        data[idx]     = t.ox + dx * f;
        data[idx + 1] = t.oy + dy * f;
        data[idx + 2] = t.oz + dz * f;
        data[idx + 3] = 0.015;
        data[idx + 4] = 1.0 * fade;
        data[idx + 5] = 0.85 * fade;
        data[idx + 6] = 0.3 * fade;
        data[idx + 7] = 1.0;
        data[idx + 8] = data[idx + 9] = data[idx + 10] = data[idx + 11] = 0;
        data[idx + 12] = data[idx + 13] = data[idx + 14] =         data[idx + 15] = 0;
        n++;
      }
    }

    let anyLpStatic = false;
    for (let di = 0; di < chunks.length; di++) {
      const ch = chunks[di];
      if (!ch.dynamic && ch.faceUVs === null && isLevelpackDiffuseTex(ch.textureName)) {
        anyLpStatic = true;
        break;
      }
    }

    const nBeforeStaticChunks = n;
    let skipStaticFill = false;
    if (
      this._staticChunkCacheValid &&
      !anyLpStatic &&
      chunkContentEpoch === this._cacheChunkEpoch &&
      this.camApproxEquals(camera) &&
      nBeforeStaticChunks === this._cachePrefixInstanceCount
    ) {
      const nf = this._staticChunkCacheFloatCount;
      data.set(this._staticChunkCacheBytes.subarray(0, nf), nBeforeStaticChunks * INSTANCE_FLOATS);
      n = nBeforeStaticChunks + nf / INSTANCE_FLOATS;
      skipStaticFill = true;
    }

    if (dbg) {
      this._perfLastFrame.fillPrefixMs = performance.now() - perfMark;
      perfMark = performance.now();
    }

    // Воксельные chunks — рендерим ПОСЛЕДНИМИ т.к. их 200K+, и если упрёмся
    // в MAX_INSTANCES, лучше пропустить часть стен (далеко от камеры) чем
    // потерять видимые эффекты. iColor.a:
    //   0.0=brick, 0.25=object texture (по UV), 0.5=concrete, 0.75=plaster, 1.0=uniform
    if (!skipStaticFill) {
    for (const c of chunks) {
      // Object chunks с per-face corner UVs рендерятся отдельным pass'ом
      // (renderObjectChunks ниже) — им нужен свой шейдер с uv-lookup.
      if (c.faceUVs !== null) continue;
      // Dynamic chunks (мебель с pose) идут в отдельный per-chunk pass ниже,
      // чтобы передать quat в шейдер и куб вращался геометрически.
      if (c.dynamic) continue;
      // Frustum culling: если AABB чанка полностью вне камеры — skip.
      // Огромная экономия при маленьких чанках (типичная арена = 53 чанка,
      // в поле зрения за раз 10-20).
      if (!this.chunkInFrustum(c)) {
        this.lastCulledCount++;
        continue;
      }
      // hideStaticVoxels скрывает только обычные wall/floor (их визуал даёт
      // static_meshes.glb). Чанки с voxVisual=true (из `vox_*` нод) ВСЕГДА
      // рендерим — они вырезаны из визуального GLB и единственный их
      // визуал — сами воксели, который и должен дырявиться при выстреле.
      if (this.hideStaticVoxels && !c.voxVisual) continue;
      if (isLevelpackDiffuseTex(c.textureName)) continue;
      this.lastDrawnChunks++;

      const sxsy = c.sx * c.sy;
      const list = c.visibleList;
      const cs = c.cubeSize;
      const ox = c.ox, oy = c.oy, oz = c.oz;
      const cr = c.color[0], cg = c.color[1], cb = c.color[2];
      // Tile-материал (9-слотовая palette) имеет ПРИОРИТЕТ над legacy
      // object-triplanar путём. Иначе chunks с textureName='wood' и
      // непустым perCubeUV (всё ещё пишется voxelize'ом для debris colors)
      // ошибочно уходят в legacy 3-проекционный atlas → uObjectTex (gray).
      // Схема: texMode = matKind * 0.1 (см. voxel.frag / materialTexMode()).
      // Object triplanar = 0.95, uniform color = 1.0 — оба вне matKind range.
      let texMode = materialTexMode(c.textureName);
      let isObjectTriplanar = false;
      if (texMode === UNIFORM_TEX_MODE && c.perCubeUV !== null) {
        // Legacy: уникально-текстурированный объект без tile-материала.
        // Сейчас не используется (мебель = wood, персонажи = skinned mesh),
        // оставлено на случай если потом захотим вернуть corner-UV пайплайн.
        texMode = OBJECT_TRIPLANAR_TEX_MODE;
        isObjectTriplanar = true;
      }

      // Bbox local: для triplanar нужно нормализовать localPos к [0..1]
      const bboxMinX = c.bboxMin[0], bboxMinY = c.bboxMin[1], bboxMinZ = c.bboxMin[2];
      const bboxSizeX = c.bboxSize[0] || 1, bboxSizeY = c.bboxSize[1] || 1, bboxSizeZ = c.bboxSize[2] || 1;
      // Mesh bbox центрирован в чанке (см. voxelize/object_loader).
      // localPos куба = (bx+0.5)*cs etc — это chunk-local, но bboxMin/Size
      // тоже в chunk-local frame, mesh центрирован в (sx*cs/2, sy*cs/2, sz*cs/2).
      const meshCenterLocalX = c.sx * cs * 0.5;
      const meshCenterLocalY = c.sy * cs * 0.5;
      const meshCenterLocalZ = c.sz * cs * 0.5;
      // Сдвиг между bbox центром (mesh центрирован в (0,0,0)) и chunk-local-center
      const bboxLocalMinX = meshCenterLocalX + bboxMinX;
      const bboxLocalMinY = meshCenterLocalY + bboxMinY;
      const bboxLocalMinZ = meshCenterLocalZ + bboxMinZ;

      // Подготовка для dynamic chunks: эффективная поза с extrapolation
      const dyn = c.dynamic;
      const pivX = ox + meshCenterLocalX;
      const pivY = oy + meshCenterLocalY;
      const pivZ = oz;
      let posX = 0, posY = 0, posZ = 0, angle = 0;
      let axX = 0, axY = 0, axZ = 1;
      if (dyn && !c.pose.resting) {
        const dt = Math.max(0, nowSec - c.pose.lastUpdateTime);
        posX = c.pose.pos[0] + c.pose.vel[0] * dt;
        posY = c.pose.pos[1] + c.pose.vel[1] * dt;
        posZ = c.pose.pos[2] + c.pose.vel[2] * dt;
        const q = quatIntegrate(c.pose.quat, c.pose.angVelVec, dt);
        const aa = quatToAxisAngle(q);
        angle = aa.angle;
        axX = aa.axis[0]; axY = aa.axis[1]; axZ = aa.axis[2];
      } else if (dyn) {
        posX = c.pose.pos[0]; posY = c.pose.pos[1]; posZ = c.pose.pos[2];
        const aa = quatToAxisAngle(c.pose.quat);
        angle = aa.angle;
        axX = aa.axis[0]; axY = aa.axis[1]; axZ = aa.axis[2];
      }

      for (let li = 0; li < list.length; li++) {
        if (n >= MAX_INSTANCES) break;
        const i = list[li];
        const bx = i % c.sx;
        const by = Math.floor(i / c.sx) % c.sy;
        const bz = Math.floor(i / sxsy);
        const localCx = (bx + 0.5) * cs;
        const localCy = (by + 0.5) * cs;
        const localCz = (bz + 0.5) * cs;
        const idx = n * INSTANCE_FLOATS;
        if (dyn && angle !== 0) {
          const lx = localCx - meshCenterLocalX;
          const ly = localCy - meshCenterLocalY;
          const lz = localCz;
          const r = rotateAxisAngle(lx, ly, lz, axX, axY, axZ, angle);
          data[idx]     = pivX + r[0] + posX;
          data[idx + 1] = pivY + r[1] + posY;
          data[idx + 2] = pivZ + r[2] + posZ;
        } else {
          data[idx]     = ox + localCx + posX;
          data[idx + 1] = oy + localCy + posY;
          data[idx + 2] = oz + localCz + posZ;
        }
        data[idx + 3] = cs;
        if (isObjectTriplanar) {
          // iColor.rgb = bboxSize (для нормализации в шейдере)
          data[idx + 4] = bboxSizeX;
          data[idx + 5] = bboxSizeY;
          data[idx + 6] = bboxSizeZ;
          data[idx + 7] = texMode;
          // iObjectUV.xyz = bboxMin in chunk-local frame
          data[idx + 8]  = bboxLocalMinX;
          data[idx + 9]  = bboxLocalMinY;
          data[idx + 10] = bboxLocalMinZ;
          data[idx + 11] = 0;
          // iLocalPos.xyz = cube center в chunk-local frame
          data[idx + 12] = localCx;
          data[idx + 13] = localCy;
          data[idx + 14] = localCz;
          data[idx + 15] = 0;
        } else {
          data[idx + 4] = cr;
          data[idx + 5] = cg;
          data[idx + 6] = cb;
          data[idx + 7] = texMode;
          // Остальные слоты нули — для triplanar/uniform путей не используются
          data[idx + 8] = 0;
          data[idx + 9] = 0;
          data[idx + 10] = 0;
          data[idx + 11] = 0;
          data[idx + 12] = 0;
          data[idx + 13] = 0;
          data[idx + 14] = 0;
          data[idx + 15] = 0;
        }
        n++;
      }
    }

    if (!skipStaticFill && n < MAX_INSTANCES) {
      const chunkFloatLen = (n - nBeforeStaticChunks) * INSTANCE_FLOATS;
      if (chunkFloatLen > this._staticChunkCacheBytes.length) {
        this._staticChunkCacheBytes = new Float32Array(chunkFloatLen + 4096);
      }
      if (chunkFloatLen > 0) {
        const start = nBeforeStaticChunks * INSTANCE_FLOATS;
        this._staticChunkCacheBytes.set(data.subarray(start, start + chunkFloatLen));
      }
      this._staticChunkCacheFloatCount = chunkFloatLen;
      this._cachePrefixInstanceCount = nBeforeStaticChunks;
      this._cacheChunkEpoch = chunkContentEpoch;
      this._staticChunkCacheValid = true;
      this.saveCamCache(camera);
    } else if (!skipStaticFill && n >= MAX_INSTANCES) {
      this._staticChunkCacheValid = false;
    }
    }

    if (dbg) {
      this._perfLastFrame.staticChunksMs = performance.now() - perfMark;
      perfMark = performance.now();
    }

    let anyDynamicVox = false;
    for (let di = 0; di < chunks.length; di++) {
      const ch = chunks[di];
      if (ch.dynamic && ch.faceUVs === null) {
        anyDynamicVox = true;
        break;
      }
    }

    if (n > 0 || anyDynamicVox || anyLpStatic) {
      gl.useProgram(this.voxProg);
      gl.bindVertexArray(this.voxVAO);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.voxInstanceBuf);
      if (n > 0) {
        if (dbg) {
          this._perfLastFrame.bufferUploadMs = 0;
          const t0 = performance.now();
          gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, n * INSTANCE_FLOATS);
          this._perfLastFrame.bufferUploadMs = performance.now() - t0;
        } else {
          gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, n * INSTANCE_FLOATS);
        }
      } else if (dbg) {
        this._perfLastFrame.bufferUploadMs = 0;
      }
      gl.uniformMatrix4fv(this.uVox.viewProj, false, this.viewProj);
      gl.uniform1f(this.uVox.tileSize, 1.0);  // 1 wrap = 1m (модулируется matScale в шейдере)
      gl.uniform1f(this.uVox.vTileScale, 1.0); // 1024×1024 квадратные — V совпадает с U
      gl.uniform3f(this.uVox.cameraPos, this.eye[0], this.eye[1], this.eye[2]);
      if (this.uVox.voxelBevel) {
        // Статичная архитектура (легаси-стены/полы/потолки из кубов 6 см,
        // levelpack-статик) рисуется БЕЗ фаски: сферическое скругление
        // нормали на каждом кубике читается как «сетка квадратиков» на
        // плоской кладке. Скругление остаётся только у dynamic chunks —
        // см. renderDynamicChunks, где uniform поднимается обратно.
        gl.uniform1f(this.uVox.voxelBevel, 0);
      }
      if (this.uVox.voxelBevelRadius) {
        gl.uniform1f(this.uVox.voxelBevelRadius, this._voxelBevelRadius);
      }
      if (this.uVox.sceneTime) {
        gl.uniform1f(this.uVox.sceneTime, this.timeAcc);
      }
      this.bindFogUniforms(gl, {
        color: this.uVox.fogColor,
        density: this.uVox.fogDensity,
        height: this.uVox.fogHeight,
        falloff: this.uVox.fogHeightFalloff,
      exposure: this.uVox.exposure,
      });
      gl.uniform1f(this.uVox.pomScale, 0.025);
      if (this.uVox.pomLayerCount) {
        gl.uniform1i(this.uVox.pomLayerCount, this._pomLayers);
      }
      if (this.uVox.pomMaxDist) {
        gl.uniform1f(this.uVox.pomMaxDist, this._pomMaxDist);
      }
      if (this.uVox.lightLodDist) {
        gl.uniform1f(this.uVox.lightLodDist, this._lightLodDist);
      }
      // 8 ближайших ламп (остальные слоты range=0 — шейдер отсекает).
      const numL = this.prepareLightsClosestToCamera(lights);
      gl.uniform1i(this.uVox.numLights, numL);
      gl.uniform4fv(this.uVox.lights, this._lpos);
      gl.uniform3fv(this.uVox.lightColors, this._lcol);
      // Slot 0 — palette diffuse atlas (brick/concrete/plaster/wood/grass/
      // dirt/stone/metal/glass по X), slot 1 — depth atlas, slot 2 — legacy
      // object triplanar. Все ОБЯЗАТЕЛЬНО привязываются к float-совместимой
      // текстуре, иначе остаётся mapTex (R8UI integer) от raycast pass и
      // draw валится GL_INVALID_OPERATION.
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.paletteTex ?? this.fallbackTex);
      gl.uniform1i(this.uVox.palette, 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this.paletteDepthTex ?? this.fallbackTex);
      gl.uniform1i(this.uVox.paletteDepth, 1);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, this.fallbackTex);
      gl.uniform1i(this.uVox.objectTex, 2);
      gl.disable(gl.BLEND);
      // Identity quat — static chunks/debris/lights/tracers не вращаются.
      gl.uniform4f(this.uVox.chunkQuat, 1, 0, 0, 0);
      if (n > 0) {
        if (dbg) {
          const tDraw = performance.now();
          gl.drawArraysInstanced(gl.TRIANGLES, 0, 36, n);
          this._perfLastFrame.voxelDrawMs = performance.now() - tDraw;
        } else {
          gl.drawArraysInstanced(gl.TRIANGLES, 0, 36, n);
        }
      } else if (dbg) {
        this._perfLastFrame.voxelDrawMs = 0;
      }
      if (anyLpStatic) {
        this.renderLevelpackVoxelBatches(gl, chunks);
      }
    } else if (dbg) {
      this._perfLastFrame.bufferUploadMs = 0;
      this._perfLastFrame.voxelDrawMs = 0;
    }

    // Dynamic chunks: per-chunk drawcall, чтобы передать quat шейдеру.
    // Шейдер сам поворачивает aPos/aNormal — кубы в стуле геометрически
    // следуют за rigid body, а не остаются axis-aligned ёлочкой.
    if (dbg) perfMark = performance.now();
    this.renderDynamicChunks(chunks, nowSec);
    if (dbg) {
      this._perfLastFrame.dynamicChunksMs = performance.now() - perfMark;
      perfMark = performance.now();
    }

    // ─── Pass 3: object chunks (interior items с corner UV grid) ───────
    // Каждый chunk рендерится отдельно своим texture (один draw call на chunk).
    this.renderObjectChunks(chunks, lights, nowSec, chunkContentEpoch);
    if (dbg) {
      this._perfLastFrame.objectChunksMs = performance.now() - perfMark;
      perfMark = performance.now();
    }

    // ─── Pass 4: mesh-objects (NPC, items с готовых GLB) ─────────────
    this.renderMeshes(lights, nowSec);
    if (dbg) {
      this._perfLastFrame.meshesMs = performance.now() - perfMark;
    }

    // ─── Pass 4b: «точечный стиль» — после ВСЕЙ опаковой геометрии,
    // чтобы depth-test корректно прятал пылинки за стенами/мешами.
    this.renderSurfaceDust(chunks, chunkContentEpoch);
    this.renderDustBursts();

    // story: билборды сюжетного режима (прозрачные, depth-test без записи).
    if (this.storyPass) {
      const numL = this.prepareLightsClosestToCamera(lights);
      this.storyPass({
        gl,
        viewProj: this.viewProj,
        eye: this.eye,
        fogColor: this._fogColor,
        fogDensity: this._fogDensity,
        fogHeight: this._fogHeight,
        fogHeightFalloff: this._fogHeightFalloff,
        exposure: this._exposure,
        numLights: numL,
        lightPos: this._lpos,
        lightCol: this._lcol,
        time: this.timeAcc,
      });
      gl.depthMask(true);
      gl.disable(gl.BLEND);
      gl.enable(gl.CULL_FACE);
      gl.bindVertexArray(null);
    }

    if (this.particleSim) {
      let particleDepthTex: WebGLTexture | null = null;
      let particleSoft = false;
      if (
        postPass &&
        this._sceneDepthTex !== null &&
        this._sceneDepthTexRead !== null &&
        this._depthBlitFbo !== null
      ) {
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this._sceneFbo);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this._depthBlitFbo);
        gl.blitFramebuffer(0, 0, fbW, fbH, 0, 0, fbW, fbH, gl.DEPTH_BUFFER_BIT, gl.NEAREST);
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this._sceneFbo);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this._sceneFbo);
        particleDepthTex = this._sceneDepthTexRead;
        particleSoft = true;
      }
      // Время симуляции частиц = timeAcc (как у debris/spawn), не аргумент `time`
      // из main (now/1000) — иначе age отрицательный и alphaOverLife для дыма даёт 0.
      this.particleRenderer.draw({
        viewProj: this.viewProj,
        view: this.view,
        time: this.timeAcc,
        viewportW: fbW,
        viewportH: fbH,
        sim: this.particleSim,
        eyeX: this.eye[0],
        eyeY: this.eye[1],
        eyeZ: this.eye[2],
        camForwardX: this.target[0] - this.eye[0],
        camForwardY: this.target[1] - this.eye[1],
        camForwardZ: this.target[2] - this.eye[2],
        depthTex: particleDepthTex,
        softParticles: particleSoft,
        near: 0.05,
        far: FAR_PLANE,
      });
    }

    // ─── weapons: вид от первого лица — оружие и руки поверх мира ─────
    // Свой проход: depth очищен (ствол не уходит в стену), ближняя
    // плоскость 1 см. FOV — как у мира, чтобы дуло на экране совпадало с
    // мировыми эффектами (вспышка, пламя, луч).
    if (this.viewModelInstances.length > 0) {
      this.renderViewModel(lights, nowSec);
    }

    if (postPass) {
      if (dbg) perfMark = performance.now();
      this.runBloomPass(fbW, fbH);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, fbW, fbH);
      gl.disable(gl.SCISSOR_TEST);
      gl.colorMask(true, true, true, true);
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.CULL_FACE);
      gl.disable(gl.BLEND);
      gl.useProgram(this.postProg);
      gl.bindVertexArray(this.quadVAO);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this._sceneColorTex);
      if (this.uPost.scene) gl.uniform1i(this.uPost.scene, 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this._bloomHalfTexA ?? this.fallbackTex);
      if (this.uPost.bloom) gl.uniform1i(this.uPost.bloom, 1);
      // 0.42 → 0.58: неоновое свечение ярких участков («нейро-арт» вайб).
      const bloomMix =
        this._luxEffectsEnabled && this._bloomUiEnabled && this._bloomHalfTexA ? 0.58 : 0;
      if (this.uPost.bloomMix) gl.uniform1f(this.uPost.bloomMix, bloomMix);
      let heatStr = 0;
      let huvx = 0.5;
      let huvy = 0.5;
      if (this._luxEffectsEnabled && this.particleSim) {
        const hs = this.particleSim.getHeatSources(this.timeAcc);
        if (hs.length > 0) {
          const uv = this.projectWorldToSceneUv(hs[0].x, hs[0].y, hs[0].z);
          if (uv) {
            huvx = uv[0];
            huvy = uv[1];
            heatStr = 1.0;
          }
        }
      }
      if (this.uPost.heatStrength) gl.uniform1f(this.uPost.heatStrength, heatStr);
      if (this.uPost.heatUv) gl.uniform2f(this.uPost.heatUv, huvx, huvy);
      // Пиксель-арт: 12 уровней на канал, без дизеринга; постеризация ДО
      // виньетки (post.frag), иначе кольца на небе.
      if (this.uPost.posterize) gl.uniform1f(this.uPost.posterize, this._pixelArt ? 12.0 : 0.0);
      if (this.uPost.invRes) gl.uniform2f(this.uPost.invRes, 1 / fbW, 1 / fbH);
      if (this.uPost.time) gl.uniform1f(this.uPost.time, time);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, null);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, null);
      this._postProcessAppliedLastFrame = true;
      if (dbg) {
        this._perfLastFrame.postMs = performance.now() - perfMark;
      }
    } else if (dbg) {
      this._perfLastFrame.postMs = 0;
    }
  }

  /** Per-chunk кэш уже подготовленных instance-данных для levelpack-pass.
   *  Сборка одного куба — это 16 float-заполнений + три деления/модуло, что
   *  на 2 млн кубов в кадр (vox_wall_garden) даёт 30+ ms JS-времени. После
   *  первого построения чанка данные не меняются, пока visibleList не
   *  изменится (break/upsert) — отслеживаем через VoxelChunkClient.visibleEpoch. */
  private _lpInstanceCache: Map<number, { epoch: number; tiles: LpTile[] }> = new Map();
  /** Счётчики последнего кадра для levelpack-статика (HUD/диагностика). */
  public lastLpTilesDrawn = 0;
  public lastLpTilesCulled = 0;
  public lastLpCubesDrawn = 0;

  /** Статические чанки с `texture_name` вида levelpacks/... — отдельный instanced draw на текстуру.
   *  Когда `hideStaticVoxels` — рисуем только voxVisual чанки (см. главный chunk-pass).
   *
   *  Геометрия статична, поэтому каждый чанк один раз режется на
   *  пространственные тайлы (~LP_TILE_METERS) со своим GPU-буфером — см.
   *  buildLevelpackChunkTiles. За кадр остаётся только frustum-тест AABB
   *  тайла и bind+draw: ни staging-копии, ни bufferSubData. Ограничение
   *  MAX_INSTANCES на батч тоже отпадает — каждый тайл рисуется сам по себе. */
  private renderLevelpackVoxelBatches(gl: WebGL2RenderingContext, chunks: VoxelChunkClient[]): void {
    const texMode = LEVELPACK_SINGLE_TRIPLANAR_TEX_MODE;
    const doneTex = new Set<string>();

    const passes = chunks.filter(
      (c) => !c.dynamic && c.faceUVs === null && (!this.hideStaticVoxels || c.voxVisual),
    );
    if (passes.length === 0) return;

    for (const c0 of passes) {
      if (!isLevelpackDiffuseTex(c0.textureName)) continue;
      if (doneTex.has(c0.textureName)) continue;
      doneTex.add(c0.textureName);

      const glTex = this.objectTextures.get(c0.textureName);
      if (!glTex) continue;

      // Set up texture/uniforms ONCE per material — flush'ы внутри батча
      // не меняют ни текстуру, ни uChunkQuat.
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, glTex);
      gl.uniform1i(this.uVox.objectTex, 2);
      // Normal map (если у material есть) — TEXTURE3. uObjectHasNormal
      // переключает шейдер на perturbed normal vs original.
      const normalName = c0.normalTexture;
      const normalTex = normalName ? this.objectNormalTextures.get(normalName) : undefined;
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, normalTex ?? this.fallbackTex);
      gl.uniform1i(this.uVox.objectNormalTex, 3);
      gl.uniform1i(this.uVox.objectHasNormal, normalTex ? 1 : 0);
      gl.uniform1f(this.uVox.objectNormalScale, c0.normalScale || 1.0);
      // Alpha-cutout / blend режим. На voxel'ах сейчас оба режима трактуем
      // как cutout (binary discard) — true alpha-blend требует depth-sorting,
      // оставлено на v2.
      gl.uniform1i(this.uVox.objectAlphaMode, c0.alphaMode);
      gl.uniform1f(this.uVox.objectAlphaCutoff, c0.alphaCutoff > 0 ? c0.alphaCutoff : 0.5);
      gl.uniform4f(this.uVox.chunkQuat, 1, 0, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.voxInstanceBuf);

      for (const c of passes) {
        if (c.textureName !== c0.textureName) continue;
        if (!this.chunkInFrustum(c)) continue;

        // Достаём тайлы чанка или строим заново при изменении visibleList
        // (разрушение стены → новый visibleEpoch → пересборка GPU-буферов).
        let cache = this._lpInstanceCache.get(c.id);
        if (!cache || cache.epoch !== c.visibleEpoch) {
          if (cache) this.disposeLpTiles(cache.tiles);
          cache = this.buildLevelpackChunkTiles(c, texMode);
          this._lpInstanceCache.set(c.id, cache);
        }

        // Каждый тайл — свой статический GPU-буфер: bind + draw, без единого
        // байта upload'а за кадр. Frustum-тест на тайл (~8 м) вместо чанка
        // (весь особняк) отсекает всё, что вне экрана.
        for (const t of cache.tiles) {
          if (t.cubes <= 0) continue;
          if (!this.aabbInFrustum(t.minX, t.minY, t.minZ, t.maxX, t.maxY, t.maxZ)) {
            this.lastLpTilesCulled++;
            continue;
          }
          gl.bindVertexArray(t.vao);
          gl.drawArraysInstanced(gl.TRIANGLES, 0, 36, t.cubes);
          this.lastLpTilesDrawn++;
          this.lastLpCubesDrawn += t.cubes;
        }
      }
      // Возвращаем общий VAO — следующие пассы (debris/dynamic) ждут его.
      gl.bindVertexArray(this.voxVAO);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.voxInstanceBuf);
    }

    // Опциональная сборка мусора кэша: если чанк удалён из registry (clear/respawn),
    // его id больше не появится в passes; cache-entry удалим лениво при росте Map'ы.
    if (this._lpInstanceCache.size > passes.length * 2 + 16) {
      const live = new Set<number>();
      for (const c of passes) live.add(c.id);
      for (const [id, entry] of this._lpInstanceCache) {
        if (!live.has(id)) {
          this.disposeLpTiles(entry.tiles);
          this._lpInstanceCache.delete(id);
        }
      }
    }
  }

  /** Освободить GPU-ресурсы тайлов (пересборка после разрушения / смена уровня). */
  private disposeLpTiles(tiles: LpTile[]): void {
    const gl = this.gl;
    for (const t of tiles) {
      gl.deleteVertexArray(t.vao);
      if (t.dustVao) gl.deleteVertexArray(t.dustVao);
      gl.deleteBuffer(t.buf);
    }
    tiles.length = 0;
  }

  /** Тайлы статического object-чанка. Собираются один раз; при разрушении
   *  (`visibleEpoch` растёт на КАЖДЫЙ выбитый куб) пересчитываются, но
   *  GL-объекты переиспользуются, а на GPU уходят только реально изменившиеся
   *  тайлы. Без этого непрерывный обстрел пересоздавал бы десятки буферов и
   *  VAO каждый кадр. Общий вход и для voxObj-пасса, и для пыли — буферы
   *  у них одни и те же. */
  private getObjectChunkTiles(c: VoxelChunkClient): { epoch: number; tiles: ObjTile[] } {
    let cache = this._objTileCache.get(c.id);
    if (!cache) {
      cache = { epoch: c.visibleEpoch, tiles: [] };
      this._objTileCache.set(c.id, cache);
      this.syncObjectChunkTiles(c, cache);
    } else if (cache.epoch !== c.visibleEpoch) {
      // Разрушение сообщает, какие кубы затронуты → пересобираем только их
      // тайлы. Пустой dirtyCubes при выросшем epoch = неизвестное изменение
      // (upsert чанка) → полная пересборка.
      this.syncObjectChunkTiles(c, cache, c.dirtyCubes.length > 0 ? c.dirtyCubes : null);
    }
    if (c.dirtyCubes.length > 0) c.dirtyCubes.length = 0;
    return cache;
  }

  /** Пересчитать раскладку статического object-чанка по тайлам и синхронизировать
   *  её с GPU. Инстанс — как в renderObjectChunks: xyz world + cubeSize, затем
   *  aliveIdx. Тайл (~LP_TILE_METERS) хранит СВОЙ буфер: за кадр остаётся только
   *  frustum-тест его AABB и draw, без пересчёта и заливки. */
  private syncObjectChunkTiles(
    c: VoxelChunkClient,
    cache: { epoch: number; tiles: ObjTile[] },
    dirtyCubes: number[] | null = null,
  ): void {
    const cs = c.cubeSize;
    const tvox = Math.max(1, Math.round(LP_TILE_METERS / cs));
    const ntx = Math.max(1, Math.ceil(c.sx / tvox));
    const nty = Math.max(1, Math.ceil(c.sy / tvox));
    const ntz = Math.max(1, Math.ceil(c.sz / tvox));
    const nTiles = ntx * nty * ntz;
    const sx = c.sx, sy = c.sy;
    const tileOf = (i: number): number => {
      const bx = i % sx;
      const tmp = (i - bx) / sx;
      const by = tmp % sy;
      const bz = (tmp - by) / sy;
      return ((bz / tvox) | 0) * ntx * nty + ((by / tvox) | 0) * ntx + ((bx / tvox) | 0);
    };

    // ── Инкрементальный путь ────────────────────────────────────────────
    // Разрушение меняет ровно 7 кубов (выбитый + 6 соседей). Раньше здесь
    // всё равно шёл проход по ВСЕМУ visibleList чанка — для chunk_wall_0 это
    // 302k кубов дважды на каждый выбитый кубик, то есть заметный фриз в
    // момент попадания (и в Chrome тоже). Теперь у тайла есть свой список
    // кубов: правим только его и переливаем только задетые тайлы.
    if (dirtyCubes && dirtyCubes.length > 0 && cache.tiles.length === nTiles) {
      const touched = new Set<number>();
      for (const i of dirtyCubes) {
        const t = tileOf(i);
        if (t < 0 || t >= nTiles) continue;
        const tile = cache.tiles[t];
        const list = tile.cubeIdx;
        const pos = list.indexOf(i);
        const visible = c.visibleIdx[i] >= 0;
        if (visible && pos < 0) { list.push(i); touched.add(t); }
        else if (!visible && pos >= 0) {
          list[pos] = list[list.length - 1];
          list.pop();
          touched.add(t);
        } else if (visible) {
          // Куб уже был виден, но у него могла открыться НОВАЯ грань
          // (стенка дыры) — маска пересчитывается при заливке тайла.
          touched.add(t);
        }
      }
      for (const t of touched) this.uploadObjTile(c, cache.tiles[t], cs);
      cache.epoch = c.visibleEpoch;
      return;
    }

    // ── Полная сборка ───────────────────────────────────────────────────
    const gl = this.gl;
    if (cache.tiles.length !== nTiles) {
      this.disposeObjTiles(cache.tiles);
      for (let t = 0; t < nTiles; t++) {
        const fb = gl.createBuffer();
        if (!fb) continue;
        cache.tiles.push({
          faceBuf: fb, faceVao: this.makeObjFaceVAO(fb), faces: 0, faceCap: 0,
          dustBuf: null, dustVao: null, dustCubes: 0, dustCap: 0,
          cubeIdx: [],
          minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0,
        });
      }
    }
    for (const t of cache.tiles) t.cubeIdx.length = 0;
    const list = c.visibleList;
    for (let li = 0; li < list.length; li++) {
      const i = list[li];
      const t = tileOf(i);
      if (t >= 0 && t < cache.tiles.length) cache.tiles[t].cubeIdx.push(i);
    }
    for (const tile of cache.tiles) this.uploadObjTile(c, tile, cs);
    cache.epoch = c.visibleEpoch;
  }

  /** Собрать инстансы одного тайла из его списка кубов и залить на GPU.
   *  Маска открытых граней считается по тому же правилу, что rebuildVisible():
   *  грань открыта, если сосед мёртв ИЛИ это край сетки чанка (край считаем
   *  открытым намеренно — соседний чанк отсюда не виден, лишняя грань на
   *  стыке безопаснее дыры). */
  private uploadObjTile(c: VoxelChunkClient, tile: ObjTile, cs: number): void {
    const gl = this.gl;
    const idx = tile.cubeIdx;
    const n = idx.length;
    if (n === 0) { tile.faces = 0; tile.dustCubes = 0; return; }

    const sx = c.sx, sy = c.sy, sz = c.sz;
    const sxsy = sx * sy;
    const alive = c.alive;
    const aliveIdxLut = c.aliveIdx;
    const ox = c.ox, oy = c.oy, oz = c.oz;
    const wantDust = this._dustEnabled;

    let faceCount = 0;
    for (let k = 0; k < n; k++) {
      const i = idx[k];
      const bx = i % sx;
      const tmp = (i - bx) / sx;
      const by = tmp % sy;
      const bz = (tmp - by) / sy;
      if (bx === sx - 1 || !isAliveBit(alive, i + 1))    faceCount++;
      if (bx === 0      || !isAliveBit(alive, i - 1))    faceCount++;
      if (by === sy - 1 || !isAliveBit(alive, i + sx))   faceCount++;
      if (by === 0      || !isAliveBit(alive, i - sx))   faceCount++;
      if (bz === sz - 1 || !isAliveBit(alive, i + sxsy)) faceCount++;
      if (bz === 0      || !isAliveBit(alive, i - sxsy)) faceCount++;
    }

    const fdata = new Float32Array(faceCount * 8);
    const cdata = wantDust ? new Float32Array(n * 8) : null;
    let fo = 0, co = 0;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    const h = cs * 0.5;

    for (let k = 0; k < n; k++) {
      const i = idx[k];
      const bx = i % sx;
      const tmp = (i - bx) / sx;
      const by = tmp % sy;
      const bz = (tmp - by) / sy;
      const wx = ox + (bx + 0.5) * cs;
      const wy = oy + (by + 0.5) * cs;
      const wz = oz + (bz + 0.5) * cs;
      const ai = aliveIdxLut ? aliveIdxLut[i] : 0;

      let m = 0;
      if (bx === sx - 1 || !isAliveBit(alive, i + 1))    m |= 1;
      if (bx === 0      || !isAliveBit(alive, i - 1))    m |= 2;
      if (by === sy - 1 || !isAliveBit(alive, i + sx))   m |= 4;
      if (by === 0      || !isAliveBit(alive, i - sx))   m |= 8;
      if (bz === sz - 1 || !isAliveBit(alive, i + sxsy)) m |= 16;
      if (bz === 0      || !isAliveBit(alive, i - sxsy)) m |= 32;
      for (let f = 0; f < 6; f++) {
        if ((m & (1 << f)) === 0) continue;
        fdata[fo] = wx; fdata[fo + 1] = wy; fdata[fo + 2] = wz; fdata[fo + 3] = cs;
        fdata[fo + 4] = ai; fdata[fo + 5] = f; fdata[fo + 6] = 0; fdata[fo + 7] = 0;
        fo += 8;
      }
      if (cdata !== null) {
        cdata[co] = wx; cdata[co + 1] = wy; cdata[co + 2] = wz; cdata[co + 3] = cs;
        cdata[co + 4] = ai; cdata[co + 5] = 0; cdata[co + 6] = 0; cdata[co + 7] = 0;
        co += 8;
      }
      if (wx - h < minX) minX = wx - h;
      if (wy - h < minY) minY = wy - h;
      if (wz - h < minZ) minZ = wz - h;
      if (wx + h > maxX) maxX = wx + h;
      if (wy + h > maxY) maxY = wy + h;
      if (wz + h > maxZ) maxZ = wz + h;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, tile.faceBuf);
    if (faceCount > tile.faceCap) {
      gl.bufferData(gl.ARRAY_BUFFER, fdata, gl.STATIC_DRAW);
      tile.faceCap = faceCount;
    } else {
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, fdata);
    }
    tile.faces = faceCount;

    if (cdata !== null) {
      if (!tile.dustBuf) {
        const db = gl.createBuffer();
        if (db) { tile.dustBuf = db; tile.dustVao = this.makeDustTileVAO(db); }
      }
      if (tile.dustBuf) {
        gl.bindBuffer(gl.ARRAY_BUFFER, tile.dustBuf);
        if (n > tile.dustCap) {
          gl.bufferData(gl.ARRAY_BUFFER, cdata, gl.STATIC_DRAW);
          tile.dustCap = n;
        } else {
          gl.bufferSubData(gl.ARRAY_BUFFER, 0, cdata);
        }
        tile.dustCubes = n;
      }
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    tile.minX = minX; tile.minY = minY; tile.minZ = minZ;
    tile.maxX = maxX; tile.maxY = maxY; tile.maxZ = maxZ;
  }

  /** Общие uniform'ы пофасадной программы. Текстуры (uv-lookup slot 0,
   *  diffuse slot 1) уже привязаны вызывающим — они одни на чанк. */
  private bindVoxObjFaceUniforms(lights: PointLight[]): void {
    const gl = this.gl;
    const u = this.uVoxObjFace;
    gl.uniformMatrix4fv(u.viewProj, false, this.viewProj);
    gl.uniform1i(u.lookupW, FACE_UV_LOOKUP_W);
    gl.uniform1i(u.uvLookup, 0);
    gl.uniform1i(u.diffuse, 1);
    if (u.baseDim) gl.uniform1f(u.baseDim, this._dustEnabled ? 0.88 : 1.0);
    const numL = this.prepareLightsClosestToCamera(lights);
    gl.uniform1i(u.numLights, numL);
    gl.uniform4fv(u.lights, this._lpos);
    gl.uniform3fv(u.lightColors, this._lcol);
    if (u.cameraPos) gl.uniform3f(u.cameraPos, this.eye[0], this.eye[1], this.eye[2]);
    if (u.lightLodDist) gl.uniform1f(u.lightLodDist, this._lightLodDist);
    this.bindFogUniforms(gl, {
      color: u.fogColor, density: u.fogDensity,
      height: u.fogHeight, falloff: u.fogHeightFalloff, exposure: u.exposure,
    });
  }

  /** Освободить GPU-ресурсы тайлов статического object-чанка. */
  private disposeObjTiles(tiles: ObjTile[]): void {
    const gl = this.gl;
    for (const t of tiles) {
      gl.deleteVertexArray(t.faceVao);
      gl.deleteBuffer(t.faceBuf);
      if (t.dustVao) gl.deleteVertexArray(t.dustVao);
      if (t.dustBuf) gl.deleteBuffer(t.dustBuf);
    }
    tiles.length = 0;
  }

  /** VAO пофасадного тайла: вершинного буфера нет вообще — только
   *  per-instance атрибуты (loc2 pos+size, loc3 aliveIdx+faceIdx).
   *  Геометрию грани шейдер строит из gl_VertexID. */
  private makeObjFaceVAO(buf: WebGLBuffer): WebGLVertexArrayObject {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("createVertexArray (obj face tile) failed");
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, 32, 0);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, 32, 16);
    gl.vertexAttribDivisor(3, 1);
    gl.bindVertexArray(null);
    return vao;
  }

  /** VAO поверх готового статического инстанс-буфера: геометрия куба общая
   *  (this.voxGeomVBO), инстанс-атрибуты (loc 2..5) смотрят в `buf`.
   *  Раскладка обязана совпадать с makeVoxelVAO. */
  private makeLpTileVAO(buf: WebGLBuffer): WebGLVertexArrayObject {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("createVertexArray (lp tile) failed");
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.voxGeomVBO);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 24, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, 12);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    for (let i = 0; i < 4; i++) {
      const loc = 2 + i;
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 4, gl.FLOAT, false, INSTANCE_FLOATS * 4, i * 16);
      gl.vertexAttribDivisor(loc, 1);
    }
    gl.bindVertexArray(null);
    return vao;
  }

  /** Разрезать статический levelpack-чанк на пространственные тайлы (~LP_TILE_METERS
   *  по стороне) и загрузить инстансы каждого тайла в СВОЙ статический GPU-буфер.
   *
   *  Зачем: раньше весь чанк (например, 302k кубов особняка в одном
   *  chunk_wall_0) на каждом кадре копировался в staging-массив и заливался
   *  через bufferSubData, а frustum-тест шёл по AABB чанка — то есть по всему
   *  зданию сразу и почти никогда не отсекал. Замер на alone_in_the_dark:
   *  490k кубов и 7.85 МБ upload'а НА КАЖДЫЙ кадр, из них 76% кубов вне
   *  frustum'а. Тайлы дают точный отсев и нулевой upload в установившемся
   *  режиме. */
  private buildLevelpackChunkTiles(
    c: VoxelChunkClient,
    texMode: number,
  ): { epoch: number; tiles: LpTile[] } {
    const gl = this.gl;
    const cs = c.cubeSize;
    // Сторона тайла в вокселях — так, чтобы получилось ~LP_TILE_METERS метров.
    const tvox = Math.max(1, Math.round(LP_TILE_METERS / cs));
    const ntx = Math.max(1, Math.ceil(c.sx / tvox));
    const nty = Math.max(1, Math.ceil(c.sy / tvox));
    const ntz = Math.max(1, Math.ceil(c.sz / tvox));

    // Раскладываем visibleList по тайлам (первый проход — счёт, второй — заполнение),
    // чтобы выделить ровно нужные Float32Array без промежуточных push-массивов.
    const list = c.visibleList;
    const nTiles = ntx * nty * ntz;
    const counts = new Int32Array(nTiles);
    const sxsy = c.sx * c.sy;
    const tileOf = (i: number): number => {
      const bx = i % c.sx;
      const tmp = (i - bx) / c.sx;
      const by = tmp % c.sy;
      const bz = (tmp - by) / c.sy;
      return ((bz / tvox) | 0) * ntx * nty + ((by / tvox) | 0) * ntx + ((bx / tvox) | 0);
    };
    for (let li = 0; li < list.length; li++) counts[tileOf(list[li])]++;

    const bufs: (Float32Array | null)[] = new Array(nTiles).fill(null);
    const fill = new Int32Array(nTiles);
    for (let t = 0; t < nTiles; t++) {
      if (counts[t] > 0) bufs[t] = new Float32Array(counts[t] * INSTANCE_FLOATS);
    }

    const ox = c.ox, oy = c.oy, oz = c.oz;
    const bboxSizeX = c.bboxSize[0] || 1, bboxSizeY = c.bboxSize[1] || 1, bboxSizeZ = c.bboxSize[2] || 1;
    const triWrap = Math.max(bboxSizeX, bboxSizeY, bboxSizeZ, 1e-3);
    const meshCenterLocalX = c.sx * cs * 0.5;
    const meshCenterLocalY = c.sy * cs * 0.5;
    const meshCenterLocalZ = c.sz * cs * 0.5;
    const bboxLocalMinX = meshCenterLocalX + c.bboxMin[0];
    const bboxLocalMinY = meshCenterLocalY + c.bboxMin[1];
    const bboxLocalMinZ = meshCenterLocalZ + c.bboxMin[2];
    void sxsy;
    void meshCenterLocalZ;

    // AABB тайла считаем по фактическим кубам (плотность внутри тайла обычно
    // сильно меньше объёма — точный bbox отсекает лучше номинального).
    const tMinX = new Float32Array(nTiles).fill(Infinity);
    const tMinY = new Float32Array(nTiles).fill(Infinity);
    const tMinZ = new Float32Array(nTiles).fill(Infinity);
    const tMaxX = new Float32Array(nTiles).fill(-Infinity);
    const tMaxY = new Float32Array(nTiles).fill(-Infinity);
    const tMaxZ = new Float32Array(nTiles).fill(-Infinity);

    const sx = c.sx;
    for (let li = 0; li < list.length; li++) {
      const i = list[li];
      const bx = i % sx;
      const tmp = (i - bx) / sx;
      const by = tmp % c.sy;
      const bz = (tmp - by) / c.sy;
      const t = ((bz / tvox) | 0) * ntx * nty + ((by / tvox) | 0) * ntx + ((bx / tvox) | 0);
      const data = bufs[t]!;
      const localCx = (bx + 0.5) * cs;
      const localCy = (by + 0.5) * cs;
      const localCz = (bz + 0.5) * cs;
      const wx = ox + localCx, wy = oy + localCy, wz = oz + localCz;
      const idx = fill[t]++ * INSTANCE_FLOATS;
      data[idx] = wx;
      data[idx + 1] = wy;
      data[idx + 2] = wz;
      data[idx + 3] = cs;
      data[idx + 4] = bboxSizeX;
      data[idx + 5] = bboxSizeY;
      data[idx + 6] = bboxSizeZ;
      data[idx + 7] = texMode;
      data[idx + 8] = bboxLocalMinX;
      data[idx + 9] = bboxLocalMinY;
      data[idx + 10] = bboxLocalMinZ;
      data[idx + 11] = 0;
      data[idx + 12] = localCx;
      data[idx + 13] = localCy;
      data[idx + 14] = localCz;
      data[idx + 15] = triWrap;
      const h = cs * 0.5;
      if (wx - h < tMinX[t]) tMinX[t] = wx - h;
      if (wy - h < tMinY[t]) tMinY[t] = wy - h;
      if (wz - h < tMinZ[t]) tMinZ[t] = wz - h;
      if (wx + h > tMaxX[t]) tMaxX[t] = wx + h;
      if (wy + h > tMaxY[t]) tMaxY[t] = wy + h;
      if (wz + h > tMaxZ[t]) tMaxZ[t] = wz + h;
    }

    const tiles: LpTile[] = [];
    for (let t = 0; t < nTiles; t++) {
      const data = bufs[t];
      if (!data || counts[t] === 0) continue;
      const buf = gl.createBuffer();
      if (!buf) continue;
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      tiles.push({
        buf,
        vao: this.makeLpTileVAO(buf),
        cubes: counts[t],
        minX: tMinX[t], minY: tMinY[t], minZ: tMinZ[t],
        maxX: tMaxX[t], maxY: tMaxY[t], maxZ: tMaxZ[t],
      });
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    return { epoch: c.visibleEpoch, tiles };
  }

  /** Pass для dynamic chunks (стулья, ящики и т.п. через main voxel шейдер).
   *  Каждый чанк — свой drawcall с собственным uChunkQuat. Шейдер сам
   *  поворачивает aPos/aNormal, JS-side rotation cube vertices не нужен. */
  private renderDynamicChunks(chunks: VoxelChunkClient[], nowSec: number): void {
    const gl = this.gl;
    // Программа/VAO/textures/lights уже подняты в основном pass'е renderChunks.
    // Меняем только uChunkQuat и instance buffer per chunk.
    // Фаска рёбер включается только здесь: у rigid-body объектов (стулья,
    // обломки мебели) кубы читаются как кубы и скругление уместно; статик
    // рисуется с bevel=0 (см. основной pass).
    if (this.uVox.voxelBevel) {
      gl.uniform1f(this.uVox.voxelBevel, this._voxelBevel);
    }
    const data = this.voxInstanceData;
    for (let ci = 0; ci < chunks.length; ci++) {
      const c = chunks[ci];
      if (!c.dynamic || c.faceUVs !== null) continue;
      if (!this.chunkInFrustum(c)) { this.lastCulledCount++; continue; }
      this.lastDrawnChunks++;
      const cs = c.cubeSize;
      const ox = c.ox, oy = c.oy, oz = c.oz;
      const cr = c.color[0], cg = c.color[1], cb = c.color[2];
      const texMode = materialTexMode(c.textureName);

      // Эффективная поза с extrapolation между сетевыми snapshot'ами.
      let posX = 0, posY = 0, posZ = 0;
      let qW = 1, qX = 0, qY = 0, qZ = 0;
      if (!c.pose.resting) {
        const dt = Math.max(0, nowSec - c.pose.lastUpdateTime);
        posX = c.pose.pos[0] + c.pose.vel[0] * dt;
        posY = c.pose.pos[1] + c.pose.vel[1] * dt;
        posZ = c.pose.pos[2] + c.pose.vel[2] * dt;
        const q = quatIntegrate(c.pose.quat, c.pose.angVelVec, dt);
        qW = q[0]; qX = q[1]; qY = q[2]; qZ = q[3];
      } else {
        posX = c.pose.pos[0]; posY = c.pose.pos[1]; posZ = c.pose.pos[2];
        qW = c.pose.quat[0]; qX = c.pose.quat[1]; qY = c.pose.quat[2]; qZ = c.pose.quat[3];
      }

      // Pivot вращения = центр XY чанка на bz=0 (как на сервере).
      const meshCenterLocalX = c.sx * cs * 0.5;
      const meshCenterLocalY = c.sy * cs * 0.5;
      const pivX = ox + meshCenterLocalX;
      const pivY = oy + meshCenterLocalY;
      const pivZ = oz;

      // Заполняем instance buffer. iPosSize.xyz = центр куба ПОСЛЕ JS-side
      // quat-rotation вокруг pivot (только translation). aPos и aNormal внутри
      // куба поворачиваются ШЕЙДЕРОМ через uChunkQuat — это даёт реальный
      // 3D rigid body вид (кубы наклоняются вместе со стулом).
      let n = 0;
      const list = c.visibleList;
      const sxsy = c.sx * c.sy;
      const useRot = qW < 0.99999 || qX !== 0 || qY !== 0 || qZ !== 0;
      for (let li = 0; li < list.length; li++) {
        if (n >= MAX_INSTANCES) break;
        const i = list[li];
        const bx = i % c.sx;
        const by = Math.floor(i / c.sx) % c.sy;
        const bz = Math.floor(i / sxsy);
        const localCx = (bx + 0.5) * cs;
        const localCy = (by + 0.5) * cs;
        const localCz = (bz + 0.5) * cs;
        const idx = n * INSTANCE_FLOATS;
        if (useRot) {
          const lx = localCx - meshCenterLocalX;
          const ly = localCy - meshCenterLocalY;
          const lz = localCz;
          // quatRotate (W,X,Y,Z) of (lx,ly,lz)
          const tx = 2 * (qY * lz - qZ * ly);
          const ty = 2 * (qZ * lx - qX * lz);
          const tz = 2 * (qX * ly - qY * lx);
          const rx = lx + qW * tx + (qY * tz - qZ * ty);
          const ry = ly + qW * ty + (qZ * tx - qX * tz);
          const rz = lz + qW * tz + (qX * ty - qY * tx);
          data[idx]     = pivX + rx + posX;
          data[idx + 1] = pivY + ry + posY;
          data[idx + 2] = pivZ + rz + posZ;
        } else {
          data[idx]     = ox + localCx + posX;
          data[idx + 1] = oy + localCy + posY;
          data[idx + 2] = oz + localCz + posZ;
        }
        data[idx + 3] = cs;
        data[idx + 4] = cr;
        data[idx + 5] = cg;
        data[idx + 6] = cb;
        data[idx + 7] = texMode;
        // iLocalPos.xyz = cube center в chunk-local frame — для wood
        // triplanar в локали (текстура вращается вместе со стулом).
        data[idx + 8]  = 0;
        data[idx + 9]  = 0;
        data[idx + 10] = 0;
        data[idx + 11] = 0;
        data[idx + 12] = localCx;
        data[idx + 13] = localCy;
        data[idx + 14] = localCz;
        data[idx + 15] = 0;
        n++;
      }
      if (n > 0) {
        gl.uniform4f(this.uVox.chunkQuat, qW, qX, qY, qZ);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.voxInstanceBuf);
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, n * INSTANCE_FLOATS);
        gl.drawArraysInstanced(gl.TRIANGLES, 0, 36, n);
      }
    }
  }

  /** Загрузить GLB-меш и зарегистрировать его в текущем контексте. */
  async loadMeshFromUrl(url: string): Promise<LoadedMesh> {
    return loadGlb(this.gl, url);
  }

  /** Создать MeshInstance с дефолтным AnimState (idle) и пустыми матрицами. */
  createMeshInstance(args: {
    mesh: LoadedMesh;
    /** Воксельный «скелет» с тем же rig что mesh (soldier_vox.glb). */
    innerMesh?: LoadedMesh | null;
    /** Режим отдельных мешей на куб (soldier_animated_real_vox.glb) — см. realVoxBody.ts. */
    realVoxelBody?: boolean;
    /** false — не вырезать inner по bodyVox в шейдере (остаётся дербис). По умолчанию true. */
    innerBodyVoxShaderCarve?: boolean;
    x: number; y: number; z: number;
    yaw: number; scale: number;
    yawOffset?: number;
    initialAnim?: string;
  }): MeshInstance {
    const jointCount = args.mesh.skin?.joints.length ?? 0;
    const innerRaw = args.innerMesh ?? null;
    let innerMesh: LoadedMesh | null = null;
    let innerBoneMatrices = new Float32Array(0);
    if (innerRaw) {
      if (canSkinInnerToOuterPose(args.mesh, innerRaw)) {
        innerMesh = innerRaw;
        const ijc = innerRaw.skin?.joints.length ?? 0;
        innerBoneMatrices = new Float32Array(ijc * 16);
      } else {
        console.warn("[mesh] innerMesh bone names missing on outer mesh — voxel body disabled");
      }
    }
    const mkPose = (): PoseSample => ({
      translation: new Map(),
      rotation: new Map(),
      scale: new Map(),
    });
    const rv = args.realVoxelBody === true;
    const inst: MeshInstance = {
      mesh: args.mesh,
      x: args.x, y: args.y, z: args.z, yaw: args.yaw,
      yawOffset: args.yawOffset ?? 0,
      aimPitch: 0,
      aimYaw: 0,
      scale: args.scale,
      state: newAnimState(args.initialAnim ?? "idle"),
      doorLayers: new Map(),
      subMeshWorldAABB: null,
      worldAabbDirty: true,
      poseComputed: false,
      lastPoseBaseAnim: "",
      lastPoseBaseTime: -1,
      lastPoseDoorCount: -1,
      hidden: false,
      hiddenJoints: null,
      fpViewOnly: false,
      nodeWorldMatrices: new Float32Array(args.mesh.nodes.length * 16),
      boneMatrices: new Float32Array(jointCount * 16),
      innerMesh,
      innerBoneMatrices,
      bodyHitCount: 0,
      bodyHitsPacked: new Float32Array(24 * 4),
      bodyVoxAlive: null,
      bodyVoxShell: null,
      bodyVoxContentHash: 0,
      bodyVoxHashInit: false,
      bodyVoxLastMutSec: -1,
      bodyVoxPrevForDebris: null,
      bodyVoxShellPrevForDebris: null,
      innerBodyVoxShaderCarve: args.innerBodyVoxShaderCarve !== false,
      innerMeshHiddenJoints: null,
      headNeckBoneIndices: null,
      npcHeadshotDetach: false,
      innerNodeWorldMatrices: innerMesh ? new Float32Array(innerMesh.nodes.length * 16) : new Float32Array(0),
      innerPoseScratch: mkPose(),
      realVoxelBody: rv && !!innerMesh,
      realVoxelHiddenSub: rv && innerMesh ? new Uint8Array(innerMesh.subMeshes.length) : null,
      realVoxelCellAlivePrev: null,
      realVoxelFlying: [],
      poseScratch: [mkPose(), mkPose(), mkPose()],
    };
    // Сразу заполняем матрицы из rest-позы (frame 0), чтобы первый рендер
    // не был с identity-bones (приплюснутый персонаж в bind-pose).
    this.updateMeshInstancePose(inst, 0);
    if (innerMesh?.skin && args.mesh.skin) {
      const yo = args.yawOffset ?? 0;
      if (!innerMesh.bodyVoxProxyBakes) innerMesh.bodyVoxProxyBakes = new Map();
      if (!innerMesh.bodyVoxProxyBakes.has(yo)) {
        const baked = bakeBodyVoxProxyData(innerMesh, args.mesh, yo);
        if (baked) innerMesh.bodyVoxProxyBakes.set(yo, baked);
      }
    }
    return inst;
  }

  /** Добавить mesh-инстанцию в сцену. Возвращает её для возможной модификации. */
  addMeshInstance(inst: MeshInstance): MeshInstance {
    this.meshInstances.push(inst);
    return inst;
  }

  /** Удалить mesh-инстанцию из сцены. */
  removeMeshInstance(inst: MeshInstance): void {
    const i = this.meshInstances.indexOf(inst);
    if (i >= 0) this.meshInstances.splice(i, 1);
  }

  /** Очистить все mesh-инстанции (на restart). */
  clearMeshInstances(): void {
    this.meshInstances.length = 0;
  }

  /** Список текущих mesh-инстанций (для AI/UI обновлений). */
  getMeshInstances(): MeshInstance[] {
    return this.meshInstances;
  }

  /** Проецирует мировую точку в пиксельные координаты канваса.
   *  Возвращает `null`, если точка за камерой / клипнулась.
   *  Вызывать ПОСЛЕ `render()` — опирается на актуальный `viewProj`.
   *
   *  Нужно в TP-режиме, чтобы двигать DOM-прицел туда, где реально
   *  окажется выстрел (шот летит от игрока вдоль yaw/pitch, а камера
   *  смещена назад, поэтому screen-центр ≠ точка попадания). */
  // Кэш canvas rect — запрашивается лениво через getBoundingClientRect()
  // (потенциально синхронный reflow). Инвалидация в render() на старте
  // кадра. projectToScreen может вызываться 2-5 раз за кадр (crosshair,
  // hp bar, etc.) — кэш исключает повторные reflow'ы.
  private _cachedRect: DOMRect | null = null;

  private camApproxEquals(cam: CameraState): boolean {
    const ez = cam.eyeZ ?? EYE_HEIGHT;
    const dx = cam.x - this._cacheCamX;
    const dy = cam.y - this._cacheCamY;
    const dz = ez - this._cacheCamZ;
    const dyaw = cam.yaw - this._cacheYaw;
    const dpit = cam.pitch - this._cachePitch;
    return (
      dx * dx + dy * dy + dz * dz < 1e-8 &&
      dyaw * dyaw + dpit * dpit < 1e-10
    );
  }

  private saveCamCache(cam: CameraState): void {
    this._cacheCamX = cam.x;
    this._cacheCamY = cam.y;
    this._cacheCamZ = cam.eyeZ ?? EYE_HEIGHT;
    this._cacheYaw = cam.yaw;
    this._cachePitch = cam.pitch;
  }

  private getObjectChunksCached(chunks: VoxelChunkClient[], epoch: number): VoxelChunkClient[] {
    if (epoch !== this._objectChunksEpoch) {
      const out = this._objectChunksCache;
      out.length = 0;
      for (let i = 0; i < chunks.length; i++) {
        const c = chunks[i];
        if (c.faceUVs !== null) out.push(c);
      }
      this._objectChunksEpoch = epoch;
    }
    return this._objectChunksCache;
  }

  /** Готовит `_lpos/_lcol` — ровно MAX_LIGHTS ближайших к камере ламп,
   *  остальные слоты range=0. Idempotent в пределах одного кадра: если уже
   *  вызывали (флаг `_preparedNumLights >= 0`), возвращает кэшированное N. */
  private prepareLightsClosestToCamera(lights: PointLight[]): number {
    if (this._preparedNumLights >= 0) return this._preparedNumLights;

    const max = Renderer.MAX_LIGHTS;
    const lpos = this._lpos;
    const lcol = this._lcol;
    for (let i = 0; i < max; i++) {
      lpos[i * 4 + 3] = 0;
    }
    if (lights.length <= max) {
      for (let i = 0; i < lights.length; i++) {
        const l = lights[i];
        lpos[i * 4]     = l.x;
        lpos[i * 4 + 1] = l.y;
        lpos[i * 4 + 2] = l.z;
        lpos[i * 4 + 3] = l.range;
        lcol[i * 3]     = l.color[0];
        lcol[i * 3 + 1] = l.color[1];
        lcol[i * 3 + 2] = l.color[2];
      }
      this._preparedNumLights = lights.length;
      return lights.length;
    }
    const ex = this.eye[0], ey = this.eye[1], ez = this.eye[2];
    const nl = lights.length;
    const picked = new Uint8Array(nl);
    for (let k = 0; k < max; k++) {
      let best = -1;
      let bestD = Infinity;
      for (let i = 0; i < nl; i++) {
        if (picked[i]) continue;
        const l = lights[i];
        const dx = l.x - ex, dy = l.y - ey, dz = l.z - ez;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < bestD) {
          bestD = d2;
          best = i;
        }
      }
      if (best < 0) break;
      picked[best] = 1;
      const l = lights[best];
      lpos[k * 4]     = l.x;
      lpos[k * 4 + 1] = l.y;
      lpos[k * 4 + 2] = l.z;
      lpos[k * 4 + 3] = l.range;
      lcol[k * 3]     = l.color[0];
      lcol[k * 3 + 1] = l.color[1];
      lcol[k * 3 + 2] = l.color[2];
    }
    this._preparedNumLights = Math.min(max, nl);
    return this._preparedNumLights;
  }

  /** Извлекает 6 world-space плоскостей view frustum из столбцов viewProj
   *  (column-major). Каждая плоскость хранится как [a,b,c,d] где
   *  ax+by+cz+d ≥ 0 означает "внутри frustum". Не нормализуем длину
   *  нормали: для теста AABB она не нужна, важен только знак. */
  private extractFrustumPlanes(): void {
    const m = this.viewProj;
    const p = this.frustumPlanes;
    // column-major: m[col*4 + row]. "row i" = [m[0+i], m[4+i], m[8+i], m[12+i]].
    const r0x = m[0], r0y = m[4], r0z = m[8],  r0w = m[12];
    const r1x = m[1], r1y = m[5], r1z = m[9],  r1w = m[13];
    const r2x = m[2], r2y = m[6], r2z = m[10], r2w = m[14];
    const r3x = m[3], r3y = m[7], r3z = m[11], r3w = m[15];
    // left = row3 + row0
    p[0] = r3x + r0x; p[1] = r3y + r0y; p[2] = r3z + r0z; p[3] = r3w + r0w;
    // right = row3 - row0
    p[4] = r3x - r0x; p[5] = r3y - r0y; p[6] = r3z - r0z; p[7] = r3w - r0w;
    // bottom = row3 + row1
    p[8] = r3x + r1x; p[9] = r3y + r1y; p[10] = r3z + r1z; p[11] = r3w + r1w;
    // top = row3 - row1
    p[12] = r3x - r1x; p[13] = r3y - r1y; p[14] = r3z - r1z; p[15] = r3w - r1w;
    // near = row3 + row2
    p[16] = r3x + r2x; p[17] = r3y + r2y; p[18] = r3z + r2z; p[19] = r3w + r2w;
    // far = row3 - row2
    p[20] = r3x - r2x; p[21] = r3y - r2y; p[22] = r3z - r2z; p[23] = r3w - r2w;
  }

  /** AABB vs frustum test. Возвращает true если AABB ЧАСТИЧНО или ПОЛНОСТЬЮ
   *  внутри frustum. Алгоритм "positive corner": для каждой плоскости
   *  выбираем угол AABB, максимально удалённый в направлении нормали; если
   *  он ПОД плоскостью — весь AABB отсечён. Быстрее чем проверка 8 углов. */
  private aabbInFrustum(
    minX: number, minY: number, minZ: number,
    maxX: number, maxY: number, maxZ: number,
  ): boolean {
    const p = this.frustumPlanes;
    for (let i = 0; i < 6; i++) {
      const o = i * 4;
      const a = p[o], b = p[o + 1], c = p[o + 2], d = p[o + 3];
      const px = a >= 0 ? maxX : minX;
      const py = b >= 0 ? maxY : minY;
      const pz = c >= 0 ? maxZ : minZ;
      if (a * px + b * py + c * pz + d < 0) return false; // вся bbox за плоскостью
    }
    return true;
  }

  /** Проверяет входит ли чанк (static или dynamic с identity pose) в
   *  видимую область камеры. Для dynamic чанков с ненулевой pose делаем
   *  консервативный expanded AABB (half-extent = sqrt(3)*radius). */
  private chunkInFrustum(c: VoxelChunkClient): boolean {
    const cs = c.cubeSize;
    let minX = c.ox, minY = c.oy, minZ = c.oz;
    let maxX = c.ox + c.sx * cs;
    let maxY = c.oy + c.sy * cs;
    let maxZ = c.oz + c.sz * cs;
    if (c.dynamic) {
      // Учитываем сдвиг pose + консервативный padding для rotation.
      const rx = (maxX - minX) * 0.5;
      const ry = (maxY - minY) * 0.5;
      const rz = (maxZ - minZ) * 0.5;
      const r = Math.sqrt(rx * rx + ry * ry + rz * rz);
      const cx = (minX + maxX) * 0.5 + c.pose.pos[0];
      const cy = (minY + maxY) * 0.5 + c.pose.pos[1];
      const cz = (minZ + maxZ) * 0.5 + c.pose.pos[2];
      minX = cx - r; maxX = cx + r;
      minY = cy - r; maxY = cy + r;
      minZ = cz - r; maxZ = cz + r;
    }
    return this.aabbInFrustum(minX, minY, minZ, maxX, maxY, maxZ);
  }

  private projectWorldToSceneUv(x: number, y: number, z: number): [number, number] | null {
    const vp = this.viewProj;
    const cx = vp[0] * x + vp[4] * y + vp[8] * z + vp[12];
    const cy = vp[1] * x + vp[5] * y + vp[9] * z + vp[13];
    const cz = vp[2] * x + vp[6] * y + vp[10] * z + vp[14];
    const cw = vp[3] * x + vp[7] * y + vp[11] * z + vp[15];
    if (cw <= 1e-6) return null;
    const ndcX = cx / cw;
    const ndcY = cy / cw;
    const ndcZ = cz / cw;
    if (ndcZ < -1.0 || ndcZ > 1.0) return null;
    return [ndcX * 0.5 + 0.5, ndcY * 0.5 + 0.5];
  }

  projectToScreen(x: number, y: number, z: number): { px: number; py: number } | null {
    const vp = this.viewProj;
    const cx = vp[0] * x + vp[4] * y + vp[8]  * z + vp[12];
    const cy = vp[1] * x + vp[5] * y + vp[9]  * z + vp[13];
    const cw = vp[3] * x + vp[7] * y + vp[11] * z + vp[15];
    if (cw <= 1e-4) return null;
    const ndcX = cx / cw;
    const ndcY = cy / cw;
    if (this._cachedRect === null) {
      this._cachedRect = this.canvas.getBoundingClientRect();
    }
    const rect = this._cachedRect;
    const px = rect.left + (ndcX * 0.5 + 0.5) * rect.width;
    const py = rect.top  + (1 - (ndcY * 0.5 + 0.5)) * rect.height;
    return { px, py };
  }

  /** Считает фактический bbox skinned-меша в локальном frame (model space)
   *  при текущей позе инстанса. Вызывать ПОСЛЕ `updateMeshInstancePose`,
   *  чтобы `inst.boneMatrices` уже были заполнены.
   *
   *  Возвращает min/max в той же системе координат, что и оригинальные
   *  POSITION'ы (Y-up для Mixamo). Это нужно потому что bind-pose bbox
   *  != render-pose bbox: например в Mixamo idle ноги расположены на Y≈0,
   *  а в bind T-pose — на Y≈-0.95. Если использовать bind-bbox для
   *  расчёта `floorOffset`, персонаж "висит" в воздухе на ~90см. */
  computeSkinnedBboxAtCurrentPose(inst: MeshInstance): {
    min: [number, number, number]; max: [number, number, number];
  } {
    const mesh = inst.mesh;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    const bones = inst.boneMatrices;

    for (const sm of mesh.subMeshes) {
      const cv = sm.cpuVerts;
      const pos = cv.positions;
      const vCount = pos.length / 3;

      if (sm.hasSkin && cv.joints && cv.weights && bones.length > 0) {
        const js = cv.joints;
        const ws = cv.weights;
        for (let vi = 0; vi < vCount; vi++) {
          const px = pos[vi * 3], py = pos[vi * 3 + 1], pz = pos[vi * 3 + 2];
          let ox = 0, oy = 0, oz = 0;
          for (let k = 0; k < 4; k++) {
            const w = ws[vi * 4 + k];
            if (w === 0) continue;
            const j = js[vi * 4 + k];
            const o = j * 16;
            // column-major mat4 × vec4 (assume w_pos=1, ignore translation row)
            const tx = bones[o + 0] * px + bones[o + 4] * py + bones[o + 8]  * pz + bones[o + 12];
            const ty = bones[o + 1] * px + bones[o + 5] * py + bones[o + 9]  * pz + bones[o + 13];
            const tz = bones[o + 2] * px + bones[o + 6] * py + bones[o + 10] * pz + bones[o + 14];
            ox += w * tx; oy += w * ty; oz += w * tz;
          }
          if (ox < minX) minX = ox; if (ox > maxX) maxX = ox;
          if (oy < minY) minY = oy; if (oy > maxY) maxY = oy;
          if (oz < minZ) minZ = oz; if (oz > maxZ) maxZ = oz;
        }
      } else {
        // Не-skinned subMesh (например weapon): применяем только nodeWorld.
        const nwOff = sm.nodeIdx * 16;
        const nw = inst.nodeWorldMatrices;
        for (let vi = 0; vi < vCount; vi++) {
          const px = pos[vi * 3], py = pos[vi * 3 + 1], pz = pos[vi * 3 + 2];
          const tx = nw[nwOff + 0] * px + nw[nwOff + 4] * py + nw[nwOff + 8]  * pz + nw[nwOff + 12];
          const ty = nw[nwOff + 1] * px + nw[nwOff + 5] * py + nw[nwOff + 9]  * pz + nw[nwOff + 13];
          const tz = nw[nwOff + 2] * px + nw[nwOff + 6] * py + nw[nwOff + 10] * pz + nw[nwOff + 14];
          if (tx < minX) minX = tx; if (tx > maxX) maxX = tx;
          if (ty < minY) minY = ty; if (ty > maxY) maxY = ty;
          if (tz < minZ) minZ = tz; if (tz > maxZ) maxZ = tz;
        }
      }
    }
    if (!isFinite(minX)) {
      return { min: [0, 0, 0], max: [0, 0, 0] };
    }
    return { min: [minX, minY, minZ], max: [maxX, maxY, maxZ] };
  }

  /** Анимация инстанса заморожена и внешний код не трогал стейт с последнего
   *  пересчёта — позу и FK можно не пересчитывать (302 ноды особняка каждый
   *  кадр сэмплировали замороженный дверной клип с аллокациями впустую). */
  private meshPoseIsStatic(inst: MeshInstance): boolean {
    const st = inst.state;
    if (st.baseSpeed !== 0) return false;
    if (st.prevBase && st.crossfadeT < 1) return false;
    if (st.upperLayer) return false;
    if (st.baseAnim !== inst.lastPoseBaseAnim || st.baseTime !== inst.lastPoseBaseTime) return false;
    if (inst.doorLayers.size !== inst.lastPoseDoorCount) return false;
    for (const layer of inst.doorLayers.values()) {
      if (!layer.holdLast) return false; // autoRemove-слой (закрытие) ещё живёт
      let maxDur = 0;
      for (const cn of layer.clips) {
        const clip = inst.mesh.animations.get(cn);
        if (clip && clip.duration > maxDur) maxDur = clip.duration;
      }
      if (maxDur === 0 || layer.time < maxDur) return false;
    }
    return true;
  }

  /** Тик AnimState и пересчёт node/bone матриц для одного инстанса.
   *  Вызывается раз за render-frame, после изменения state.baseAnim/upperLayer. */
  updateMeshInstancePose(inst: MeshInstance, dt: number): void {
    if (inst.poseComputed && this.meshPoseIsStatic(inst)) return;
    if (dt > 0) tickAnimState(inst.state, dt);
    const mesh = inst.mesh;
    let baseClip =
      mesh.animations.size > 0 ? mesh.animations.get(inst.state.baseAnim) : undefined;
    // Скиновый персонаж никогда не показывает bind-позу («Т-поза»): если
    // затребованный клип у меша отсутствует (override/опечатка/набор клипов
    // другой модели) — играем idle. Ветка bind ниже остаётся для статических
    // пропов без скелета.
    if (baseClip === undefined && mesh.skin && mesh.animations.size > 0) {
      baseClip = mesh.animations.get("idle");
    }
    // Статические пропы (ammo/medkit .glb) часто без клипов или без "idle" —
    // раньше здесь был ранний return и скин/ноды не обновлялись → меш не рисовался.
    if (mesh.animations.size === 0 || baseClip === undefined) {
      const rest = inst.poseScratch[0]!;
      rest.translation.clear();
      rest.rotation.clear();
      rest.scale.clear();
      computeNodeWorldMatrices(mesh.nodes, mesh.nodeTraversalOrder, rest, inst.nodeWorldMatrices);
      if (mesh.skin && inst.boneMatrices.length > 0) {
        computeBoneMatrices(mesh.skin, inst.nodeWorldMatrices, inst.boneMatrices);
      }
      if (inst.innerMesh && inst.innerNodeWorldMatrices.length >= inst.innerMesh.nodes.length * 16) {
        buildInnerPoseFromOuter(
          inst.innerMesh.nodes,
          mesh.nodes,
          rest,
          inst.innerPoseScratch,
        );
        computeNodeWorldMatrices(
          inst.innerMesh.nodes,
          inst.innerMesh.nodeTraversalOrder,
          inst.innerPoseScratch,
          inst.innerNodeWorldMatrices,
        );
      }
      if (inst.innerMesh?.skin && inst.innerBoneMatrices.length > 0) {
        computeBoneMatricesInnerFromOuterPose(
          inst.innerMesh.skin,
          inst.innerMesh.nodes,
          inst.mesh.nodes,
          inst.nodeWorldMatrices,
          inst.innerBoneMatrices,
        );
      }
      this.markPoseComputed(inst);
      return;
    }
    const [pa, pb, pblend] = inst.poseScratch;
    sampleClipInto(baseClip, inst.state.baseTime, isLoopingAnim(inst.state.baseAnim), pa);
    let basePose: PoseSample = pa;

    // Cross-fade между prev → current base (idle ↔ walk и т.п.)
    if (inst.state.prevBase && inst.state.crossfadeT < 1) {
      const prevClip = mesh.animations.get(inst.state.prevBase);
      if (prevClip) {
        sampleClipInto(prevClip, inst.state.prevBaseTime, isLoopingAnim(inst.state.prevBase), pb);
        blendPosesInto(pb, pa, inst.state.crossfadeT, pblend);
        basePose = pblend;
      }
    }

    // Upper-body overlay (стрельба поверх ходьбы и т.п.).
    // Маска и extraNodes берутся из layer'а, если заданы (для hurt — своя
    // маска "корпус+голова, без рук и ног"); иначе — общие mesh.upperBody*.
    const u = inst.state.upperLayer;
    if (u && mesh.skin) {
      const mask = (u.mask ?? mesh.upperBodyMask);
      const extraNodes = u.extraNodes ?? mesh.upperBodyExtraNodes;
      // rotation-only override — применяем ТОЛЬКО для default mask (shoot /
      // stance-idle overlay). Для hurt-flinch (своя u.mask) не трогаем
      // Hips, иначе flinch корпуса гасится override'ом.
      const rotOnlyNodes = (u.mask == null) ? mesh.upperBodyRotationOnlyNodes : [];
      if (mask) {
        const clip = mesh.animations.get(u.clipName);
        if (clip) {
          const upperSlot = basePose === pblend ? pa : pb;
          sampleClipInto(clip, u.time, u.looping, upperSlot);
          const w = Math.abs(u.weight);
          if (w > 0.001) {
            overlayPose(
              basePose, upperSlot, mask, w,
              mesh.skin.joints, extraNodes, rotOnlyNodes,
            );
          }
        }
      }
    }

    // Дверные оверлеи static-меша уровня: общие клипы (DoorOpen и т.п.)
    // сэмплируются С ФИЛЬТРОМ по нодам конкретной двери прямо поверх base-позы.
    if (inst.doorLayers.size > 0) {
      let expired: string[] | null = null;
      for (const [key, layer] of inst.doorLayers) {
        if (dt > 0) layer.time += dt * layer.speed;
        let maxDur = 0;
        for (const cn of layer.clips) {
          const clip = mesh.animations.get(cn);
          if (!clip) continue;
          if (clip.duration > maxDur) maxDur = clip.duration;
          sampleClipInto(clip, layer.time, false, basePose, layer.nodeFilter);
        }
        if (layer.autoRemove && maxDur > 0 && layer.time >= maxDur) {
          (expired ??= []).push(key);
        }
      }
      if (expired) for (const key of expired) inst.doorLayers.delete(key);
    }

    // Aim IK: наклоняем верхний корпус (Spine) на pitch игрока + докручиваем
    // на yaw для компенсации запечённого угла ствола в shoot-анимации.
    // Применяем ПОСЛЕ overlay, ДО FK — изменение распространится на всё что
    // выше Spine (плечи, руки, оружие через weapone_bone subtree).
    if (mesh.aimPitchBoneIdx !== null &&
        (Math.abs(inst.aimPitch) > 0.001 || Math.abs(inst.aimYaw) > 0.001)) {
      applyAimToPose(basePose, mesh, inst.aimPitch, inst.aimYaw);
    }

    computeNodeWorldMatrices(mesh.nodes, mesh.nodeTraversalOrder, basePose, inst.nodeWorldMatrices);
    if (mesh.skin && inst.boneMatrices.length > 0) {
      computeBoneMatrices(mesh.skin, inst.nodeWorldMatrices, inst.boneMatrices);
    }
    if (inst.innerMesh && inst.innerNodeWorldMatrices.length >= inst.innerMesh.nodes.length * 16) {
      buildInnerPoseFromOuter(
        inst.innerMesh.nodes,
        mesh.nodes,
        basePose,
        inst.innerPoseScratch,
      );
      computeNodeWorldMatrices(
        inst.innerMesh.nodes,
        inst.innerMesh.nodeTraversalOrder,
        inst.innerPoseScratch,
        inst.innerNodeWorldMatrices,
      );
    }
    if (inst.innerMesh?.skin && inst.innerBoneMatrices.length > 0) {
      computeBoneMatricesInnerFromOuterPose(
        inst.innerMesh.skin,
        inst.innerMesh.nodes,
        inst.mesh.nodes,
        inst.nodeWorldMatrices,
        inst.innerBoneMatrices,
      );
    }
    this.markPoseComputed(inst);
  }

  /** Пересчёт мировых AABB всех не-skinned сабмешей инстанса:
   *  world = (im · nodeWorld) × локальный bbox примитива (meshLoader).
   *  Ghost/video-сабмеши получают вырожденный AABB и не куллятся. */
  private updateSubMeshWorldAABBs(inst: MeshInstance, im: Float32Array): void {
    const subs = inst.mesh.subMeshes;
    if (!inst.subMeshWorldAABB || inst.subMeshWorldAABB.length !== subs.length * 6) {
      inst.subMeshWorldAABB = new Float32Array(subs.length * 6);
    }
    const out = inst.subMeshWorldAABB;
    const m = this._scratchAabbMat;
    for (let i = 0; i < subs.length; i++) {
      const sm = subs[i];
      const o = i * 6;
      if (sm.hasSkin || sm.ghostKind || sm.diffuseVideo) {
        out[o] = out[o + 1] = out[o + 2] = 0;
        out[o + 3] = out[o + 4] = out[o + 5] = -1; // «не куллить» — тест не применяется
        continue;
      }
      const nwOff = sm.nodeIdx * 16;
      const nodeMat = inst.nodeWorldMatrices.subarray(nwOff, nwOff + 16);
      mat4Multiply(m, im, nodeMat);
      const bmin = sm.bboxMin;
      const bmax = sm.bboxMax;
      // AABB-трансформация column-major матрицей (min/max по вкладу каждой оси).
      for (let r = 0; r < 3; r++) {
        let lo = m[12 + r];
        let hi = lo;
        for (let c = 0; c < 3; c++) {
          const e = m[c * 4 + r];
          const a = e * bmin[c];
          const b = e * bmax[c];
          if (a < b) { lo += a; hi += b; } else { lo += b; hi += a; }
        }
        out[o + r] = lo;
        out[o + 3 + r] = hi;
      }
    }
    inst.worldAabbDirty = false;
  }

  private _scratchAabbMat = new Float32Array(16);

  /** Поза пересчитана: запомнить снапшот стейта для meshPoseIsStatic и
   *  пометить мировые AABB сабмешей устаревшими. */
  private markPoseComputed(inst: MeshInstance): void {
    inst.poseComputed = true;
    inst.lastPoseBaseAnim = inst.state.baseAnim;
    inst.lastPoseBaseTime = inst.state.baseTime;
    inst.lastPoseDoorCount = inst.doorLayers.size;
    inst.worldAabbDirty = true;
  }

  /** uBones для skinned инстанса: FP-cull на outer, зануление конечностей на inner. */
  private bindActorSkinBones(inst: MeshInstance, bones: Float32Array, pass: "inner" | "outer"): void {
    const gl = this.gl;
    if (bones.length > 0) {
      const fpHide = pass === "outer" && inst.hiddenJoints && inst.hiddenJoints.length > 0;
      const innerHide = pass === "inner" && inst.innerMeshHiddenJoints
        && inst.innerMeshHiddenJoints.length > 0;
      const outerHeadHide =
        pass === "outer" &&
        inst.npcHeadshotDetach &&
        inst.headNeckBoneIndices !== null &&
        inst.headNeckBoneIndices.length > 0;
      if (fpHide || innerHide || outerHeadHide) {
        const buf = this._hiddenJointsScratch;
        buf.set(bones);
        if (fpHide) {
          for (const j of inst.hiddenJoints!) {
            const o = j * 16;
            for (let k = 0; k < 16; k++) buf[o + k] = 0;
          }
        }
        if (innerHide) {
          for (const j of inst.innerMeshHiddenJoints!) {
            const o = j * 16;
            for (let k = 0; k < 16; k++) buf[o + k] = 0;
          }
        }
        if (outerHeadHide) {
          for (const j of inst.headNeckBoneIndices!) {
            const o = j * 16;
            for (let k = 0; k < 16; k++) buf[o + k] = 0;
          }
        }
        gl.uniformMatrix4fv(this.uMesh.bones, false, buf.subarray(0, bones.length));
      } else {
        gl.uniformMatrix4fv(this.uMesh.bones, false, bones);
      }
    } else {
      gl.uniformMatrix4fv(this.uMesh.bones, false, this._identityBones);
    }
  }

  /** R8 16×19 — битмаска тела для mesh.frag (uBodyDamageMode == 4). */
  /** Ловим изменения bodyVox (в т.ч. in-place) для таймера «вылета» обломков. */
  private syncBodyVoxDamageClock(inst: MeshInstance, nowSec: number): void {
    const vox = inst.bodyVoxAlive;
    if (!vox || vox.length !== BODY_VOX_GRID_BYTES) return;
    let nh = 2166136261 >>> 0;
    for (let i = 0; i < vox.length; i++) {
      nh = Math.imul(nh ^ vox[i], 16777619) >>> 0;
    }
    const sh = inst.bodyVoxShell;
    if (sh && sh.length === BODY_VOX_GRID_BYTES) {
      for (let i = 0; i < sh.length; i++) {
        nh = Math.imul(nh ^ sh[i], 16777619) >>> 0;
      }
    }
    if (!inst.bodyVoxHashInit) {
      inst.bodyVoxHashInit = true;
      inst.bodyVoxContentHash = nh;
      let anyDead = false;
      for (let i = 0; i < vox.length; i++) {
        if (vox[i] !== 0xff) {
          anyDead = true;
          break;
        }
      }
      if (!anyDead && sh && sh.length === BODY_VOX_GRID_BYTES) {
        for (let i = 0; i < sh.length; i++) {
          if (sh[i] !== 0xff) {
            anyDead = true;
            break;
          }
        }
      }
      inst.bodyVoxLastMutSec = anyDead ? nowSec - 2.45 : -1;
    } else if (nh !== inst.bodyVoxContentHash) {
      inst.bodyVoxContentHash = nh;
      inst.bodyVoxLastMutSec = nowSec;
    }
  }

  private uploadBodyVoxBitmap(bits: Uint8Array): void {
    const gl = this.gl;
    if (!this._bodyVoxBitsTex) {
      const t = gl.createTexture();
      if (!t) throw new Error("createTexture body vox");
      this._bodyVoxBitsTex = t;
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }
    this._bodyVoxUploadPad.fill(0);
    this._bodyVoxUploadPad.set(bits.subarray(0, BODY_VOX_GRID_BYTES));
    gl.bindTexture(gl.TEXTURE_2D, this._bodyVoxBitsTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, 16, 19, 0, gl.RED, gl.UNSIGNED_BYTE, this._bodyVoxUploadPad);
  }

  /** Режим 4: discard по bodyVox для inner-каркаса и для гладкого outer shell. */
  private applyBodyVoxDamageMode4Uniforms(
    inst: MeshInstance,
    vox: Uint8Array,
    outerShell: boolean,
    nowSec: number,
  ): void {
    const gl = this.gl;
    gl.uniform1i(this.uMesh.bodyDamageMode, 4);
    if (this.uMesh.bodyOuterShell) gl.uniform1i(this.uMesh.bodyOuterShell, outerShell ? 1 : 0);
    this.uploadBodyVoxBitmap(vox);
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this._bodyVoxBitsTex);
    if (this.uMesh.bodyVoxBits) gl.uniform1i(this.uMesh.bodyVoxBits, 3);
    if (this.uMesh.bodyGridPack) {
      gl.uniform4f(this.uMesh.bodyGridPack, BODY_VOX_OX, BODY_VOX_OY, BODY_VOX_OZ, BODY_VOX_CUBE);
    }
    if (this.uMesh.bodyVoxSize) {
      gl.uniform3i(this.uMesh.bodyVoxSize, BODY_VOX_SX, BODY_VOX_SY, BODY_VOX_SZ);
    }
    if (this.uMesh.bodyVoxDebris) gl.uniform1i(this.uMesh.bodyVoxDebris, 0);
    let debrisAge = 2.5;
    if (inst.bodyVoxLastMutSec >= 0) {
      debrisAge = Math.min(5.5, Math.max(0, nowSec - inst.bodyVoxLastMutSec));
    }
    if (this.uMesh.bodyDebrisAge) gl.uniform1f(this.uMesh.bodyDebrisAge, debrisAge);
    if (this.uMesh.bodyVoxBorderDent) gl.uniform1f(this.uMesh.bodyVoxBorderDent, 0);
  }

  private composeRealVoxelFlyModel(piece: RealVoxelFlyPiece, out: Float32Array): void {
    const dx = piece.px - piece.c0x;
    const dy = piece.py - piece.c0y;
    const dz = piece.pz - piece.c0z;
    const T = this._flyTScratch;
    T.fill(0);
    T[0] = T[5] = T[10] = T[15] = 1;
    T[12] = dx;
    T[13] = dy;
    T[14] = dz;
    mat4Multiply(out, T, piece.baseModel);
  }

  /** Отлетевшие кубы — тот же VAO, новая world matrix. */
  private drawRealVoxelFlying(inst: MeshInstance, gl: WebGL2RenderingContext): void {
    if (!inst.innerMesh || inst.realVoxelFlying.length === 0) return;
    const inner = inst.innerMesh;
    const outM = this._flyModelOut;
    for (const piece of inst.realVoxelFlying) {
      const sm = inner.subMeshes[piece.subIdx];
      if (!sm) continue;
      const skinFly = piece.frozenBones !== null && piece.spawnIm !== null;
      if (skinFly) {
        gl.uniformMatrix4fv(this.uMesh.model, false, piece.spawnIm!);
        gl.uniformMatrix4fv(this.uMesh.bones, false, piece.frozenBones!);
        gl.uniform1i(this.uMesh.useSkin, 1);
        if (this.uMesh.useFlyDetach) gl.uniform1i(this.uMesh.useFlyDetach, 1);
        if (this.uMesh.flyWorldDelta) {
          gl.uniform3f(
            this.uMesh.flyWorldDelta,
            piece.px - piece.c0x,
            piece.py - piece.c0y,
            piece.pz - piece.c0z,
          );
        }
      } else {
        this.composeRealVoxelFlyModel(piece, outM);
        gl.uniformMatrix4fv(this.uMesh.model, false, outM);
        gl.uniform1i(this.uMesh.useSkin, 0);
        this.bindActorSkinBones(inst, inst.innerBoneMatrices, "inner");
        if (this.uMesh.useFlyDetach) gl.uniform1i(this.uMesh.useFlyDetach, 0);
      }
      gl.uniform1i(this.uMesh.bodyDamageMode, 0);
      if (this.uMesh.bodyVoxDebris) gl.uniform1i(this.uMesh.bodyVoxDebris, 0);
      if (this.uMesh.bodyVoxBorderDent) gl.uniform1f(this.uMesh.bodyVoxBorderDent, 0);
      this.bindMeshMaterial(sm);
      if (this.uMesh.meatTint) gl.uniform1f(this.uMesh.meatTint, 0.88);
      gl.bindVertexArray(sm.vao);
      this.drawMeshTriangles(gl, sm);
      this.lastRealVoxelBodyDrawCalls++;
    }
    if (this.uMesh.meatTint) gl.uniform1f(this.uMesh.meatTint, 0);
    if (this.uMesh.useFlyDetach) gl.uniform1i(this.uMesh.useFlyDetach, 0);
  }

  /** inst без skin: model = im·nodeWorld; ghost_kind=character — цилиндр. billboard к локальной камере (MP: только свой клиент). */
  private modelMatNoSkin(out: Mat4, im: Mat4, nodeMat: Float32Array, sm: SubMesh): void {
    if (sm.ghostKind === "character") {
      mat4GhostCharacterBillboardZUp(
        out,
        im,
        nodeMat,
        [this.eye[0]!, this.eye[1]!, this.eye[2]!],
        sm.ghostBillboardYawOffsetRad,
      );
    } else {
      mat4Multiply(out, im, nodeMat);
    }
  }

  /** glTF: baseColor + normal + metallicRoughness + alphaMode; texture units 0..2. */
  private bindMeshMaterial(sm: SubMesh): void {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0);
    if (sm.diffuseTex && sm.diffuseVideo && sm.diffuseVideo.readyState >= 2) {
      gl.bindTexture(gl.TEXTURE_2D, sm.diffuseTex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      const internalFmt = MESH_COLOR_SRGB ? (gl.SRGB8_ALPHA8 ?? gl.RGBA8) : gl.RGBA8;
      gl.texImage2D(gl.TEXTURE_2D, 0, internalFmt, gl.RGBA, gl.UNSIGNED_BYTE, sm.diffuseVideo);
    }
    if (sm.diffuseTex) {
      gl.bindTexture(gl.TEXTURE_2D, sm.diffuseTex);
      gl.uniform1i(this.uMesh.hasDiffuse, 1);
    } else {
      gl.bindTexture(gl.TEXTURE_2D, this.fallbackTex);
      gl.uniform1i(this.uMesh.hasDiffuse, 0);
    }
    if (this.uMesh.baseColorFactor) {
      const b = sm.baseColorFactor;
      gl.uniform4f(this.uMesh.baseColorFactor, b[0], b[1], b[2], b[3]);
    }

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, sm.normalTex ?? this.meshFlatNormalTex);
    if (this.uMesh.hasNormalMap) gl.uniform1i(this.uMesh.hasNormalMap, sm.normalTex ? 1 : 0);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, sm.metallicRoughnessTex ?? this.meshDefaultMRTex);
    if (this.uMesh.hasMetallicRoughness) {
      gl.uniform1i(this.uMesh.hasMetallicRoughness, sm.metallicRoughnessTex ? 1 : 0);
    }
    if (this.uMesh.normalScale) gl.uniform1f(this.uMesh.normalScale, sm.normalScale);
    if (this.uMesh.roughnessFactor) gl.uniform1f(this.uMesh.roughnessFactor, sm.roughnessFactor);
    if (this.uMesh.metallicFactor) gl.uniform1f(this.uMesh.metallicFactor, sm.metallicFactor);
    if (this.uMesh.alphaMode) gl.uniform1i(this.uMesh.alphaMode, sm.alphaMode);
    if (this.uMesh.alphaCutoff) gl.uniform1f(this.uMesh.alphaCutoff, sm.alphaCutoff);
    if (this.uMesh.diffuseVideoBillboard) {
      const isBillboard = sm.diffuseVideo !== null ? 1 : 0;
      gl.uniform1i(this.uMesh.diffuseVideoBillboard, isBillboard);
    }
    if (this.uMesh.videoPacked) {
      gl.uniform1i(this.uMesh.videoPacked, sm.diffuseVideo !== null && VIDEO_PACKED_ALPHA ? 1 : 0);
    }

    gl.activeTexture(gl.TEXTURE0);

    if (sm.alphaMode === 2) {
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    } else {
      gl.disable(gl.BLEND);
    }
  }

  private drawMeshTriangles(gl: WebGL2RenderingContext, sm: SubMesh): void {
    if (sm.doubleSided) gl.disable(gl.CULL_FACE);
    gl.drawElements(gl.TRIANGLES, sm.indexCount, sm.indexType, 0);
    if (sm.doubleSided) {
      gl.enable(gl.CULL_FACE);
      gl.cullFace(gl.BACK);
    }
  }

  private renderMeshes(lights: PointLight[], nowSec: number, list: MeshInstance[] = this.meshInstances, cull = true): void {
    if (list.length === 0) return;
    const gl = this.gl;
    this.lastRealVoxelBodyDrawCalls = 0;

    gl.useProgram(this.meshProg);
    gl.uniformMatrix4fv(this.uMesh.viewProj, false, this.viewProj);
    gl.uniform1i(this.uMesh.diffuse, 0);
    if (this.uMesh.normalMap) gl.uniform1i(this.uMesh.normalMap, 1);
    if (this.uMesh.metallicRoughness) gl.uniform1i(this.uMesh.metallicRoughness, 2);
    gl.vertexAttrib4f(5, 1, 1, 1, 1);

    // 8 ближайших ламп (помощник сортирует по distance к камере).
    const numL = this.prepareLightsClosestToCamera(lights);
    gl.uniform1i(this.uMesh.numLights, numL);
    gl.uniform4fv(this.uMesh.lights, this._lpos);
    gl.uniform3fv(this.uMesh.lightColors, this._lcol);
    if (this.uMesh.lightLodDist) gl.uniform1f(this.uMesh.lightLodDist, this._lightLodDist);
    if (this.uMesh.cameraPos) {
      gl.uniform3f(this.uMesh.cameraPos, this.eye[0], this.eye[1], this.eye[2]);
    }
    this.bindFogUniforms(gl, {
      color: this.uMesh.fogColor,
      density: this.uMesh.fogDensity,
      height: this.uMesh.fogHeight,
      falloff: this.uMesh.fogHeightFalloff,
      exposure: this.uMesh.exposure,
    });

    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.depthMask(true);
    // Раньше SRC_ALPHA для альфы diffuse — у гибридного тела давало «дымку» и казалось,
    // что слой «отрастает». Cutout через discard + BLEND оставляем выключенным.
    gl.disable(gl.BLEND);
    for (const inst of list) {
      if (inst.hidden) continue;
      if (this.uMesh.bodyDeadVolCount) gl.uniform1i(this.uMesh.bodyDeadVolCount, 0);
      // weapons: свечение инстанса (0 — как раньше).
      if (this.uMesh.emissive) {
        const em = inst.emissive;
        gl.uniform3f(this.uMesh.emissive, em ? em[0] : 0, em ? em[1] : 0, em ? em[2] : 0);
      }
      this.syncBodyVoxDamageClock(inst, nowSec);
      // Instance model matrix: T(x,y,z) · Rz(yaw + yawOffset) · Rx(+90°) · S(scale).
      // После Mixamo Armature (Rx(90°)+scale 0.01) с компенсирующими IBM (scale=100)
      // skinned меш рендерится в Y-up frame (height по Y, ~1.89 единиц = метры).
      // Наш мир — Z-up; Rx(+90°) переводит mesh Y → world Z.
      // yawOffset компенсирует mesh-конвенцию (например новый soldier_animated.glb
      // имеет forward = -Z вместо +X, для него yawOffset = -π/2).
      const finalYaw = inst.yaw + inst.yawOffset;
      const c = Math.cos(finalYaw), s = Math.sin(finalYaw);
      const sc = inst.scale;
      const im = this._scratchModelMat;
      im[0]  = sc * c;   im[1]  = sc * s;   im[2]  = 0;        im[3]  = 0;
      im[4]  = 0;        im[5]  = 0;        im[6]  = sc;       im[7]  = 0;
      im[8]  = sc * s;   im[9]  = -sc * c;  im[10] = 0;        im[11] = 0;
      im[12] = inst.x;   im[13] = inst.y;   im[14] = inst.z;   im[15] = 1;
      if (inst.worldMatrix) im.set(inst.worldMatrix); // weapons

      // 1) Мясной каркас (soldier_vox / innerMesh): скин + discard по серверной битмаске (режим 4).
      if (inst.innerMesh && inst.innerBoneMatrices.length > 0) {
        const carveInner = inst.innerBodyVoxShaderCarve !== false;
        const skipInnerCarcass = carveInner && bodyVoxTooSparseToRenderInner(inst.bodyVoxAlive);

        const voxPre = inst.bodyVoxAlive;
        const hasBitsPre = voxPre !== null && voxPre.length === BODY_VOX_GRID_BYTES;
        const innerBitsOk = hasBitsPre && !inst.realVoxelBody && carveInner;
        if (innerBitsOk && voxPre) {
          const dv = fillDeadBodyVoxWorldSpheres(
            voxPre,
            inst,
            im,
            this._proxySkinM,
            this._proxyWp,
            this._bodyDeadVolBuf,
            { x: this.eye[0]!, y: this.eye[1]!, z: this.eye[2]! },
            inst.bodyVoxShell && inst.bodyVoxShell.length === BODY_VOX_GRID_BYTES
              ? inst.bodyVoxShell
              : null,
          );
          if (this.uMesh.bodyDeadVolCount) gl.uniform1i(this.uMesh.bodyDeadVolCount, dv);
          if (this.uMesh.bodyDeadVol && dv > 0) {
            gl.uniform4fv(this.uMesh.bodyDeadVol, this._bodyDeadVolBuf.subarray(0, dv * 4));
          }
        }

        if (!skipInnerCarcass) {
        if (this.uMesh.useFlyDetach) gl.uniform1i(this.uMesh.useFlyDetach, 0);
        this.bindActorSkinBones(inst, inst.innerBoneMatrices, "inner");
        // Один GLB часто содержит и гладкий «output», и воксель. Раньше опирались только на
        // bodyLayer===inner — при сбое тегов получался либо дубль гладкий+воксель, либо «только автомат»
        // (воксель отброшен как layer:null при innerHasTaggedCarcass). Сверяем имя ноды и не дублируем weapon.
        const inner = inst.innerMesh;
        const voxelLike = innerMeshHasVoxelLike(inner);
        const innerNW = inst.innerNodeWorldMatrices;
        const useInnerFk = innerNW.length >= inner.nodes.length * 16;
        for (let si = 0; si < inner.subMeshes.length; si++) {
          const sm = inner.subMeshes[si]!;
          if (innerSubMeshIsOuterShell(inner, sm)) continue;
          if (innerSubMeshIsDuplicateWeapon(inner, sm)) continue;
          if (inst.innerHiddenNodes && !sm.hasSkin && inst.innerHiddenNodes.has(sm.nodeIdx)) continue; // weapons
          if (voxelLike && !innerSubMeshIsVoxelCarcass(inner, sm)) continue;
          if (inst.fpViewOnly && sm.hasSkin) continue;
          if (inst.realVoxelBody && inst.realVoxelHiddenSub && inst.realVoxelHiddenSub[si] !== 0) {
            continue;
          }
          let modelMat: Float32Array;
          if (sm.hasSkin) {
            modelMat = im;
          } else {
            const nwOff = sm.nodeIdx * 16;
            const nodeMat = useInnerFk
              ? innerNW.subarray(nwOff, nwOff + 16)
              : inst.nodeWorldMatrices.subarray(nwOff, nwOff + 16);
            modelMat = this._scratchModelMatNode;
            this.modelMatNoSkin(modelMat, im, nodeMat, sm);
          }
          gl.uniformMatrix4fv(this.uMesh.model, false, modelMat);
          gl.uniform1i(this.uMesh.useSkin, sm.hasSkin ? 1 : 0);

          const voxBm = inst.bodyVoxAlive;
          const hasBitsInner = voxBm !== null && voxBm.length === BODY_VOX_GRID_BYTES;
          const bitsOkInner = hasBitsInner && !inst.realVoxelBody;
          const useVoxShaderCarve = bitsOkInner && carveInner;

          if (sm.hasSkin) {
            if (useVoxShaderCarve) {
              this.applyBodyVoxDamageMode4Uniforms(inst, voxBm!, false, nowSec);
            } else if (bitsOkInner && !carveInner) {
              gl.uniform1i(this.uMesh.bodyDamageMode, 0);
              if (this.uMesh.bodyVoxBorderDent) gl.uniform1f(this.uMesh.bodyVoxBorderDent, 0);
              if (this.uMesh.bodyVoxDebris) gl.uniform1i(this.uMesh.bodyVoxDebris, 0);
            } else {
              gl.uniform1i(this.uMesh.bodyDamageMode, 3);
              if (this.uMesh.bodyVoxBorderDent) gl.uniform1f(this.uMesh.bodyVoxBorderDent, 0);
              if (this.uMesh.bodyVoxDebris) gl.uniform1i(this.uMesh.bodyVoxDebris, 0);
              if (this.uMesh.hitCount) gl.uniform1i(this.uMesh.hitCount, inst.bodyHitCount | 0);
              if (this.uMesh.hits) gl.uniform4fv(this.uMesh.hits, inst.bodyHitsPacked);
            }
            if (this.uMesh.actorXY) gl.uniform2f(this.uMesh.actorXY, inst.x, inst.y);
            if (this.uMesh.actorYaw) gl.uniform1f(this.uMesh.actorYaw, inst.yaw);
            if (this.uMesh.actorFootZ) gl.uniform1f(this.uMesh.actorFootZ, inst.z);
          } else if (useVoxShaderCarve) {
            this.applyBodyVoxDamageMode4Uniforms(inst, voxBm!, false, nowSec);
            if (this.uMesh.actorXY) gl.uniform2f(this.uMesh.actorXY, inst.x, inst.y);
            if (this.uMesh.actorYaw) gl.uniform1f(this.uMesh.actorYaw, inst.yaw);
            if (this.uMesh.actorFootZ) gl.uniform1f(this.uMesh.actorFootZ, inst.z);
          } else if (bitsOkInner && !carveInner) {
            gl.uniform1i(this.uMesh.bodyDamageMode, 0);
            if (this.uMesh.bodyVoxBorderDent) gl.uniform1f(this.uMesh.bodyVoxBorderDent, 0);
            if (this.uMesh.bodyVoxDebris) gl.uniform1i(this.uMesh.bodyVoxDebris, 0);
            if (this.uMesh.actorXY) gl.uniform2f(this.uMesh.actorXY, inst.x, inst.y);
            if (this.uMesh.actorYaw) gl.uniform1f(this.uMesh.actorYaw, inst.yaw);
            if (this.uMesh.actorFootZ) gl.uniform1f(this.uMesh.actorFootZ, inst.z);
          } else {
            gl.uniform1i(this.uMesh.bodyDamageMode, 0);
            if (this.uMesh.bodyVoxBorderDent) gl.uniform1f(this.uMesh.bodyVoxBorderDent, 0);
            if (this.uMesh.bodyVoxDebris) gl.uniform1i(this.uMesh.bodyVoxDebris, 0);
          }
          this.bindMeshMaterial(sm);
          if (this.uMesh.meatTint) {
            let mt = 0;
            if (innerSubMeshIsVoxelCarcass(inner, sm)) {
              if (inst.realVoxelBody) mt = 0.88;
              else if (bitsOkInner && !carveInner) {
                // Гибрид без discard в шейдере (NPC): без diffuse остаётся серый — красим как мясо.
                mt = 0.93;
              }
            }
            gl.uniform1f(this.uMesh.meatTint, mt);
          }
          gl.bindVertexArray(sm.vao);
          this.drawMeshTriangles(gl, sm);
          if (inst.realVoxelBody && !sm.hasSkin) this.lastRealVoxelBodyDrawCalls++;
        }
        if (inst.realVoxelBody && inst.realVoxelFlying.length > 0) {
          this.drawRealVoxelFlying(inst, gl);
        }
        }
      }

      if (this.uMesh.bodyVoxDebris) gl.uniform1i(this.uMesh.bodyVoxDebris, 0);
      if (this.uMesh.bodyVoxBorderDent) gl.uniform1f(this.uMesh.bodyVoxBorderDent, 0);

      const canOuterBodyVox =
        !!inst.innerMesh &&
        inst.innerBoneMatrices.length > 0 &&
        inst.bodyVoxAlive !== null &&
        inst.bodyVoxAlive.length === BODY_VOX_GRID_BYTES &&
        !inst.realVoxelBody;

      // 2) Основной GLB: гладкий outer с теми же дырами по bodyVox (режим 4), ниже — красный воксель inner.
      //    Без hybrid inner — прежний outer по сферам uHits (режим 1).
      if (this.uMesh.bodyOuterShell) gl.uniform1i(this.uMesh.bodyOuterShell, 0);
      this.bindActorSkinBones(inst, inst.boneMatrices, "outer");

      // Frustum-куллинг не-skinned сабмешей: 302 сабмеша особняка рисовались
      // из любой точки карты без отсева. AABB кэшируется в мире и
      // инвалидируется только при пересчёте позы (двери и т.п.).
      if (inst.worldAabbDirty || !inst.subMeshWorldAABB) {
        this.updateSubMeshWorldAABBs(inst, im);
      }
      const smAABB = inst.subMeshWorldAABB;

      for (let smIdx = 0; smIdx < inst.mesh.subMeshes.length; smIdx++) {
        const sm = inst.mesh.subMeshes[smIdx];
        if (inst.fpViewOnly && sm.hasSkin) continue;
        if (sm.bodyLayer === "inner" && inst.innerMesh) continue;
        if (inst.hiddenNodes && !sm.hasSkin && inst.hiddenNodes.has(sm.nodeIdx)) continue; // weapons
        if (cull && smAABB && !sm.hasSkin && !sm.ghostKind && !sm.diffuseVideo) {
          const o = smIdx * 6;
          if (!this.aabbInFrustum(smAABB[o], smAABB[o + 1], smAABB[o + 2],
                                  smAABB[o + 3], smAABB[o + 4], smAABB[o + 5])) {
            continue;
          }
        }

        if (canOuterBodyVox && sm.bodyLayer === "outer") {
          let modelMat: Float32Array;
          if (sm.hasSkin) {
            modelMat = im;
          } else {
            const nwOff = sm.nodeIdx * 16;
            const nodeMat = inst.nodeWorldMatrices.subarray(nwOff, nwOff + 16);
            modelMat = this._scratchModelMatNode;
            this.modelMatNoSkin(modelMat, im, nodeMat, sm);
          }
          gl.uniformMatrix4fv(this.uMesh.model, false, modelMat);
          gl.uniform1i(this.uMesh.useSkin, sm.hasSkin ? 1 : 0);
          const outerBits =
            inst.bodyVoxShell && inst.bodyVoxShell.length === BODY_VOX_GRID_BYTES
              ? inst.bodyVoxShell
              : inst.bodyVoxAlive!;
          this.applyBodyVoxDamageMode4Uniforms(inst, outerBits, true, nowSec);
          if (this.uMesh.actorXY) gl.uniform2f(this.uMesh.actorXY, inst.x, inst.y);
          if (this.uMesh.actorYaw) gl.uniform1f(this.uMesh.actorYaw, inst.yaw);
          if (this.uMesh.actorFootZ) gl.uniform1f(this.uMesh.actorFootZ, inst.z);
          this.bindMeshMaterial(sm);
          if (this.uMesh.meatTint) gl.uniform1f(this.uMesh.meatTint, 0);
          gl.bindVertexArray(sm.vao);
          this.drawMeshTriangles(gl, sm);
          if (this.uMesh.bodyOuterShell) gl.uniform1i(this.uMesh.bodyOuterShell, 0);
          continue;
        }

        if (inst.innerMesh && sm.hasSkin && sm.bodyLayer !== "outer") continue;

        let modelMat: Float32Array;
        if (sm.hasSkin) {
          modelMat = im;
        } else {
          const nwOff = sm.nodeIdx * 16;
          const nodeMat = inst.nodeWorldMatrices.subarray(nwOff, nwOff + 16);
          modelMat = this._scratchModelMatNode;
          this.modelMatNoSkin(modelMat, im, nodeMat, sm);
        }
        gl.uniformMatrix4fv(this.uMesh.model, false, modelMat);
        gl.uniform1i(this.uMesh.useSkin, sm.hasSkin ? 1 : 0);

        const layer = sm.bodyLayer;
        if (layer === "outer") {
          gl.uniform1i(this.uMesh.bodyDamageMode, 1);
          if (this.uMesh.hitCount) gl.uniform1i(this.uMesh.hitCount, inst.bodyHitCount | 0);
          if (this.uMesh.hits) gl.uniform4fv(this.uMesh.hits, inst.bodyHitsPacked);
          if (this.uMesh.actorXY) gl.uniform2f(this.uMesh.actorXY, inst.x, inst.y);
          if (this.uMesh.actorYaw) gl.uniform1f(this.uMesh.actorYaw, inst.yaw);
          if (this.uMesh.actorFootZ) gl.uniform1f(this.uMesh.actorFootZ, inst.z);
        } else {
          gl.uniform1i(this.uMesh.bodyDamageMode, 0);
        }

        this.bindMeshMaterial(sm);
        if (this.uMesh.meatTint) gl.uniform1f(this.uMesh.meatTint, 0);

        gl.bindVertexArray(sm.vao);
        this.drawMeshTriangles(gl, sm);
      }
    }

    gl.disable(gl.BLEND);
    gl.depthMask(true);
    gl.bindVertexArray(null);
    gl.disable(gl.CULL_FACE);
    if (this.uMesh.emissive) gl.uniform3f(this.uMesh.emissive, 0, 0, 0);
  }

  // ─── weapons: вид от первого лица, приближение, трассеры оружия ───────

  /** Текущий вертикальный FOV (рад); снайперка сужает. */
  private fovY = FOV_Y;
  /** Инстансы вида от первого лица (оружие + руки), рисуются поверх мира. */
  private viewModelInstances: MeshInstance[] = [];
  private _vmProj: Mat4 = mat4Identity();
  private _vmViewProj: Mat4 = mat4Identity();
  private _vmLights: PointLight[] = [];

  /** Вертикальный FOV в радианах (по умолчанию 75°). */
  setFovY(rad: number): void {
    this.fovY = Math.min(Math.max(rad, 0.15), 2.0);
  }

  getFovY(): number {
    return this.fovY;
  }

  /** Вертикальный FOV кадра: заданный, но не шире MAX_FOV_X по горизонтали. */
  private effectiveFovY(): number {
    const aspect = this.canvas.width / Math.max(1, this.canvas.height);
    return Math.min(this.fovY, 2 * Math.atan(Math.tan(MAX_FOV_X / 2) / aspect));
  }

  /** Список инстансов вида от первого лица (пустой — прохода нет). */
  setViewModelInstances(list: MeshInstance[]): void {
    this.viewModelInstances = list;
  }

  /** Камера мира: глаз, вперёд, вверх (после render()). */
  getCameraBasis(): { eye: [number, number, number]; fwd: [number, number, number]; up: [number, number, number] } {
    return {
      eye: [this.eye[0]!, this.eye[1]!, this.eye[2]!],
      fwd: [this.target[0]! - this.eye[0]!, this.target[1]! - this.eye[1]!, this.target[2]! - this.eye[2]!],
      up: [this.up[0]!, this.up[1]!, this.up[2]!],
    };
  }

  private renderViewModel(lights: PointLight[], nowSec: number): void {
    const gl = this.gl;
    gl.clear(gl.DEPTH_BUFFER_BIT);
    mat4Perspective(this._vmProj, this.effectiveFovY(), this.canvas.width / this.canvas.height, 0.01, 8.0);
    mat4Multiply(this._vmViewProj, this._vmProj, this.view);
    const saved = this.viewProj;
    this.viewProj = this._vmViewProj;
    // Подсветка оружия в руках: тёплый «фонарь» у плеча, достаёт только до
    // рук и ствола (мир уже нарисован) — в тёмных коридорах оружие читается.
    const ux = this.up[0]!, uy = this.up[1]!, uz = this.up[2]!;
    const vl = this._vmLights;
    vl.length = 0;
    vl.push({
      x: this.eye[0]! + ux * 0.25, y: this.eye[1]! + uy * 0.25, z: this.eye[2]! + uz * 0.25,
      range: 2.2, color: [1.5, 1.3, 1.05], noBulb: true,
    });
    for (const L of lights) vl.push(L);
    this._preparedNumLights = -1; // свой набор ламп (кэш кадра — от мира)
    this.renderMeshes(vl, nowSec, this.viewModelInstances, false);
    this._preparedNumLights = -1;
    this.viewProj = saved;
  }

  /** Кубики-«искры» трассера оружия вдоль отрезка. */
  private fillWeaponTracer(t: Tracer, o: TracerOpts, data: Float32Array, n: number): number {
    const age = (this.timeAcc - t.born) / o.life;
    if (age >= 1 || age < 0) return n;
    const fade = 1 - age;
    const dx = t.hx - t.ox, dy = t.hy - t.oy, dz = t.hz - t.oz;
    const len = Math.hypot(dx, dy, dz);
    if (len <= o.skip + 0.05) return n;
    const startF = o.skip / len;
    const segs = Math.min(400, Math.max(2, Math.floor((len - o.skip) * o.density)));
    const size = o.size * (0.55 + 0.45 * fade);
    for (let s = 0; s <= segs; s++) {
      if (n >= MAX_INSTANCES) break;
      const f = startF + ((1 - startF) * s) / segs;
      const idx = n * INSTANCE_FLOATS;
      data[idx] = t.ox + dx * f;
      data[idx + 1] = t.oy + dy * f;
      data[idx + 2] = t.oz + dz * f;
      data[idx + 3] = size;
      data[idx + 4] = o.color[0] * fade;
      data[idx + 5] = o.color[1] * fade;
      data[idx + 6] = o.color[2] * fade;
      data[idx + 7] = 1.0;
      data[idx + 8] = data[idx + 9] = data[idx + 10] = data[idx + 11] = 0;
      data[idx + 12] = data[idx + 13] = data[idx + 14] = data[idx + 15] = 0;
      n++;
    }
    return n;
  }

  /** Отдельный рендер pass для object chunks с corner UV lookup. */
  private renderObjectChunks(chunks: VoxelChunkClient[], lights: PointLight[], nowSec: number, chunkEpoch: number): void {
    const gl = this.gl;
    const objectChunks = this.getObjectChunksCached(chunks, chunkEpoch);
    if (objectChunks.length === 0) return;

    gl.useProgram(this.voxObjProg);
    gl.bindVertexArray(this.voxObjVAO);
    gl.uniformMatrix4fv(this.uVoxObj.viewProj, false, this.viewProj);
    gl.uniform1i(this.uVoxObj.lookupW, FACE_UV_LOOKUP_W);
    gl.uniform1i(this.uVoxObj.uvLookup, 0); // texture unit 0
    gl.uniform1i(this.uVoxObj.diffuse, 1);  // texture unit 1
    // Зерно теперь «едва заметное» (по фидбеку: квадраты читались инородно):
    // подложка почти полной яркости, точки — лёгкий шёпот поверх (см. alpha
    // в dustSurface.frag). Пиксель-арт вайб несут 360p + постеризация.
    if (this.uVoxObj.baseDim) {
      gl.uniform1f(this.uVoxObj.baseDim, this._dustEnabled ? 0.88 : 1.0);
    }
    // Lights — reuse уже подготовленные буферы если prepareLightsClosestToCamera
    // уже был вызван в основном pass'е. Для безопасности вызываем ещё раз —
    // цена O(N_lights) незаметна, если ламп мало.
    const numL = this.prepareLightsClosestToCamera(lights);
    gl.uniform1i(this.uVoxObj.numLights, numL);
    gl.uniform4fv(this.uVoxObj.lights, this._lpos);
    gl.uniform3fv(this.uVoxObj.lightColors, this._lcol);
    if (this.uVoxObj.cameraPos) {
      gl.uniform3f(this.uVoxObj.cameraPos, this.eye[0], this.eye[1], this.eye[2]);
    }
    if (this.uVoxObj.lightLodDist) {
      gl.uniform1f(this.uVoxObj.lightLodDist, this._lightLodDist);
    }
    this.bindFogUniforms(gl, {
      color: this.uVoxObj.fogColor,
      density: this.uVoxObj.fogDensity,
      height: this.uVoxObj.fogHeight,
      falloff: this.uVoxObj.fogHeightFalloff,
      exposure: this.uVoxObj.exposure,
    });

    // Ленивое переключение программ: статические faceUV-чанки почти все подряд,
    // раньше КАЖДЫЙ из них делал useProgram(face)+полную перезаливку юниформ и
    // обратный useProgram(voxObj) — 90-170 useProgram и сотни uniform-вызовов
    // за кадр у дома. Теперь программа и общие юниформы биндятся один раз.
    let faceProgActive = false;
    for (const c of objectChunks) {
      if (!this.chunkInFrustum(c)) { this.lastCulledCount++; continue; }
      this.lastDrawnChunks++;
      // Slot 0: uv-lookup; slot 1: diffuse атлас текущего объекта.
      const uvTex = this.faceUVTextures.get(c.id);
      if (!uvTex) continue;
      const diffuse = this.objectTextures.get(c.textureName);
      if (!diffuse) continue; // без diffuse-текстуры рисовать не из чего
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, uvTex);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, diffuse);
      // Заполняем instance data (8 floats per cube): worldPos+size, aliveIdx+pad+pad+pad
      const cs = c.cubeSize;
      const ox = c.ox, oy = c.oy, oz = c.oz;
      const meshCenterLocalX = c.sx * cs * 0.5;
      const meshCenterLocalY = c.sy * cs * 0.5;
      const pivX = ox + meshCenterLocalX;
      const pivY = oy + meshCenterLocalY;
      const pivZ = oz;
      const dyn = c.dynamic;
      let posX = 0, posY = 0, posZ = 0;
      let qW = 1, qX = 0, qY = 0, qZ = 0;
      if (dyn && !c.pose.resting) {
        const dt = Math.max(0, nowSec - c.pose.lastUpdateTime);
        posX = c.pose.pos[0] + c.pose.vel[0] * dt;
        posY = c.pose.pos[1] + c.pose.vel[1] * dt;
        posZ = c.pose.pos[2] + c.pose.vel[2] * dt;
        const q = quatIntegrate(c.pose.quat, c.pose.angVelVec, dt);
        qW = q[0]; qX = q[1]; qY = q[2]; qZ = q[3];
      } else if (dyn) {
        posX = c.pose.pos[0]; posY = c.pose.pos[1]; posZ = c.pose.pos[2];
        qW = c.pose.quat[0]; qX = c.pose.quat[1]; qY = c.pose.quat[2]; qZ = c.pose.quat[3];
      }
      // Статический чанк (архитектура уровня) — геометрия не меняется между
      // кадрами, поэтому инстансы живут в постоянных GPU-буферах, нарезанных
      // по ~LP_TILE_METERS. За кадр: frustum-тест AABB тайла + bind/draw.
      // Раньше здесь каждый кадр заново считался ВЕСЬ visibleList чанка
      // (для alone_in_the_dark — 245k кубов: три деления и восемь записей на
      // куб) и заливался через bufferSubData, а chunkInFrustum отсекал по
      // AABB всего особняка, то есть практически никогда.
      if (!dyn) {
        const cache = this.getObjectChunkTiles(c);
        // Пофасадная программа: инстанс = одна открытая грань, 6 вершин.
        // Кубовый бокс из 36 вершин остаётся только у dynamic-чанков ниже.
        if (!faceProgActive) {
          gl.useProgram(this.voxObjFaceProg);
          this.bindVoxObjFaceUniforms(lights);
          faceProgActive = true;
        }
        gl.uniform1i(this.uVoxObjFace.objectAlphaMode, c.alphaMode);
        gl.uniform1f(this.uVoxObjFace.objectAlphaCutoff, c.alphaCutoff > 0 ? c.alphaCutoff : 0.5);
        // Рисуем БЛИЖНИЕ тайлы первыми: проход фрагментно-ограничен (замер:
        // вершин стало в 5 раз меньше, а время упало лишь на треть — значит
        // платим за перерисовку). При порядке front-to-back early-Z режет
        // закрытые фрагменты ДО шейдинга. Сортируем в переиспользуемом
        // массиве — на кадр ~200 элементов, аллокаций нет.
        const order = this._objTileOrder;
        order.length = 0;
        const ex = this.eye[0], ey = this.eye[1], ez = this.eye[2];
        for (let ti = 0; ti < cache.tiles.length; ti++) {
          const t = cache.tiles[ti];
          if (t.faces <= 0) continue;
          if (!this.aabbInFrustum(t.minX, t.minY, t.minZ, t.maxX, t.maxY, t.maxZ)) {
            this.lastLpTilesCulled++;
            continue;
          }
          const cx = (t.minX + t.maxX) * 0.5 - ex;
          const cy = (t.minY + t.maxY) * 0.5 - ey;
          const cz = (t.minZ + t.maxZ) * 0.5 - ez;
          order.push({ t, d: cx * cx + cy * cy + cz * cz });
        }
        order.sort((a, b) => a.d - b.d);
        for (let oi = 0; oi < order.length; oi++) {
          const t = order[oi].t;
          gl.bindVertexArray(t.faceVao);
          gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, t.faces);
          this.lastLpTilesDrawn++;
          this.lastLpCubesDrawn += t.faces;
        }
        continue;
      }

      // Динамический чанк — программа voxObjProg и её юниформы.
      if (faceProgActive) {
        gl.useProgram(this.voxObjProg);
        gl.bindVertexArray(this.voxObjVAO);
        faceProgActive = false;
      }
      gl.uniform1i(this.uVoxObj.objectAlphaMode, c.alphaMode);
      gl.uniform1f(this.uVoxObj.objectAlphaCutoff, c.alphaCutoff > 0 ? c.alphaCutoff : 0.5);
      // Передаём quat в шейдер — шейдер сам поворачивает aPos и aNormal,
      // т.е. каждый куб поворачивается ВМЕСТЕ с rigid body, а не остаётся
      // axis-aligned.
      gl.uniform4f(this.uVoxObj.chunkQuat, qW, qX, qY, qZ);

      const data = this.voxObjInstanceData;
      const flush = (count: number): void => {
        if (count === 0) return;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.voxObjInstanceBuf);
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, count * 8);
        gl.drawArraysInstanced(gl.TRIANGLES, 0, 36, count);
      };
      let n = 0;
      const list = c.visibleList;
      const sxsy = c.sx * c.sy;
      const aliveIdxLut = c.aliveIdx;
      const useRotation = dyn && (qW < 0.99999 || qX !== 0 || qY !== 0 || qZ !== 0);
      for (let li = 0; li < list.length; li++) {
        if (n >= MAX_INSTANCES) {
          flush(n);
          n = 0;
        }
        const i = list[li];
        const bx = i % c.sx;
        const by = Math.floor(i / c.sx) % c.sy;
        const bz = Math.floor(i / sxsy);
        const localCx = (bx + 0.5) * cs;
        const localCy = (by + 0.5) * cs;
        const localCz = (bz + 0.5) * cs;
        const idx = n * 8;
        if (useRotation) {
          const lx = localCx - meshCenterLocalX;
          const ly = localCy - meshCenterLocalY;
          const lz = localCz;
          // quat-rotate (W,X,Y,Z) of (lx,ly,lz)
          const tx = 2 * (qY * lz - qZ * ly);
          const ty = 2 * (qZ * lx - qX * lz);
          const tz = 2 * (qX * ly - qY * lx);
          const rx = lx + qW * tx + (qY * tz - qZ * ty);
          const ry = ly + qW * ty + (qZ * tx - qX * tz);
          const rz = lz + qW * tz + (qX * ty - qY * tx);
          data[idx]     = pivX + rx + posX;
          data[idx + 1] = pivY + ry + posY;
          data[idx + 2] = pivZ + rz + posZ;
        } else {
          data[idx]     = ox + localCx + posX;
          data[idx + 1] = oy + localCy + posY;
          data[idx + 2] = oz + localCz + posZ;
        }
        data[idx + 3] = cs;
        // aliveIdx — порядковый номер в патч-атласе. Шейдер вычислит позицию
        // патча: col = aliveIdx % ppr, row = aliveIdx / ppr.
        data[idx + 4] = aliveIdxLut ? aliveIdxLut[i] : 0;
        data[idx + 5] = 0;
        data[idx + 6] = 0;
        data[idx + 7] = 0;
        n++;
      }
      flush(n);
    }
    // Вернуть состояние, которое ожидает остальной код кадра (старое
    // поведение: после каждого статик-чанка активной оставалась voxObjProg).
    if (faceProgActive) {
      gl.useProgram(this.voxObjProg);
      gl.bindVertexArray(this.voxObjVAO);
    }
  }

  private makeQuadVAO(): WebGLVertexArrayObject {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("createVertexArray failed");
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer();
    if (!buf) throw new Error("createBuffer failed");
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,  1, -1,  -1, 1,
      -1,  1,  1, -1,   1, 1,
    ]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    return vao;
  }

  private makeVoxObjVAO(): [WebGLVertexArrayObject, WebGLBuffer] {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("createVertexArray failed");
    gl.bindVertexArray(vao);

    // Cube geometry — same as main voxel pass (36 verts, pos+normal stride 24)
    const verts = buildCubeMesh();
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 24, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, 12);

    // Per-instance: vec4 worldPos+size, vec4 [aliveIdx, _, _, _] = 8 floats stride.
    // (aliveIdx как float — диапазон до 16 млн точно укладывается в mantissa
    // float32 без потерь; для наших object chunks ~10K alive это сильно с запасом.)
    const ibo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ARRAY_BUFFER, MAX_INSTANCES * 8 * 4, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, 32, 0);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, 32, 16);
    gl.vertexAttribDivisor(3, 1);

    gl.bindVertexArray(null);
    return [vao, ibo];
  }

  /** Загрузить per-face corner UVs как RG16UI lookup-текстуру.
   *
   *  faceUVs layout (см. voxelize/main.go):
   *    alive_count × 6 граней × 4 угла × 4 байта (u16 u, u16 v, little-endian).
   *
   *  Texture layout (1D пакуем в 2D шириной FACE_UV_LOOKUP_W):
   *    texelIdx = aliveIdx*24 + faceIdx*4 + cornerIdx
   *    row = texelIdx / W, col = texelIdx % W
   *    каждый texel = (u_uint16, v_uint16) ∈ [0, 65535]
   *
   *  В шейдере: vec2 uv = vec2(texelFetch(uUVLookup, ivec2(col,row), 0).rg) / 65535.0.
   *  Для V-flip (GLB convention) уже сделано в voxelize, поэтому клиент сэмплит
   *  diffuse-текстуру напрямую без преобразований.
   */
  uploadFaceUVs(chunkId: number, faceUVs: Uint8Array, aliveCount: number): void {
    const gl = this.gl;
    const totalTexels = aliveCount * 24;
    const W = FACE_UV_LOOKUP_W;
    const H = Math.max(1, Math.ceil(totalTexels / W));
    // RG16UI ожидает Uint16Array (2 канала на texel). Переупаковываем bytes → uint16.
    // faceUVs уже в LE формате, можно напрямую ремаппить через DataView для надёжности.
    const grid = new Uint16Array(W * H * 2);
    const dv = new DataView(faceUVs.buffer, faceUVs.byteOffset, faceUVs.byteLength);
    for (let i = 0; i < totalTexels; i++) {
      grid[i * 2]     = dv.getUint16(i * 4, true);
      grid[i * 2 + 1] = dv.getUint16(i * 4 + 2, true);
    }
    let tex = this.faceUVTextures.get(chunkId);
    if (!tex) {
      const t = gl.createTexture();
      if (!t) throw new Error("createTexture failed");
      tex = t;
      this.faceUVTextures.set(chunkId, tex);
    }
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG16UI, W, H, 0, gl.RG_INTEGER, gl.UNSIGNED_SHORT, grid);
    // Integer-самплеры обязаны быть NEAREST — фильтрация UV не имеет смысла,
    // мы хотим читать ТОЧНО записанные углы.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  /** Получить uv-lookup текстуру по ID чанка. */
  getFaceUVTexture(chunkId: number): WebGLTexture | undefined {
    return this.faceUVTextures.get(chunkId);
  }

  /** «Точечный стиль» вкл/выкл (mobile-lite отключает оба dust-пасса). */
  setDustStyle(on: boolean): void {
    if (on === this._dustEnabled) return;
    this._dustEnabled = on;
    // Покубовые буферы пыли строятся только когда она включена (на слабых
    // машинах их вообще нет — экономия VRAM). При включении на лету тайлы
    // надо пересобрать, иначе рисовать пыль будет не из чего.
    if (on) {
      for (const entry of this._objTileCache.values()) this.disposeObjTiles(entry.tiles);
      this._objTileCache.clear();
    }
  }

  private makeDustBurstVAO(): [WebGLVertexArrayObject, WebGLBuffer] {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("createVertexArray failed");
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer();
    if (!buf) throw new Error("createBuffer failed");
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    // born = -1e9 у неинициализированных точек → age >> life → size 0.
    const init = new Float32Array(DUST_BURST_CAP * DUST_FLOATS);
    for (let i = 0; i < DUST_BURST_CAP; i++) init[i * DUST_FLOATS + 10] = -1e9;
    gl.bufferData(gl.ARRAY_BUFFER, init, gl.DYNAMIC_DRAW);
    const stride = DUST_FLOATS * 4;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, stride, 12);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, stride, 24);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 2, gl.FLOAT, false, stride, 40);
    gl.bindVertexArray(null);
    return [vao, buf];
  }

  /** Песчаное осыпание: count зёрен из (x,y,z) с импульсом (vx,vy,vz).
   *  Референс — Houdini sand sim: зёрна преимущественно СЫПЛЮТСЯ ВНИЗ с
   *  небольшим срывом по импульсу, а хвост зёрен «дотекает» из точки с
   *  задержкой (струйки из кратера — born в будущем, шейдер поддерживает).
   *  Копится в батч, заливается капованным аплоадом за кадр. */
  spawnDustBurst(
    x: number, y: number, z: number,
    vx: number, vy: number, vz: number,
    color: [number, number, number], floorZ: number, count: number,
  ): void {
    if (!this._dustEnabled) return;
    const n = Math.min(count, DUST_BURST_MAX_PER_CALL);
    if (n <= 0) return;
    const p = this.dustPending;
    // Кап очереди: при огромном обвале не раздуваем массив бесконечно.
    if (p.length >= DUST_BURST_CAP * DUST_FLOATS) return;
    const now = this.timeAcc;
    for (let i = 0; i < n; i++) {
      const ang = Math.random() * Math.PI * 2;
      const spread = 0.25 + Math.random() * 0.75;
      const shade = 0.8 + Math.random() * 0.4; // зёрна чуть разного тона
      // ~40% зёрен — «струйка»: рождаются с задержкой до 0.9 с.
      const trickle = i % 5 >= 3 ? Math.random() * 0.9 : 0;
      p.push(
        x + (Math.random() - 0.5) * 0.2,
        y + (Math.random() - 0.5) * 0.2,
        z + (Math.random() - 0.5) * 0.2,
        vx * 0.45 + Math.cos(ang) * spread,
        vy * 0.45 + Math.sin(ang) * spread,
        // Песок сыплется: вертикаль в основном вниз, лишь чуть подброса.
        vz * 0.2 - 0.3 - Math.random() * 1.2,
        color[0] * shade,
        color[1] * shade,
        color[2] * shade,
        floorZ,
        now + trickle,
        2.2 + Math.random() * 1.4, // долго лежим в куче
      );
    }
  }

  /** Залить накопленные зёрна в ring-buffer (максимум DUST_FLUSH_PER_FRAME
   *  за кадр — гигантский обвал размазывается на несколько кадров). */
  private flushPendingDust(): void {
    const p = this.dustPending;
    if (p.length === 0) return;
    const gl = this.gl;
    const n = Math.min(p.length / DUST_FLOATS, DUST_FLUSH_PER_FRAME);
    const data = new Float32Array(p.slice(0, n * DUST_FLOATS));
    p.splice(0, n * DUST_FLOATS);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.dustBurstBuf);
    const head = this.dustBurstHead;
    const tailRoom = DUST_BURST_CAP - head;
    if (n <= tailRoom) {
      gl.bufferSubData(gl.ARRAY_BUFFER, head * DUST_FLOATS * 4, data, 0, n * DUST_FLOATS);
    } else {
      gl.bufferSubData(gl.ARRAY_BUFFER, head * DUST_FLOATS * 4, data, 0, tailRoom * DUST_FLOATS);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, tailRoom * DUST_FLOATS, (n - tailRoom) * DUST_FLOATS);
    }
    this.dustBurstHead = (head + n) % DUST_BURST_CAP;
  }

  /** Пыль от разрушения: один drawArrays по всему ring-buffer'у. Мёртвые
   *  точки схлопываются в size=0 в вершинном шейдере. Рисовать ПОСЛЕ всей
   *  опаковой геометрии (depth test on, write off, additive). */
  renderDustBursts(): void {
    if (!this._dustEnabled) return;
    const gl = this.gl;
    this.flushPendingDust();
    gl.useProgram(this.dustBurstProg);
    gl.bindVertexArray(this.dustBurstVAO);
    gl.uniformMatrix4fv(this.uDustBurst.viewProj, false, this.viewProj);
    gl.uniform3f(this.uDustBurst.cameraPos, this.eye[0], this.eye[1], this.eye[2]);
    gl.uniform1f(this.uDustBurst.time, this.timeAcc);
    // Осыпающиеся зёрна — ТОГО ЖЕ калибра, что точки поверхности
    // (dustSurface): мир состоит из точек, и рассыпается на такие же.
    gl.uniform1f(this.uDustBurst.pointScale, this.canvas.height * 0.05);
    // Песок — непрозрачная крошка, НЕ светящийся дым: обычный альфа-блендинг.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);
    gl.drawArrays(gl.POINTS, 0, DUST_BURST_CAP);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  }

  /** Пыль на стенах: мерцающие точки на гранях vox-чанков (см. dustSurface.vert).
   *  Инстанс-буферы кэшируются per-chunk и пересобираются только когда чанк
   *  реально менялся (visibleEpoch). Рисовать после опаковой геометрии. */
  renderSurfaceDust(chunks: VoxelChunkClient[], chunkEpoch: number): void {
    if (!this._dustEnabled) return;
    const objectChunks = this.getObjectChunksCached(chunks, chunkEpoch);
    if (objectChunks.length === 0) return;
    const gl = this.gl;
    gl.useProgram(this.dustSurfProg);
    gl.uniformMatrix4fv(this.uDustSurf.viewProj, false, this.viewProj);
    gl.uniform1i(this.uDustSurf.lookupW, FACE_UV_LOOKUP_W);
    gl.uniform1i(this.uDustSurf.uvLookup, 0);
    gl.uniform1i(this.uDustSurf.diffuse, 1);
    gl.uniform3f(this.uDustSurf.cameraPos, this.eye[0], this.eye[1], this.eye[2]);
    gl.uniform1f(this.uDustSurf.time, this.timeAcc);
    // Крупно: точка на кубе 0.25 м с 2 м ≈ 26 px — поверхность из зёрен
    // (K=6, размер компенсирует плотность).
    gl.uniform1f(this.uDustSurf.pointScale, this.canvas.height * 0.21);
    this.bindFogUniforms(gl, {
      color: this.uDustSurf.fogColor,
      density: this.uDustSurf.fogDensity,
      height: this.uDustSurf.fogHeight,
      falloff: this.uDustSurf.fogHeightFalloff,
    });
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(false);

    for (const c of objectChunks) {
      // Пыль только на статике: dynamic (rigid-body) кубы вращаются pose'ой,
      // а кэш держит мировые позиции — их пылить не будем.
      if (c.dynamic) continue;
      if (!this.chunkInFrustum(c)) continue;
      const uvTex = this.faceUVTextures.get(c.id);
      const diffuse = this.objectTextures.get(c.textureName);
      if (!uvTex || !diffuse) continue;

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, uvTex);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, diffuse);

      // Пыль рисуется по тем же тайлам и тем же GPU-буферам, что и voxObj-пасс
      // (раскладка инстанса совпадает) — отдельный кэш больше не нужен.
      // Главное: frustum-отсев на тайл, а не на весь чанк. При K точек на куб
      // непокрытая пыль стоила больше самого мира (245k кубов × 6 точек,
      // крупные alpha-blend спрайты — чистый overdraw за экраном).
      const cache = this.getObjectChunkTiles(c);
      for (const t of cache.tiles) {
        if (t.dustCubes <= 0 || !t.dustVao) continue;
        if (!this.aabbInFrustum(t.minX, t.minY, t.minZ, t.maxX, t.maxY, t.maxZ)) continue;
        gl.bindVertexArray(t.dustVao);
        gl.drawArraysInstanced(gl.POINTS, 0, DUST_POINTS_PER_CUBE, t.dustCubes);
      }
    }
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  }

  /** VAO пыли поверх инстанс-буфера тайла: только per-instance атрибуты
   *  (loc2 pos+size, loc3 aliveIdx) — геометрию точек шейдер строит из
   *  gl_VertexID. Раскладка та же, что в voxObj-тайле, поэтому буфер общий. */
  private makeDustTileVAO(buf: WebGLBuffer): WebGLVertexArrayObject {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("createVertexArray (dust tile) failed");
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, 32, 0);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, 32, 16);
    gl.vertexAttribDivisor(3, 1);
    gl.bindVertexArray(null);
    return vao;
  }

  private makeVoxelVAO(): [WebGLVertexArrayObject, WebGLBuffer] {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error("createVertexArray failed");
    gl.bindVertexArray(vao);

    // Геометрия юнит-куба: 6 граней × 2 треугольника × 3 вершины = 36 vert
    const verts = buildCubeMesh();
    const vbo = gl.createBuffer();
    if (!vbo) throw new Error("createBuffer failed");
    this.voxGeomVBO = vbo;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
    // pos + normal по 3 floats each, stride 24
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 24, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, 12);

    // Per-instance buffer:
    //   loc 2 vec4 — iPosSize (xyz pos, w cube size)
    //   loc 3 vec4 — iColor   (rgb fallback color, a texMode)
    //   loc 4 vec2 — iObjectUV (per-cube UV для object texture; иначе игнорируется)
    // Stride = 10 floats × 4 = 40 байт
    const ibo = gl.createBuffer();
    if (!ibo) throw new Error("createBuffer failed");
    gl.bindBuffer(gl.ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ARRAY_BUFFER, MAX_INSTANCES * INSTANCE_FLOATS * 4, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, INSTANCE_FLOATS * 4, 0);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, INSTANCE_FLOATS * 4, 16);
    gl.vertexAttribDivisor(3, 1);
    gl.enableVertexAttribArray(4);
    gl.vertexAttribPointer(4, 4, gl.FLOAT, false, INSTANCE_FLOATS * 4, 32);
    gl.vertexAttribDivisor(4, 1);
    gl.enableVertexAttribArray(5);
    gl.vertexAttribPointer(5, 4, gl.FLOAT, false, INSTANCE_FLOATS * 4, 48);
    gl.vertexAttribDivisor(5, 1);

    gl.bindVertexArray(null);
    return [vao, ibo];
  }
}

function buildCubeMesh(): Float32Array {
  // Куб от -0.5 до +0.5, ось Z — вверх.
  const faces: Array<{ n: [number, number, number]; v: [number, number, number][] }> = [
    { n: [+1, 0, 0], v: [[+0.5, -0.5, -0.5], [+0.5, +0.5, -0.5], [+0.5, +0.5, +0.5], [+0.5, -0.5, +0.5]] },
    { n: [-1, 0, 0], v: [[-0.5, +0.5, -0.5], [-0.5, -0.5, -0.5], [-0.5, -0.5, +0.5], [-0.5, +0.5, +0.5]] },
    { n: [0, +1, 0], v: [[+0.5, +0.5, -0.5], [-0.5, +0.5, -0.5], [-0.5, +0.5, +0.5], [+0.5, +0.5, +0.5]] },
    { n: [0, -1, 0], v: [[-0.5, -0.5, -0.5], [+0.5, -0.5, -0.5], [+0.5, -0.5, +0.5], [-0.5, -0.5, +0.5]] },
    { n: [0, 0, +1], v: [[-0.5, -0.5, +0.5], [+0.5, -0.5, +0.5], [+0.5, +0.5, +0.5], [-0.5, +0.5, +0.5]] },
    { n: [0, 0, -1], v: [[-0.5, +0.5, -0.5], [+0.5, +0.5, -0.5], [+0.5, -0.5, -0.5], [-0.5, -0.5, -0.5]] },
  ];
  const out = new Float32Array(36 * 6);
  let o = 0;
  for (const f of faces) {
    const tri = [f.v[0], f.v[1], f.v[2], f.v[0], f.v[2], f.v[3]];
    for (const v of tri) {
      out[o++] = v[0]; out[o++] = v[1]; out[o++] = v[2];
      out[o++] = f.n[0]; out[o++] = f.n[1]; out[o++] = f.n[2];
    }
  }
  return out;
}
